import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  alpha,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Slide,
  Slider,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import HotelIcon from '@mui/icons-material/Hotel';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import TakeoutDiningIcon from '@mui/icons-material/TakeoutDining';
import ApartmentIcon from '@mui/icons-material/Apartment';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import {
  createPosOrder,
  deletePosOrder,
  getPosOrderDetail,
  getPosOrderEbillContext,
  getConfigurationTaxRules,
  getPosOrders,
  getRestaurantLandingData,
  sendPosOrderEbill,
  updatePosOrder,
} from '../../posRestaurant/api';
import { mapPosOrderTaxBreakdownToAppliedTaxRows } from '../../posRestaurant/openOrderUtils';
import { useLocation, useNavigate } from 'react-router-dom';
import { searchGuestByPhone } from '../../../services/api';
import { detectThemeSync, watchThemeChanges } from '../../../utils/themeUtils';
import ErrorState from '../../../components/common/ErrorState';
import DashboardHeader from '../../../components/common/DashboardHeader';
import PrinterValidationBanner from '../../../components/restaurant/PrinterValidationBanner';
import useMobileView from '../../../hooks/useMobileView';
import { MOBILE_APP_HEADER_HEIGHT, MOBILE_BOTTOM_NAV_HEIGHT } from '../../../components/layout/mobile/mobileAppTheme';
import {
  getThermalPaperWidthMm as readThermalPaperWidthMm,
  hasDesktopPrintApi,
  isDesktopApp,
  isDesktopShellMissingApis,
  isPrinterValidated,
  openPrinterValidationDialog,
  silentPrintRestaurantHtml,
} from '../../../utils/desktopPrinter';
import { buildRestaurantReceiptHtml } from '../utils/restaurantReceiptHtml';
import countryCodesData from '../../../data/country-codes.json';
import {
  BOOKING_DRAWER_ACCENT,
  getBookingDrawerAuditButtonSx,
  getBookingDrawerFooterSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerMetaChipSx,
  getBookingDrawerTheme,
  withDrawerMenuPaperSx,
} from '../../../utils/bookingDrawerLayout';
import {
  createDrawerOverlayProps,
  getDrawerRootSx,
} from '../../../utils/drawerOverlayProps';
import {
  getPosOrderDrawerPaperSx,
  getPosOrderDrawerSectionPaperSx,
  getPosOrderDrawerSummaryPaperSx,
  getPosOrderDrawerToggleGroupSx,
} from '../../../utils/posRestaurantLayout';
import { isGlobalQrOrderNotifierActive } from '../../../utils/restaurantQrIncomingOrders';
import { subscribeRestaurantOrdersPoll } from '../../../utils/restaurantOrdersPollCoordinator';

const CURRENCY = 'INR';
const MAX_DISCOUNT_PERCENT = 100;
const QUEUE_VISIBLE_STATUSES = new Set(['draft', 'placed', 'preparing', 'served']);
const EDITABLE_ORDER_STATUSES = new Set(['draft', 'placed', 'preparing', 'served']);
const ACTIVE_ORDER_ACCENT = '#ff6b35';
const defaultOrderItem = {
  menu_item: '',
  quantity: 1,
  note: '',
  variation_id: null,
  variation_label: '',
  variation_price: 0,
};
const ToastSlideTransition = (props) => <Slide {...props} direction="left" />;
const KOT_PREPARATION_TIME_OPTIONS = [10, 20, 30, 40, 50, 60, 90, 120];
/** Incoming open-order popup: only these values in the prep-time dropdown. */
const INCOMING_ORDER_PREP_OPTIONS = [15, 30, 45, 60];

const snapToIncomingPrepOption = (raw) => {
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n)) return 30;
  if (INCOMING_ORDER_PREP_OPTIONS.includes(n)) return n;
  return INCOMING_ORDER_PREP_OPTIONS.reduce(
    (best, opt) => (Math.abs(opt - n) < Math.abs(best - n) ? opt : best),
    INCOMING_ORDER_PREP_OPTIONS[0],
  );
};

const playIncomingOrderAlertSound = () => {
  if (typeof window === 'undefined') return;
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;

    const audioContext = new AudioContextClass();
    const now = audioContext.currentTime;
    const gain = audioContext.createGain();
    gain.connect(audioContext.destination);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.22, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 5);

    const oscillator = audioContext.createOscillator();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, now);
    oscillator.frequency.setValueAtTime(1040, now + 0.14);
    oscillator.connect(gain);
    oscillator.start(now);
    oscillator.stop(now + 5);
    oscillator.onended = () => {
      audioContext.close().catch(() => {});
    };
  } catch (error) {
    // Browser autoplay/audio restrictions should not interrupt incoming order handling.
  }
};
const PAYMENT_METHOD_OPTIONS = [
  'Online',
  'Cash',
  'Card',
  'Bank Transfer',
  'Company Settlement',
  'UPI',
  'Credit Sale',
  'Cheque',
  'Payment Link',
];
const COMPANY_SETTLEMENT_PAYMENT_METHOD = 'Company Settlement';
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const escapeReceiptText = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const formatReceiptAmount = (value) => Number(value || 0).toLocaleString('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const getOrderTypeLabel = (orderType) => {
  if (orderType === 'room_service') return 'Room Service';
  if (orderType === 'dine_in') return 'Dine In';
  if (orderType === 'takeaway') return 'Takeaway';
  return 'Order';
};

const getOrderTypeIcon = (orderType) => {
  if (orderType === 'room_service') return <RoomServiceIcon sx={{ fontSize: 18 }} />;
  if (orderType === 'takeaway') return <TakeoutDiningIcon sx={{ fontSize: 18 }} />;
  return <LocalDiningIcon sx={{ fontSize: 18 }} />;
};

const getOrderStatusMeta = (status) => {
  if (status === 'preparing') return { label: 'Preparing', color: '#f59e0b' };
  if (status === 'served') return { label: 'Served', color: '#10b981' };
  if (status === 'draft') return { label: 'Draft', color: '#9ca3af' };
  return { label: 'Placed', color: BOOKING_DRAWER_ACCENT };
};

const isPendingOrderStatus = (status) => {
  const key = String(status || '').toLowerCase();
  return key === 'draft' || key === 'placed';
};

const formatOrderNumber = (orderNumber) => String(orderNumber || '').replace(/^POS-/i, '') || '--';

const getOrderTargetLabel = (targetType, target) => {
  if (targetType === 'room') {
    return `Room ${target?.room_number || target?.id || '--'}`;
  }
  return String(target?.name || `Table ${target?.id || '--'}`);
};

const formatOrderTime = (isoString) => {
  if (!isoString) return '--';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatOrderDateTime = (isoString) => {
  if (!isoString) return '--';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatCurrencyAmount = (value) =>
  `${CURRENCY} ${Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const getDiscountAmountCap = (subtotal) => Math.max(0, Number(subtotal || 0) * (MAX_DISCOUNT_PERCENT / 100));

const clampDiscountValue = (discountType, rawValue, subtotal) => {
  const numericValue = Math.max(0, Number(rawValue || 0));
  if (discountType === 'amount') {
    return Math.min(numericValue, getDiscountAmountCap(subtotal));
  }
  return Math.min(numericValue, MAX_DISCOUNT_PERCENT);
};

const isZeroAmountFullDiscountSettlement = (
  discountType,
  discountValue,
  estimatedSubtotal,
  estimatedTotal,
) => {
  if (estimatedTotal > 0.005) return false;
  const numericDiscountValue = Number(discountValue || 0);
  if (numericDiscountValue <= 0) return false;
  if (discountType === 'percentage') {
    return numericDiscountValue >= 100;
  }
  return estimatedSubtotal > 0 && numericDiscountValue >= estimatedSubtotal - 0.005;
};

const getOrderElapsedMs = (order, nowTimestamp = Date.now()) => {
  const createdAt = new Date(order?.created_at || 0).getTime();
  if (!createdAt || Number.isNaN(createdAt)) return 0;
  return Math.max(0, nowTimestamp - createdAt);
};

const formatOrderElapsedTime = (elapsedMs) => {
  const seconds = Math.max(0, Math.floor(Number(elapsedMs || 0) / 1000));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  }
  return `${String(minutes).padStart(2, '0')}m ${String(remainingSeconds).padStart(2, '0')}s`;
};

const formatClockDuration = (elapsedMs) => {
  const totalSeconds = Math.max(0, Math.floor(Number(elapsedMs || 0) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const getQueueCriticalityMeta = (elapsedMinutes, targetMinutes = 30) => {
  const target = Math.max(10, Number(targetMinutes || 30));
  const ratio = elapsedMinutes / target;
  if (ratio >= 2) return { label: 'Escalated', color: '#7f1d1d', ratio };
  if (ratio >= 1.5) return { label: 'Critical', color: '#b91c1c', ratio };
  if (ratio >= 1.25) return { label: 'Urgent', color: '#ea580c', ratio };
  if (ratio >= 1) return { label: 'High', color: '#d97706', ratio };
  if (ratio >= 0.75) return { label: 'Attention', color: '#ca8a04', ratio };
  return { label: 'On Track', color: '#16a34a', ratio };
};

const normalizeQueueTimers = (timers = []) =>
  (Array.isArray(timers) ? timers : [])
    .filter((timer) => timer && typeof timer === 'object')
    .map((timer, index) => ({
      timer_id: timer.timer_id || `timer-${index + 1}`,
      started_at: timer.started_at || null,
      ended_at: timer.ended_at || null,
      target_minutes: Math.max(10, Number(timer.target_minutes || 30)),
    }));

const mergeQueueTimersForMergedOrder = (destinationTimers = [], sourceTimers = []) => {
  const normalizedDestinationTimers = normalizeQueueTimers(destinationTimers);
  const normalizedSourceTimers = normalizeQueueTimers(sourceTimers);
  const destinationActive = normalizedDestinationTimers.find((timer) => !timer.ended_at) || null;
  const sourceActive = normalizedSourceTimers.find((timer) => !timer.ended_at) || null;

  const mergedTimers = [
    ...normalizedDestinationTimers.filter((timer) => timer.ended_at),
    ...normalizedSourceTimers.filter((timer) => timer.ended_at),
  ];

  if (destinationActive || sourceActive) {
    const mergedActiveTargetMinutes = Number(destinationActive?.target_minutes || 0)
      + Number(sourceActive?.target_minutes || 0);
    const mergedActiveStartCandidates = [destinationActive?.started_at, sourceActive?.started_at]
      .filter(Boolean)
      .map((value) => new Date(value).getTime())
      .filter((value) => Number.isFinite(value));
    const mergedActiveStartedAt = mergedActiveStartCandidates.length
      ? new Date(Math.min(...mergedActiveStartCandidates)).toISOString()
      : (destinationActive?.started_at || sourceActive?.started_at || new Date().toISOString());

    mergedTimers.push({
      timer_id: 'timer-active',
      started_at: mergedActiveStartedAt,
      ended_at: null,
      target_minutes: Math.max(10, mergedActiveTargetMinutes || 30),
    });
  }

  return mergedTimers.map((timer, index) => ({
    ...timer,
    timer_id: `timer-${index + 1}`,
  }));
};

const getRoomImageSrc = (room) =>
  room?.room_image_url ||
  room?.room_image ||
  room?.room_type_image_url ||
  room?.room_type_image ||
  room?.image_url ||
  '';

const getServiceCardSx = (isDark, hasActiveOrder) => ({
  borderRadius: 2.3,
  cursor: 'pointer',
  overflow: 'hidden',
  position: 'relative',
  borderColor: hasActiveOrder
    ? ACTIVE_ORDER_ACCENT
    : (isDark ? 'rgba(76,225,182,0.32)' : 'rgba(76,225,182,0.3)'),
  boxShadow: hasActiveOrder
    ? (isDark
      ? '0 0 0 1px rgba(255,107,53,0.35), 0 12px 28px rgba(255,107,53,0.33)'
      : '0 0 0 1px rgba(255,107,53,0.32), 0 12px 28px rgba(255,107,53,0.24)')
    : (isDark ? '0 10px 22px rgba(5,10,18,0.28)' : '0 8px 20px rgba(15,23,42,0.1)'),
  background: hasActiveOrder
    ? (isDark
      ? 'linear-gradient(152deg, rgba(255,107,53,0.16) 0%, rgba(255,107,53,0.06) 42%, rgba(18,25,38,0.92) 100%)'
      : 'linear-gradient(152deg, rgba(255,107,53,0.16) 0%, rgba(255,107,53,0.05) 42%, #ffffff 100%)')
    : (isDark
      ? 'linear-gradient(152deg, rgba(22,30,45,0.96) 0%, rgba(16,25,37,0.96) 58%, rgba(18,25,38,0.96) 100%)'
      : 'linear-gradient(152deg, #ffffff 0%, #f5fffb 58%, #f8fbff 100%)'),
  transition: 'all .25s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: hasActiveOrder
      ? 'linear-gradient(90deg, rgba(255,107,53,0.95), rgba(251,146,60,0.9))'
      : 'linear-gradient(90deg, rgba(76,225,182,0.95), rgba(59,130,246,0.75))',
    zIndex: 2,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    width: 96,
    height: 96,
    borderRadius: '50%',
    right: -36,
    top: -42,
    background: hasActiveOrder
      ? (isDark ? 'rgba(255,107,53,0.16)' : 'rgba(255,107,53,0.14)')
      : (isDark ? 'rgba(76,225,182,0.12)' : 'rgba(76,225,182,0.12)'),
    pointerEvents: 'none',
    zIndex: 0,
  },
  '&:hover': {
    borderColor: hasActiveOrder ? ACTIVE_ORDER_ACCENT : '#4ce1b6',
    transform: 'translateY(-2px)',
    boxShadow: hasActiveOrder
      ? '0 14px 30px rgba(255,107,53,0.3)'
      : (isDark ? '0 14px 28px rgba(76,225,182,0.2)' : '0 12px 24px rgba(76,225,182,0.22)'),
  },
});

const getServiceCardMediaSx = (isDark, hasActiveOrder) => ({
  width: '100%',
  height: 102,
  borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.09)' : 'rgba(15,23,42,0.08)'}`,
  background: hasActiveOrder
    ? (isDark
      ? 'linear-gradient(135deg, rgba(255,107,53,0.32) 0%, rgba(251,146,60,0.22) 48%, rgba(22,31,45,0.9) 100%)'
      : 'linear-gradient(135deg, rgba(255,107,53,0.24) 0%, rgba(251,146,60,0.16) 48%, #fff8f5 100%)')
    : (isDark
      ? 'linear-gradient(135deg, rgba(59,130,246,0.24) 0%, rgba(76,225,182,0.22) 100%)'
      : 'linear-gradient(135deg, #e7f7ff 0%, #dcfff4 100%)'),
  position: 'relative',
  zIndex: 1,
});

const getServiceSectionPanelSx = (isDark, accent) => ({
  p: 2.2,
  borderRadius: 2.5,
  position: 'relative',
  overflow: 'hidden',
  border: `1px solid ${isDark ? alpha(accent, 0.32) : alpha(accent, 0.24)}`,
  background: isDark
    ? `linear-gradient(145deg, ${alpha(accent, 0.14)} 0%, rgba(17,23,34,0.96) 52%, rgba(15,22,32,0.98) 100%)`
    : `linear-gradient(145deg, #ffffff 0%, ${alpha(accent, 0.1)} 52%, #f8fbff 100%)`,
  boxShadow: isDark ? '0 10px 24px rgba(0,0,0,0.28)' : '0 10px 24px rgba(15,23,42,0.08)',
});

const parseGuestPhoneForForm = (rawPhone, rawCountryCode) => {
  const normalizedCountryCode = String(rawCountryCode || '+91').startsWith('+')
    ? String(rawCountryCode || '+91')
    : `+${String(rawCountryCode || '91')}`;
  const countryDigits = normalizedCountryCode.replace(/\D/g, '');
  let digits = String(rawPhone || '').replace(/\D/g, '');
  if (countryDigits && digits.startsWith(countryDigits) && digits.length > 10) {
    digits = digits.slice(countryDigits.length);
  }
  if (digits.length > 10) digits = digits.slice(-10);
  return {
    mobile: digits,
    countryCode: normalizedCountryCode || '+91',
  };
};

const getOrderTypeFromServiceMode = (serviceMode) => {
  if (serviceMode === 'room_service') return 'room_service';
  if (serviceMode === 'dine_in') return 'dine_in';
  return 'takeaway';
};

const getServiceModeFromOrderType = (orderType) => {
  if (orderType === 'room_service') return 'room_service';
  if (orderType === 'dine_in') return 'dine_in';
  return 'pick_up';
};

const getEditableOrderNote = (rawNote) => {
  const note = String(rawNote || '');
  const profileIndex = note.indexOf(' | Mobile:');
  if (profileIndex > -1) return note.slice(0, profileIndex).trim();
  return note;
};

const buildAppliedTaxesFromRules = (rules = []) =>
  rules.map((rule, index) => {
    const hasPercentage = rule?.tax_percentage !== null && rule?.tax_percentage !== undefined;
    return {
      tax_key: `tax-${rule?.id || index}-${index}`,
      tax_rule_id: Number(rule?.id),
      name: rule?.name || rule?.tax_type_display || 'Tax',
      tax_type_label: rule?.tax_type_display || 'Tax',
      mode: hasPercentage ? 'percentage' : 'amount',
      value: String(hasPercentage ? Number(rule?.tax_percentage || 0) : Number(rule?.tax_amount || 0)),
      enabled: true,
    };
  });

const buildDisabledAppliedTaxesFromRules = (rules = []) =>
  buildAppliedTaxesFromRules(rules).map((row) => ({ ...row, enabled: false }));

/** Matches backend `pos_restaurant.tax_utils.POS_TAX_APPLICABLE_ON` so configuration rules align with checkout / POS. */
const POS_RESTAURANT_TAX_APPLICABLE = new Set(['food_beverage', 'services', 'total_amount']);

const mapOrderTaxBreakdownToAppliedTaxes = (rows, fallbackRules = [], options = {}) => {
  const explicitEmptyMeansDisabled = Boolean(options.explicitEmptyMeansDisabled);
  if (!Array.isArray(rows)) {
    return buildAppliedTaxesFromRules(fallbackRules);
  }
  if (!rows.length) {
    return explicitEmptyMeansDisabled
      ? buildDisabledAppliedTaxesFromRules(fallbackRules)
      : buildAppliedTaxesFromRules(fallbackRules);
  }
  const mapped = mapPosOrderTaxBreakdownToAppliedTaxRows(rows);
  const hasUseful = mapped.some((r) => Number(r.value) > 0);
  if (!hasUseful && fallbackRules.length) {
    return buildAppliedTaxesFromRules(fallbackRules);
  }
  return mapped;
};

const normalizeVariationId = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.floor(parsed);
};

const getOrderItemIdentityKey = (row = {}) => {
  const menuItemId = Number(row?.menu_item || 0);
  const variationId = normalizeVariationId(row?.variation_id);
  return `${menuItemId}:${variationId || 0}`;
};

const getMenuItemVariationOptions = (menuItem, variationLabelMap = new Map()) => {
  const variationRows = Array.isArray(menuItem?.variation_config) ? menuItem.variation_config : [];
  const seenVariationIds = new Set();
  return variationRows
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const variationId = normalizeVariationId(row.variation_id);
      if (!variationId || seenVariationIds.has(variationId)) return null;
      seenVariationIds.add(variationId);
      const extraPrice = Number(row.price || 0);
      return {
        variation_id: variationId,
        label: variationLabelMap.get(variationId) || `Variation #${variationId}`,
        price: Number.isFinite(extraPrice) ? Math.max(0, extraPrice) : 0,
      };
    })
    .filter(Boolean);
};

const resolveOrderItemUnitPrice = (row, menuItemBasePrice = 0) => {
  const variationId = normalizeVariationId(row?.variation_id);
  if (variationId) {
    const variationOnlyPrice = Number(row?.variation_price || 0);
    return Number.isFinite(variationOnlyPrice) ? Math.max(0, variationOnlyPrice) : 0;
  }
  const explicitUnitPrice = row?.unit_price;
  if (explicitUnitPrice !== null && explicitUnitPrice !== undefined && explicitUnitPrice !== '') {
    const parsedExplicitPrice = Number(explicitUnitPrice);
    if (Number.isFinite(parsedExplicitPrice) && parsedExplicitPrice >= 0) {
      return parsedExplicitPrice;
    }
  }
  const extraPrice = Number(row?.variation_price || 0);
  const normalizedExtraPrice = Number.isFinite(extraPrice) ? Math.max(0, extraPrice) : 0;
  return Math.max(0, Number(menuItemBasePrice || 0) + normalizedExtraPrice);
};

const normalizeDraftOrderItems = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((row) => ({
      ...defaultOrderItem,
      menu_item: Number(row?.menu_item),
      quantity: Math.max(1, Math.floor(Number(row?.quantity || 1))),
      item_name: String(row?.item_name || ''),
      note: String(row?.special_note || row?.note || ''),
      variation_id: normalizeVariationId(row?.variation_id),
      variation_label: String(row?.variation_name || row?.variation_label || '').trim(),
      variation_price: Math.max(0, Number(row?.variation_price || 0)),
      unit_price: row?.unit_price,
    }))
    .filter((row) => Number(row.menu_item) > 0);

const mergeDraftOrderItems = (baseItems = [], incomingItems = []) => {
  const mergedItems = [];
  const itemIndexMap = new Map();

  [...baseItems, ...incomingItems].forEach((row) => {
    const menuItemId = Number(row?.menu_item);
    const quantity = Math.max(1, Math.floor(Number(row?.quantity || 1)));
    const note = String(row?.note || '').trim();
    const rowIdentityKey = getOrderItemIdentityKey(row);
    if (!menuItemId) return;

    if (!itemIndexMap.has(rowIdentityKey)) {
      itemIndexMap.set(rowIdentityKey, mergedItems.length);
      mergedItems.push({
        ...defaultOrderItem,
        menu_item: menuItemId,
        quantity,
        note,
        variation_id: normalizeVariationId(row?.variation_id),
        variation_label: String(row?.variation_label || row?.variation_name || '').trim(),
        variation_price: Math.max(0, Number(row?.variation_price || 0)),
        unit_price: row?.unit_price,
      });
      return;
    }

    const existingIdx = itemIndexMap.get(rowIdentityKey);
    const existingRow = mergedItems[existingIdx];
    mergedItems[existingIdx] = {
      ...existingRow,
      quantity: Number(existingRow.quantity || 0) + quantity,
      note: [existingRow.note, note].filter(Boolean).join(existingRow.note && note ? ' | ' : ''),
    };
  });

  return mergedItems;
};

const RestaurantLandingPage = ({
  embedded = false,
  initialEditOrderId = null,
  onEmbeddedClose = null,
  onEmbeddedSaved = null,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const { isCompact } = useMobileView();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [isTargetChangeLoading, setIsTargetChangeLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });
  const [data, setData] = useState({
    rooms: [],
    tables: [],
    categories: [],
    menu_variations: [],
    menu_items: [],
    settings: { gst_percentage: '0.00', enable_room_order: true },
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paymentDrawerOpen, setPaymentDrawerOpen] = useState(false);
  const [orderTargetMenuAnchorEl, setOrderTargetMenuAnchorEl] = useState(null);
  const [selection, setSelection] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [pendingMergeSource, setPendingMergeSource] = useState(null);
  const [orderForm, setOrderForm] = useState({
    notes: '',
    order_items: [],
  });
  const [orderMeta, setOrderMeta] = useState({
    service_mode: 'dine_in',
    phone_country_code: '+91',
    mobile: '',
    customer_name: '',
    payment_collection_mode: 'collect_now',
    payment_method: '',
    partial_paid_amount: '',
    preparation_time_minutes: 30,
  });
  const [phoneCountryCodeMenuOpen, setPhoneCountryCodeMenuOpen] = useState(false);
  const [phoneCountryCodeSearch, setPhoneCountryCodeSearch] = useState('');
  const [searchingGuest, setSearchingGuest] = useState(false);
  const [itemEntry, setItemEntry] = useState({
    menu_item_id: '',
    quantity: 1,
  });
  const [variationSelectionDialog, setVariationSelectionDialog] = useState({
    open: false,
    menu_item_id: '',
    quantity: 1,
    options: [],
    selected_variation_id: '',
  });
  const [itemEntryErrors, setItemEntryErrors] = useState({
    menu_item_id: '',
    quantity: '',
  });
  const [paymentValidationError, setPaymentValidationError] = useState('');
  const [kotConfirmDialog, setKotConfirmDialog] = useState({
    open: false,
    actionType: 'kot',
    preparationTimeMinutes: 30,
  });
  const [cancelDialog, setCancelDialog] = useState({
    open: false,
    reason: '',
    error: '',
  });
  const [updateReasonDialog, setUpdateReasonDialog] = useState({
    open: false,
    reason: '',
    error: '',
  });
  const [ebillDialog, setEbillDialog] = useState({
    open: false,
    email: '',
    error: '',
    loading: false,
    source: '',
  });
  const [incomingOrderDialog, setIncomingOrderDialog] = useState({
    open: false,
    queue: [],
    activeOrder: null,
    processing: false,
  });
  const [incomingOrderPrepMinutes, setIncomingOrderPrepMinutes] = useState(30);
  const [pendingAcceptingOrderId, setPendingAcceptingOrderId] = useState(null);
  const [pendingDecliningOrderId, setPendingDecliningOrderId] = useState(null);
  const [queueNowTimestamp, setQueueNowTimestamp] = useState(() => Date.now());
  const [servingOrderIds, setServingOrderIds] = useState({});
  const [foodBeverageTaxes, setFoodBeverageTaxes] = useState([]);
  const [billingAdjustments, setBillingAdjustments] = useState({
    discount_type: 'percentage',
    discount_value: '',
    applied_taxes: [],
    delivery_charge: '',
    container_charge: '',
  });
  const phoneCountryCodeAnchorRef = useRef(null);
  const phoneInputAnchorRef = useRef(null);
  const lastHandledEditOrderRef = useRef(null);
  const ordersSectionRef = useRef(null);
  const incomingQueueKnownOrderIdsRef = useRef(new Set());
  const incomingQueueInitializedRef = useRef(false);

  const isDark = currentTheme === 'dark';
  const drawerTheme = getBookingDrawerTheme(isDark);
  const posOrderDrawerPaperSx = getPosOrderDrawerPaperSx(isDark);
  const drawerOverlays = useMemo(
    () => createDrawerOverlayProps(isDark, isDark ? drawerTheme.surface : '#ffffff'),
    [isDark, drawerTheme.surface],
  );
  const footerActionButtonSx = {
    textTransform: 'none',
    borderRadius: 2,
    minHeight: 50,
    fontWeight: 800,
    fontSize: { xs: '0.74rem', sm: '0.8rem', md: '0.86rem' },
    flex: 1,
    minWidth: 0,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    textAlign: 'center',
    lineHeight: 1.18,
    px: 0.55,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'transparent',
    boxShadow: isDark ? '0 4px 14px rgba(0,0,0,0.28)' : '0 4px 14px rgba(15,23,42,0.12)',
    transition: 'transform 0.16s ease, box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: isDark ? '0 8px 20px rgba(0,0,0,0.34)' : '0 8px 20px rgba(15,23,42,0.18)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
    '&.Mui-disabled': {
      borderColor: isDark ? '#343a47' : '#d4dae2',
      backgroundColor: isDark ? '#2a2f3a' : '#eef2f7',
      color: isDark ? '#778093' : '#8c96a7',
      boxShadow: 'none',
    },
  };

  useEffect(() => {
    const cleanup = watchThemeChanges((theme) => setCurrentTheme(theme));
    return cleanup;
  }, []);

  const loadData = async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const [payload, ordersPayload, configuredTaxRules] = await Promise.all([
        getRestaurantLandingData(),
        getPosOrders().catch(() => []),
        getConfigurationTaxRules({ is_active: 'true' }).catch(() => []),
      ]);
      const normalizedOrders = Array.isArray(ordersPayload)
        ? ordersPayload
        : Array.isArray(ordersPayload?.results)
          ? ordersPayload.results
          : [];
      const normalizedTaxes = (Array.isArray(configuredTaxRules) ? configuredTaxRules : [])
        .filter((rule) =>
          POS_RESTAURANT_TAX_APPLICABLE.has(String(rule?.applicable_on || '').toLowerCase())
          && rule?.is_active !== false
          && (!rule?.currency || String(rule.currency).toUpperCase() === CURRENCY)
        )
        .sort((first, second) => (
          Number(first?.sort_order || 0) - Number(second?.sort_order || 0)
        ) || String(first?.name || '').localeCompare(String(second?.name || '')));
      setData({
        rooms: payload.rooms || [],
        tables: payload.tables || [],
        categories: payload.categories || [],
        menu_variations: payload.menu_variations || [],
        menu_items: payload.menu_items || [],
        settings: payload.settings || { gst_percentage: '0.00', enable_room_order: true },
      });
      setFoodBeverageTaxes(normalizedTaxes);
      setOrders(normalizedOrders);
      syncIncomingOrderQueueFromRows(normalizedOrders, { shouldPrompt: false });
    } catch (err) {
      if (silent) {
        throw err;
      }
      setError(err);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const setToastMessage = (severity, message) => {
    setToast({ open: true, severity, message });
  };

  const isQrBasedIncomingOrder = useCallback((order) => {
    if (!order || typeof order !== 'object') return false;
    if (order?.is_open_qr_order) return true;
    const sourceCandidates = [
      order?.source,
      order?.order_source,
      order?.created_via,
      order?.origin,
      order?.channel,
    ]
      .map((value) => String(value || '').trim().toLowerCase())
      .filter(Boolean);
    if (sourceCandidates.some((value) => value.includes('qr') || value.includes('guest'))) {
      return true;
    }
    // Current backend QR flow creates orders in `draft` and manual POS creates in `placed`.
    return String(order?.status || '').toLowerCase() === 'draft';
  }, []);

  const getIncomingOrderSignature = useCallback((order) => {
    const orderId = Number(order?.id || 0);
    if (!orderId) return '';
    const itemSignature = (Array.isArray(order?.items) ? order.items : [])
      .map((item) => [
        Number(item?.menu_item || item?.menu_item_id || 0),
        normalizeVariationId(item?.variation_id) || '',
        Number(item?.quantity || 0),
        String(item?.special_note || '').trim(),
      ].join('|'))
      .sort()
      .join(',');
    return [
      orderId,
      itemSignature,
    ].join(':');
  }, []);

  const getIncomingOpenOrders = useCallback((rows) => (
    (Array.isArray(rows) ? rows : [])
      .filter((order) => {
        const status = String(order?.status || '').toLowerCase();
        if (status !== 'draft' && status !== 'placed') return false;
        return isQrBasedIncomingOrder(order);
      })
      .sort((first, second) => {
        const firstTs = new Date(first?.created_at || 0).getTime();
        const secondTs = new Date(second?.created_at || 0).getTime();
        return (Number.isFinite(firstTs) ? firstTs : 0) - (Number.isFinite(secondTs) ? secondTs : 0);
      })
  ), [isQrBasedIncomingOrder]);

  const syncIncomingOrderQueueFromRows = useCallback((rows, { shouldPrompt = false } = {}) => {
    const queueRows = getIncomingOpenOrders(rows);
    if (!incomingQueueInitializedRef.current) {
      queueRows.forEach((order) => {
        const signature = getIncomingOrderSignature(order);
        if (signature) incomingQueueKnownOrderIdsRef.current.add(signature);
      });
      incomingQueueInitializedRef.current = true;
      return;
    }
    if (!shouldPrompt) return;
    const unseenOrders = queueRows.filter((order) => {
      const signature = getIncomingOrderSignature(order);
      return signature && !incomingQueueKnownOrderIdsRef.current.has(signature);
    });
    if (!unseenOrders.length) return;

    unseenOrders.forEach((order) => {
      const signature = getIncomingOrderSignature(order);
      if (signature) incomingQueueKnownOrderIdsRef.current.add(signature);
    });
    if (isGlobalQrOrderNotifierActive()) {
      return;
    }
    setIncomingOrderDialog((prev) => {
      const existing = Array.isArray(prev?.queue) ? prev.queue : [];
      const mergedMap = new Map();
      [...existing, ...unseenOrders].forEach((order) => {
        const orderId = Number(order?.id || 0);
        if (orderId) mergedMap.set(orderId, order);
      });
      const mergedQueue = Array.from(mergedMap.values()).sort((first, second) => {
        const firstTs = new Date(first?.created_at || 0).getTime();
        const secondTs = new Date(second?.created_at || 0).getTime();
        return (Number.isFinite(firstTs) ? firstTs : 0) - (Number.isFinite(secondTs) ? secondTs : 0);
      });
      const activeOrder = prev?.activeOrder && mergedMap.has(Number(prev.activeOrder.id))
        ? mergedMap.get(Number(prev.activeOrder.id))
        : mergedQueue[0] || null;
      return {
        open: Boolean(activeOrder),
        queue: mergedQueue,
        activeOrder,
        processing: false,
      };
    });
    const firstUnseen = unseenOrders[0];
    setIncomingOrderPrepMinutes(snapToIncomingPrepOption(firstUnseen?.preparation_time_minutes || 30));
    playIncomingOrderAlertSound();
    setToastMessage('info', `${unseenOrders.length} new open order${unseenOrders.length > 1 ? 's' : ''} received in restaurant queue.`);
  }, [getIncomingOpenOrders, getIncomingOrderSignature]);

  useEffect(() => {
    const o = incomingOrderDialog.activeOrder;
    if (!o?.id) return;
    setIncomingOrderPrepMinutes(snapToIncomingPrepOption(o.preparation_time_minutes || 30));
  }, [incomingOrderDialog.activeOrder?.id]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeRestaurantOrdersPoll(
      (normalizedOrders, meta = {}) => {
        if (!meta.changed && !meta.cached) return;
        setOrders(normalizedOrders);
        syncIncomingOrderQueueFromRows(normalizedOrders, { shouldPrompt: Boolean(meta.changed) });
      },
      { immediate: false },
    );
    return unsubscribe;
  }, [syncIncomingOrderQueueFromRows]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setQueueNowTimestamp(Date.now());
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (embedded) return;
    const params = new URLSearchParams(location.search);
    if (params.get('section') !== 'orders') return;
    navigate('/pos-restaurant/?tab=orders', { replace: true });
  }, [embedded, location.search, navigate]);

  const orderLookupById = useMemo(() => {
    const lookup = new Map();
    orders.forEach((order) => {
      const orderId = Number(order?.id || 0);
      if (orderId > 0) {
        lookup.set(orderId, order);
      }
    });
    return lookup;
  }, [orders]);
  const getOrderById = useCallback((orderId) => {
    const normalizedOrderId = Number(orderId || 0);
    if (!normalizedOrderId) return null;
    return orderLookupById.get(normalizedOrderId) || null;
  }, [orderLookupById]);
  const findRunningMergeTargetOrder = useCallback((incomingOrder) => {
    const sessionId = Number(incomingOrder?.qr_table_session || 0);
    const roomId = Number(incomingOrder?.room || 0);
    const isRoomServiceOrder = String(incomingOrder?.order_type || '').toLowerCase() === 'room_service';
    const incomingOrderId = Number(incomingOrder?.id || 0);
    return (orders || [])
      .filter((order) => {
        if (Number(order?.id || 0) === incomingOrderId) return false;
        if (sessionId) {
          if (Number(order?.qr_table_session || 0) !== sessionId) return false;
        } else if (isRoomServiceOrder && roomId) {
          if (Number(order?.room || 0) !== roomId) return false;
          if (String(order?.order_type || '').toLowerCase() !== 'room_service') return false;
        } else {
          return false;
        }
        const status = String(order?.status || '').toLowerCase();
        return status === 'preparing' || status === 'served';
      })
      .sort((first, second) =>
        new Date(first?.created_at || 0).getTime() - new Date(second?.created_at || 0).getTime()
      )[0] || null;
  }, [orders]);
  const latestEditableOrdersByTarget = useMemo(() => {
    const roomLookup = new Map();
    const tableLookup = new Map();
    const insertOrder = (targetLookup, targetId, order, timestamp) => {
      if (!targetId) return;
      const existingRows = targetLookup.get(targetId) || [];
      const orderId = Number(order?.id || 0);
      if (!orderId) return;
      if (existingRows.some((row) => Number(row?.id || 0) === orderId)) return;
      const nextRows = [...existingRows, { _order: order, _sortTime: timestamp }]
        .map((row) => (
          row?._order
            ? row
            : { _order: row, _sortTime: new Date(row?.updated_at || row?.created_at || 0).getTime() }
        ))
        .sort((first, second) => Number(second?._sortTime || 0) - Number(first?._sortTime || 0))
        .slice(0, 2)
        .map((row) => row?._order || null)
        .filter(Boolean);
      targetLookup.set(targetId, nextRows);
    };

    orders.forEach((order) => {
      if (!EDITABLE_ORDER_STATUSES.has(order?.status)) return;
      const timestamp = new Date(order?.updated_at || order?.created_at || 0).getTime();
      const roomId = Number(order?.room || 0);
      if (roomId > 0) {
        insertOrder(roomLookup, roomId, order, timestamp);
      }
      const tableId = Number(order?.table || 0);
      if (tableId > 0) {
        insertOrder(tableLookup, tableId, order, timestamp);
      }
    });

    return { room: roomLookup, table: tableLookup };
  }, [orders]);
  const findLatestEditableOrderByTarget = useCallback((targetType, targetId, excludedOrderId = null) => {
    const normalizedTargetId = Number(targetId || 0);
    if (!normalizedTargetId) return null;
    const targetLookup = targetType === 'room'
      ? latestEditableOrdersByTarget.room
      : latestEditableOrdersByTarget.table;
    const candidates = targetLookup.get(normalizedTargetId) || [];
    if (!candidates.length) return null;
    const excludedId = Number(excludedOrderId || 0);
    if (!excludedId) return candidates[0] || null;
    return candidates.find((order) => Number(order?.id || 0) !== excludedId) || null;
  }, [latestEditableOrdersByTarget]);

  const buildDraftOrderFormData = ({
    targetType,
    targetId,
    status = 'placed',
    serviceMode = orderMeta.service_mode,
    customerName = orderMeta.customer_name,
    phoneCountryCode = orderMeta.phone_country_code,
    mobile = orderMeta.mobile,
    paymentCollectionMode = orderMeta.payment_collection_mode,
    paymentMethod = orderMeta.payment_method,
    preparationTimeMinutes = orderMeta.preparation_time_minutes,
    queueTimers = editingOrder?.queue_timers || [],
    discountType = billingAdjustments.discount_type,
    discountValue = Number(billingAdjustments.discount_value || 0),
    deliveryCharge = deliveryChargeAmount,
    containerCharge = containerChargeAmount,
    taxRows = configuredTaxBreakdown,
    notes = orderForm.notes,
    orderItems = orderForm.order_items,
    auditContext = null,
    preserveEmptyPaymentMethod = false,
  }) => {
    const validItems = (Array.isArray(orderItems) ? orderItems : [])
      .filter((row) => row?.menu_item && Number(row?.quantity) > 0)
      .map((row) => {
        const variationId = normalizeVariationId(row?.variation_id);
        return {
          menu_item: Number(row.menu_item),
          quantity: Number(row.quantity),
          variation_id: variationId,
          special_note: String(row.note || row.special_note || '').trim(),
        };
      });

    if (!validItems.length) {
      throw new Error('Add at least one menu item before moving order.');
    }
    const priceByItem = new Map((data?.menu_items || []).map((item) => [Number(item.id), Number(item.price || 0)]));
    const variationLabelLookup = new Map(
      (data?.menu_variations || []).map((variation) => [
        Number(variation.id),
        variation.online_display_name || variation.name || `Variation #${variation.id}`,
      ])
    );
    const variationOptionsByItem = new Map();
    (data?.menu_items || []).forEach((menuItem) => {
      variationOptionsByItem.set(
        Number(menuItem.id),
        getMenuItemVariationOptions(menuItem, variationLabelLookup)
      );
    });
    const subtotalForDraft = validItems.reduce((sum, row) => (
      sum + (
        (() => {
          const basePrice = priceByItem.get(Number(row.menu_item)) || 0;
          const variationId = normalizeVariationId(row.variation_id);
          if (!variationId) return basePrice;
          const variationOption = (variationOptionsByItem.get(Number(row.menu_item)) || []).find(
            (option) => Number(option.variation_id) === Number(variationId)
          );
          const extraPrice = Number(variationOption?.price || 0);
          return Number.isFinite(extraPrice) ? Math.max(0, extraPrice) : 0;
        })()
      ) * Number(row.quantity || 0)
    ), 0);
    const clampedDiscountValue = clampDiscountValue(discountType, discountValue, subtotalForDraft);

    const normalizedTaxRows = (Array.isArray(taxRows) ? taxRows : [])
      .filter((row) => row && typeof row === 'object')
      .map((row, index) => ({
        tax_key: row?.tax_key || `tax-${index}`,
        tax_rule_id: Number(row?.tax_rule_id || 0) || null,
        name: row?.name || row?.tax_type_label || 'Tax',
        tax_type_label: row?.tax_type_label || row?.name || 'Tax',
        mode: row?.mode === 'amount' ? 'amount' : 'percentage',
        value: Number(row?.numericValue ?? row?.value ?? 0),
        numericValue: Number(row?.numericValue ?? row?.value ?? 0),
        amount: Number(row?.amount || 0),
        enabled: row?.enabled !== false,
      }));

    const normalizedPaymentMethod = String(paymentMethod || '').trim();
    const isPartialCollectionMode = paymentCollectionMode === 'partial';
    let normalizedPaymentCollectionMode = (paymentCollectionMode === 'due' || isPartialCollectionMode) ? 'due' : 'collect_now';
    if (!normalizedPaymentMethod && !preserveEmptyPaymentMethod) {
      normalizedPaymentCollectionMode = 'due';
    }
    const shouldKeepPaymentMethod = normalizedPaymentCollectionMode === 'collect_now' || isPartialCollectionMode;

    const formData = new FormData();
    formData.append('order_type', getOrderTypeFromServiceMode(serviceMode));
    formData.append('status', String(status || 'placed'));
    formData.append('customer_name', String(customerName || '').trim());
    formData.append('customer_phone_country_code', String(phoneCountryCode || '').trim() || '+91');
    formData.append('customer_phone', String(mobile || '').trim());
    formData.append('payment_collection_mode', normalizedPaymentCollectionMode);
    formData.append('payment_method', shouldKeepPaymentMethod ? normalizedPaymentMethod : '');
    formData.append('preparation_time_minutes', String(Math.max(10, Number(preparationTimeMinutes || 30))));
    formData.append('queue_timers_json', JSON.stringify(normalizeQueueTimers(queueTimers)));
    formData.append('discount_type', discountType === 'amount' ? 'amount' : 'percentage');
    formData.append('discount_value', String(clampedDiscountValue));
    formData.append('delivery_charge', String(Number(deliveryCharge || 0)));
    formData.append('container_charge', String(Number(containerCharge || 0)));
    formData.append('tax_breakdown_json', JSON.stringify(normalizedTaxRows));
    formData.append('notes', String(notes || '').trim());
    formData.append('order_items_json', JSON.stringify(validItems));
    if (auditContext && typeof auditContext === 'object') {
      formData.append('audit_context_json', JSON.stringify(auditContext));
    }
    if (targetType === 'room') {
      formData.append('room', String(targetId));
      formData.append('table', '');
    } else {
      formData.append('table', String(targetId));
      formData.append('room', '');
    }
    return formData;
  };

  const handleChangeOrderTarget = async (nextTargetType, nextTarget) => {
    setOrderTargetMenuAnchorEl(null);
    if (!selection || !nextTarget || submitting || isTargetChangeLoading) return;

    const currentTargetId = Number(selection?.target?.id || 0);
    const nextTargetId = Number(nextTarget?.id || 0);
    const sourceOrder = editingOrder?.id
      ? getOrderById(editingOrder.id)
      : null;
    const sourceTargetLabel = getOrderTargetLabel(selection?.targetType, selection?.target);
    const nextTargetLabel = getOrderTargetLabel(nextTargetType, nextTarget);

    if (currentTargetId === nextTargetId && selection?.targetType === nextTargetType) {
      return;
    }

    const destinationOrder = findLatestEditableOrderByTarget(
      nextTargetType,
      nextTargetId,
      editingOrder?.id
    );

    const sourceOrderId = Number(sourceOrder?.id || editingOrder?.id || 0);
    setIsTargetChangeLoading(true);
    try {
      if (destinationOrder) {
        const destinationItems = normalizeDraftOrderItems(destinationOrder?.items || []);
        const sourceDraftItems = normalizeDraftOrderItems(orderForm.order_items || []);
        const mergedItems = mergeDraftOrderItems(destinationItems, sourceDraftItems);
        const destinationNote = getEditableOrderNote(destinationOrder?.notes);
        const sourceNote = String(orderForm.notes || '').trim();
        const mergedNote = [destinationNote, sourceNote].filter(Boolean).join(destinationNote && sourceNote ? '\n' : '');
        const mergedQueueTimers = mergeQueueTimersForMergedOrder(
          destinationOrder?.queue_timers || [],
          sourceOrder?.queue_timers || editingOrder?.queue_timers || []
        );
        const mergedActiveTimer = mergedQueueTimers.find((timer) => !timer.ended_at);

        const mergedPayload = buildDraftOrderFormData({
          targetType: nextTargetType,
          targetId: nextTargetId,
          status: destinationOrder?.status === 'served' ? 'placed' : (destinationOrder?.status || 'placed'),
          serviceMode: getServiceModeFromOrderType(destinationOrder.order_type),
          customerName: destinationOrder?.customer_name || (nextTargetType === 'room' ? (nextTarget?.guest_name || '') : ''),
          phoneCountryCode: destinationOrder?.customer_phone_country_code || '+91',
          mobile: destinationOrder?.customer_phone || '',
          // Keep merged order ready for explicit payment selection later.
          paymentCollectionMode: 'collect_now',
          paymentMethod: '',
          preserveEmptyPaymentMethod: true,
          preparationTimeMinutes: Number(
            mergedActiveTimer?.target_minutes
            || destinationOrder?.preparation_time_minutes
            || 30
          ),
          queueTimers: mergedQueueTimers,
          discountType: destinationOrder?.discount_type || 'percentage',
          discountValue: Number(destinationOrder?.discount_value || 0),
          deliveryCharge: Number(destinationOrder?.delivery_charge || 0),
          containerCharge: Number(destinationOrder?.container_charge || 0),
          taxRows: Array.isArray(destinationOrder?.tax_breakdown) ? destinationOrder.tax_breakdown : [],
          notes: mergedNote,
          orderItems: mergedItems,
          auditContext: {
            action: 'order_merged',
            label: 'Order Merged',
            message: `Merged ${formatOrderNumber(sourceOrder?.order_number || editingOrder?.order_number)} from ${sourceTargetLabel} into ${nextTargetLabel}.`,
            details: {
              merge_source_order_id: Number(sourceOrder?.id || editingOrder?.id || 0) || null,
              merge_source_order_number: formatOrderNumber(sourceOrder?.order_number || editingOrder?.order_number),
              merged_into_order_id: Number(destinationOrder?.id || 0) || null,
              merged_into_order_number: formatOrderNumber(destinationOrder?.order_number),
              from_target: sourceTargetLabel,
              to_target: nextTargetLabel,
            },
          },
        });

        const mergedSavedOrder = await updatePosOrder(destinationOrder.id, mergedPayload);
        let mergeCleanupWarningSuffix = '';

        if (sourceOrderId && Number(sourceOrderId) !== Number(destinationOrder.id)) {
          const sourceOrderRef = formatOrderNumber(sourceOrder?.order_number || editingOrder?.order_number);
          const sourceTimers = normalizeQueueTimers(sourceOrder?.queue_timers || editingOrder?.queue_timers || [])
            .map((timer) => (timer.ended_at ? timer : { ...timer, ended_at: new Date().toISOString() }));
          const sourcePaymentMethod = String(sourceOrder?.payment_method || '').trim();
          const sourcePaymentCollectionMode = sourcePaymentMethod
            ? (sourceOrder?.payment_collection_mode === 'due' ? 'due' : 'collect_now')
            : 'due';
          try {
            await updatePosOrder(sourceOrderId, {
              status: 'completed',
              queue_timers: sourceTimers,
              payment_collection_mode: sourcePaymentCollectionMode,
              payment_method: sourcePaymentCollectionMode === 'due' ? '' : sourcePaymentMethod,
              audit_context: {
                action: 'merge_source_closed',
                label: 'Merged Source Closed',
                message: `Source order ${sourceOrderRef} auto-closed after merge into ${formatOrderNumber(destinationOrder?.order_number)}.`,
                details: {
                  merged_into_order_id: Number(destinationOrder?.id || 0) || null,
                  merged_into_order_number: formatOrderNumber(destinationOrder?.order_number),
                  source_order_id: Number(sourceOrderId) || null,
                  source_order_number: sourceOrderRef,
                  from_target: sourceTargetLabel,
                  to_target: nextTargetLabel,
                },
              },
            });
            try {
              await deletePosOrder(sourceOrderId);
            } catch (_deleteMergeSourceError) {
              mergeCleanupWarningSuffix = ` Source order ${sourceOrderRef} was merged but could not be removed; it remains closed.`;
            }
          } catch (_closeMergeSourceError) {
            mergeCleanupWarningSuffix = ` Source order ${sourceOrderRef} was merged but could not be safely closed, so it was not removed.`;
          }
        }

        const mergedTaxBreakdown = Array.isArray(mergedSavedOrder?.tax_breakdown)
          ? mergedSavedOrder.tax_breakdown
          : (Array.isArray(destinationOrder?.tax_breakdown) ? destinationOrder.tax_breakdown : []);
        const mergedAppliedTaxes = mapOrderTaxBreakdownToAppliedTaxes(
          mergedTaxBreakdown,
          foodBeverageTaxes,
          { explicitEmptyMeansDisabled: true }
        );

        setSelection({ targetType: nextTargetType, target: nextTarget });
        setEditingOrder({
          id: Number(mergedSavedOrder?.id || destinationOrder.id),
          order_number: mergedSavedOrder?.order_number || destinationOrder.order_number,
          status: mergedSavedOrder?.status || destinationOrder.status || 'placed',
          queue_timers: normalizeQueueTimers(mergedSavedOrder?.queue_timers || destinationOrder.queue_timers),
          preparation_time_minutes: Number(
            mergedSavedOrder?.preparation_time_minutes
            || destinationOrder?.preparation_time_minutes
            || 30
          ),
        });
        setOrderForm({
          notes: getEditableOrderNote(mergedSavedOrder?.notes || mergedNote),
          order_items: normalizeDraftOrderItems(mergedSavedOrder?.items || mergedItems),
        });
        setOrderMeta({
          service_mode: getServiceModeFromOrderType(mergedSavedOrder?.order_type || destinationOrder.order_type),
          phone_country_code: mergedSavedOrder?.customer_phone_country_code || destinationOrder?.customer_phone_country_code || '+91',
          mobile: mergedSavedOrder?.customer_phone || destinationOrder?.customer_phone || '',
          customer_name: mergedSavedOrder?.customer_name || destinationOrder?.customer_name || '',
          payment_collection_mode: mergedSavedOrder?.payment_collection_mode || destinationOrder?.payment_collection_mode || 'collect_now',
          payment_method: mergedSavedOrder?.payment_method || destinationOrder?.payment_method || '',
          partial_paid_amount: '',
          preparation_time_minutes: Number(
            mergedSavedOrder?.preparation_time_minutes
            || destinationOrder?.preparation_time_minutes
            || 30
          ),
        });
        setBillingAdjustments({
          discount_type: mergedSavedOrder?.discount_type || destinationOrder?.discount_type || 'percentage',
          discount_value: String(
            Number(mergedSavedOrder?.discount_value ?? destinationOrder?.discount_value ?? 0)
          ),
          applied_taxes: mergedAppliedTaxes,
          delivery_charge: String(
            Number(mergedSavedOrder?.delivery_charge ?? destinationOrder?.delivery_charge ?? 0)
          ),
          container_charge: String(
            Number(mergedSavedOrder?.container_charge ?? destinationOrder?.container_charge ?? 0)
          ),
        });
        await loadData({ silent: true });
        setToastMessage(
          mergeCleanupWarningSuffix ? 'warning' : 'success',
          `${nextTargetLabel} merged and auto-saved into ${formatOrderNumber(destinationOrder.order_number)}.${mergeCleanupWarningSuffix}`
        );
        return;
      }

      const guestPhoneMeta = parseGuestPhoneForForm(nextTarget?.guest_phone, nextTarget?.guest_country_code);
      const effectiveServiceMode = nextTargetType === 'room'
        ? 'room_service'
        : (orderMeta.service_mode === 'room_service' ? 'dine_in' : orderMeta.service_mode);
      const movePayload = buildDraftOrderFormData({
        targetType: nextTargetType,
        targetId: nextTargetId,
        status: sourceOrder?.status || 'placed',
        serviceMode: effectiveServiceMode,
        customerName: orderMeta.customer_name || (nextTargetType === 'room' ? (nextTarget?.guest_name || '') : ''),
        phoneCountryCode: orderMeta.phone_country_code || guestPhoneMeta.countryCode || '+91',
        mobile: orderMeta.mobile || (nextTargetType === 'room' ? (guestPhoneMeta.mobile || '') : ''),
        paymentCollectionMode: orderMeta.payment_collection_mode,
        paymentMethod: orderMeta.payment_method,
        preparationTimeMinutes: orderMeta.preparation_time_minutes,
        queueTimers: sourceOrder?.queue_timers || editingOrder?.queue_timers || [],
        discountType: billingAdjustments.discount_type,
        discountValue: Number(billingAdjustments.discount_value || 0),
        deliveryCharge: deliveryChargeAmount,
        containerCharge: containerChargeAmount,
        taxRows: configuredTaxBreakdown,
        notes: orderForm.notes,
        orderItems: orderForm.order_items,
        auditContext: {
          action: 'target_changed',
          label: 'Room/Table Changed',
          message: `Order moved from ${sourceTargetLabel} to ${nextTargetLabel}.`,
          details: {
            from_target: sourceTargetLabel,
            to_target: nextTargetLabel,
            from_target_type: selection?.targetType || '',
            to_target_type: nextTargetType,
          },
        },
      });

      let persistedOrder = null;
      if (sourceOrderId) {
        persistedOrder = await updatePosOrder(sourceOrderId, movePayload);
      } else {
        persistedOrder = await createPosOrder(movePayload);
      }

      const persistedTaxBreakdown = Array.isArray(persistedOrder?.tax_breakdown)
        ? persistedOrder.tax_breakdown
        : [];
      const persistedAppliedTaxes = mapOrderTaxBreakdownToAppliedTaxes(
        persistedTaxBreakdown,
        foodBeverageTaxes,
        { explicitEmptyMeansDisabled: true }
      );

      setSelection({ targetType: nextTargetType, target: nextTarget });
      const movedOrderId = Number(persistedOrder?.id || sourceOrderId || 0);
      setEditingOrder(movedOrderId > 0
        ? {
          id: movedOrderId,
          order_number: persistedOrder?.order_number || editingOrder?.order_number || '',
          status: persistedOrder?.status || sourceOrder?.status || 'placed',
          queue_timers: normalizeQueueTimers(persistedOrder?.queue_timers || sourceOrder?.queue_timers || editingOrder?.queue_timers),
          preparation_time_minutes: Number(
            persistedOrder?.preparation_time_minutes
            || orderMeta.preparation_time_minutes
            || 30
          ),
        }
        : null);
      setOrderForm((prev) => ({
        notes: getEditableOrderNote(persistedOrder?.notes || prev.notes),
        order_items: normalizeDraftOrderItems(persistedOrder?.items || prev.order_items),
      }));
      setOrderMeta((prev) => ({
        ...prev,
        service_mode: effectiveServiceMode,
        phone_country_code: persistedOrder?.customer_phone_country_code || prev.phone_country_code || guestPhoneMeta.countryCode || '+91',
        mobile: persistedOrder?.customer_phone || prev.mobile || (nextTargetType === 'room' ? (guestPhoneMeta.mobile || '') : ''),
        customer_name: persistedOrder?.customer_name || prev.customer_name || (nextTargetType === 'room' ? (nextTarget?.guest_name || '') : ''),
        payment_collection_mode: persistedOrder?.payment_collection_mode || prev.payment_collection_mode,
        payment_method: persistedOrder?.payment_method || prev.payment_method,
        partial_paid_amount: '',
        preparation_time_minutes: Number(
          persistedOrder?.preparation_time_minutes
          || prev.preparation_time_minutes
          || 30
        ),
      }));
      setBillingAdjustments((prev) => ({
        ...prev,
        discount_type: persistedOrder?.discount_type || prev.discount_type,
        discount_value: String(Number(persistedOrder?.discount_value ?? prev.discount_value ?? 0)),
        applied_taxes: persistedAppliedTaxes,
        delivery_charge: String(Number(persistedOrder?.delivery_charge ?? prev.delivery_charge ?? 0)),
        container_charge: String(Number(persistedOrder?.container_charge ?? prev.container_charge ?? 0)),
      }));

      await loadData({ silent: true });
      setToastMessage('success', `Order moved to ${nextTargetLabel} and auto-saved.`);
    } catch (err) {
      setToastMessage('error', err?.userMessage || err?.message || 'Failed to move order.');
    } finally {
      setPendingMergeSource(null);
      setIsTargetChangeLoading(false);
    }
  };

  const openOrderDrawer = (targetType, target, preferredOrderId = null) => {
    const matchingActiveOrders = orders.filter((order) => {
      if (!EDITABLE_ORDER_STATUSES.has(order?.status)) return false;
      if (targetType === 'room') return Number(order?.room) === Number(target?.id);
      return Number(order?.table) === Number(target?.id);
    });
    const preferredOrder = preferredOrderId
      ? getOrderById(preferredOrderId)
      : null;
    const existingOrder = preferredOrder
      || (preferredOrderId
        ? matchingActiveOrders.find((order) => Number(order?.id) === Number(preferredOrderId))
          || matchingActiveOrders
            .slice()
            .sort(
              (first, second) =>
                new Date(second?.updated_at || second?.created_at || 0).getTime()
                - new Date(first?.updated_at || first?.created_at || 0).getTime()
            )[0]
        : matchingActiveOrders
          .slice()
          .sort(
            (first, second) =>
              new Date(second?.updated_at || second?.created_at || 0).getTime()
              - new Date(first?.updated_at || first?.created_at || 0).getTime()
          )[0]) || null;
    const existingItems = normalizeDraftOrderItems(existingOrder?.items || []);
    const existingTaxBreakdown = Array.isArray(existingOrder?.tax_breakdown)
      ? existingOrder.tax_breakdown
      : [];
    const loadedAppliedTaxes = mapOrderTaxBreakdownToAppliedTaxes(
      existingTaxBreakdown,
      foodBeverageTaxes,
      { explicitEmptyMeansDisabled: Boolean(existingOrder) }
    );
    const serviceMode = existingOrder
      ? getServiceModeFromOrderType(existingOrder.order_type)
      : (targetType === 'room' ? 'room_service' : 'dine_in');
    const guestPhoneMeta = parseGuestPhoneForForm(target?.guest_phone, target?.guest_country_code);
    setSelection({ targetType, target });
    setEditingOrder(existingOrder
      ? {
        id: existingOrder.id,
        order_number: existingOrder.order_number,
        status: existingOrder.status,
        queue_timers: normalizeQueueTimers(existingOrder.queue_timers),
        preparation_time_minutes: Number(existingOrder.preparation_time_minutes || 30),
      }
      : null);
    setOrderForm({
      notes: existingOrder ? getEditableOrderNote(existingOrder.notes) : '',
      order_items: existingItems,
    });
    setOrderMeta({
      service_mode: serviceMode,
      phone_country_code: existingOrder?.customer_phone_country_code || guestPhoneMeta.countryCode || '+91',
      mobile: existingOrder?.customer_phone || (targetType === 'room' ? (guestPhoneMeta.mobile || '') : ''),
      customer_name: existingOrder?.customer_name || (targetType === 'room' ? (target?.guest_name || '') : ''),
      payment_collection_mode: existingOrder?.payment_collection_mode || 'collect_now',
      payment_method: existingOrder?.payment_method || '',
      partial_paid_amount: '',
      preparation_time_minutes: Number(existingOrder?.preparation_time_minutes || 30),
    });
    setItemEntry({
      menu_item_id: '',
      quantity: 1,
    });
    setVariationSelectionDialog({
      open: false,
      menu_item_id: '',
      quantity: 1,
      options: [],
      selected_variation_id: '',
    });
    setItemEntryErrors({ menu_item_id: '', quantity: '' });
    setBillingAdjustments({
      discount_type: existingOrder?.discount_type || 'percentage',
      discount_value: existingOrder ? String(Number(existingOrder.discount_value || 0)) : '',
      applied_taxes: loadedAppliedTaxes,
      delivery_charge: existingOrder ? String(Number(existingOrder.delivery_charge || 0)) : '',
      container_charge: existingOrder ? String(Number(existingOrder.container_charge || 0)) : '',
    });
    setPendingMergeSource(null);
    setIsTargetChangeLoading(false);
    setOrderTargetMenuAnchorEl(null);
    setPaymentDrawerOpen(false);
    setDrawerOpen(true);
  };

  useEffect(() => {
    let cancelled = false;
    const query = new URLSearchParams(location.search);
    const editOrderParam = embedded ? String(initialEditOrderId || '') : query.get('edit_order');
    if (!editOrderParam) {
      if (!embedded) {
        lastHandledEditOrderRef.current = null;
      }
      return;
    }
    if (loading) return;
    if (lastHandledEditOrderRef.current === editOrderParam) return;

    const orderId = Number(editOrderParam);
    if (!orderId) {
      if (embedded) {
        setToastMessage('error', 'Invalid order selected for editing.');
        if (typeof onEmbeddedClose === 'function') {
          onEmbeddedClose();
        }
        lastHandledEditOrderRef.current = editOrderParam;
        return;
      }
      query.delete('edit_order');
      navigate(
        {
          pathname: location.pathname,
          search: query.toString() ? `?${query.toString()}` : '',
        },
        { replace: true }
      );
      return;
    }

    const clearEditOrderQuery = () => {
      if (embedded) {
        lastHandledEditOrderRef.current = editOrderParam;
        return;
      }
      query.delete('edit_order');
      navigate(
        {
          pathname: location.pathname,
          search: query.toString() ? `?${query.toString()}` : '',
        },
        { replace: true }
      );
      lastHandledEditOrderRef.current = editOrderParam;
    };

    const openFromResolvedOrder = (resolvedOrder) => {
      const roomId = Number(resolvedOrder?.room || 0);
      const tableId = Number(resolvedOrder?.table || 0);
      if (roomId > 0) {
        const roomTarget = data.rooms.find((room) => Number(room.id) === roomId) || {
          id: roomId,
          room_number: resolvedOrder.room_number || '—',
          guest_name: resolvedOrder.customer_name || '',
        };
        openOrderDrawer('room', roomTarget, resolvedOrder.id);
        return true;
      }
      if (tableId > 0) {
        const tableTarget = data.tables.find((table) => Number(table.id) === tableId) || {
          id: tableId,
          name: resolvedOrder.table_name || 'Table',
        };
        openOrderDrawer('table', tableTarget, resolvedOrder.id);
        return true;
      }
      return false;
    };

    const resolveAndOpenOrder = async () => {
      let targetOrder = getOrderById(orderId);

      if (!targetOrder) {
        try {
          const fetchedOrder = await getPosOrderDetail(orderId);
          if (cancelled) return;
          if (Number(fetchedOrder?.id || 0) === orderId) {
            targetOrder = fetchedOrder;
            setOrders((prevOrders) => {
              const rows = Array.isArray(prevOrders) ? prevOrders : [];
              const existingIndex = rows.findIndex((row) => Number(row?.id) === orderId);
              if (existingIndex > -1) {
                const nextRows = [...rows];
                nextRows[existingIndex] = { ...nextRows[existingIndex], ...fetchedOrder };
                return nextRows;
              }
              return [...rows, fetchedOrder];
            });
          }
        } catch (_err) {
          // Fallback to existing not-found behavior.
        }
      }

      if (!targetOrder) {
        setToastMessage('error', `Order #${orderId} not found.`);
        clearEditOrderQuery();
        return;
      }

      const opened = openFromResolvedOrder(targetOrder);
      if (!opened) {
        setToastMessage('error', 'Selected order has no room/table reference for editing.');
      }
      clearEditOrderQuery();
    };

    resolveAndOpenOrder();
    return () => {
      cancelled = true;
    };
  }, [
    location.search,
    location.pathname,
    navigate,
    loading,
    orders,
    data.rooms,
    data.tables,
    embedded,
    initialEditOrderId,
    onEmbeddedClose,
  ]);

  const handleQueueOrderClick = (order) => {
    if (!order) return;
    const roomId = Number(order?.room || 0);
    const tableId = Number(order?.table || 0);
    if (roomId > 0) {
      const roomTarget = data.rooms.find((room) => Number(room.id) === roomId) || {
        id: roomId,
        room_number: order.room_number || '—',
      };
      openOrderDrawer('room', roomTarget, order.id);
      return;
    }
    if (tableId > 0) {
      const tableTarget = data.tables.find((table) => Number(table.id) === tableId) || {
        id: tableId,
        name: order.table_name || 'Table',
      };
      openOrderDrawer('table', tableTarget, order.id);
      return;
    }
    setToastMessage('error', 'This queue order has no room/table reference to open.');
  };

  const closeDrawer = ({ notifyParent = true } = {}) => {
    setDrawerOpen(false);
    setPaymentDrawerOpen(false);
    setEbillDialog({ open: false, email: '', error: '', loading: false, source: '' });
    setVariationSelectionDialog({
      open: false,
      menu_item_id: '',
      quantity: 1,
      options: [],
      selected_variation_id: '',
    });
    setPaymentValidationError('');
    setOrderTargetMenuAnchorEl(null);
    setPendingMergeSource(null);
    setIsTargetChangeLoading(false);
    setCancelDialog({ open: false, reason: '', error: '' });
    setUpdateReasonDialog({ open: false, reason: '', error: '' });
    setSelection(null);
    setEditingOrder(null);
    setKotConfirmDialog((prev) => ({ ...prev, open: false }));
    setSearchingGuest(false);
    setPhoneCountryCodeMenuOpen(false);
    setPhoneCountryCodeSearch('');
    if (embedded && notifyParent && typeof onEmbeddedClose === 'function') {
      onEmbeddedClose();
    }
  };

  const openPaymentDrawer = () => {
    if (isEmbeddedEditMode) {
      return;
    }
    if (!selection) {
      setToastMessage('error', 'Choose room or table to place order.');
      return;
    }
    const validItems = orderForm.order_items.filter((row) => row.menu_item && Number(row.quantity) > 0);
    if (validItems.length === 0) {
      setToastMessage('error', 'Add at least one menu item before payment.');
      return;
    }
    setPaymentValidationError('');
    setPaymentDrawerOpen(true);
  };

  const closePaymentDrawer = () => {
    setPaymentDrawerOpen(false);
    setPaymentValidationError('');
    setOrderTargetMenuAnchorEl(null);
    setEbillDialog({ open: false, email: '', error: '', loading: false, source: '' });
  };

  const openEbillDialog = async () => {
    if (!selection) {
      setToastMessage('error', 'Choose room or table to place order.');
      return;
    }
    const validItems = orderForm.order_items.filter((row) => row.menu_item && Number(row.quantity) > 0);
    if (validItems.length === 0) {
      setToastMessage('error', 'Add at least one menu item before payment.');
      return;
    }
    const isDuePayment = orderMeta.payment_collection_mode === 'due';
    const isPartialPayment = orderMeta.payment_collection_mode === 'partial';
    const selectedPaymentMethod = resolvedPaymentMethod;
    if (!isDuePayment && !selectedPaymentMethod) {
      setPaymentValidationError('Select payment method.');
      return;
    }
    if (isPartialPayment) {
      const enteredPartialAmount = Number(orderMeta.partial_paid_amount || 0);
      if (!Number.isFinite(enteredPartialAmount) || enteredPartialAmount <= 0) {
        setPaymentValidationError('Enter partial paid amount.');
        return;
      }
      if (enteredPartialAmount >= (partialPaymentBaseAmount - 0.005)) {
        setPaymentValidationError('For full payment choose Collect Now.');
        return;
      }
      if (partialDueNowAmount <= 0) {
        setPaymentValidationError('Remaining due amount should be greater than zero.');
        return;
      }
    }
    setPaymentValidationError('');

    const localSuggestedEmail = String(selection?.target?.guest_email || '').trim();
    setEbillDialog({
      open: true,
      email: localSuggestedEmail,
      error: '',
      loading: Boolean(editingOrder?.id),
      source: localSuggestedEmail ? 'room_guest' : '',
    });

    const orderId = Number(editingOrder?.id || 0);
    if (!orderId) {
      return;
    }

    try {
      const payload = await getPosOrderEbillContext(orderId);
      const apiSuggestedEmail = String(payload?.default_email || '').trim();
      setEbillDialog((prev) => ({
        ...prev,
        email: apiSuggestedEmail || prev.email,
        source: apiSuggestedEmail ? 'booking_guest' : prev.source,
        loading: false,
      }));
    } catch (_err) {
      setEbillDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const closeEbillDialog = () => {
    setEbillDialog({ open: false, email: '', error: '', loading: false, source: '' });
  };

  const confirmPaymentAndSendEbill = async () => {
    const email = String(ebillDialog.email || '').trim();
    if (!email) {
      setEbillDialog((prev) => ({ ...prev, error: 'Email is required to send e-bill.' }));
      return;
    }
    if (!EMAIL_REGEX.test(email)) {
      setEbillDialog((prev) => ({ ...prev, error: 'Enter a valid email address.' }));
      return;
    }
    setEbillDialog((prev) => ({ ...prev, error: '' }));
    const success = await handleSubmitOrder('payment_ebill', { recipientEmail: email });
    if (success) {
      closeEbillDialog();
    }
  };

  const getThermalPaperWidthMm = () => readThermalPaperWidthMm();

  const getPrintableOrderItems = (order) => {
    const menuLookup = new Map((data?.menu_items || []).map((item) => [Number(item.id), item]));
    const variationLookup = new Map(
      (data?.menu_variations || []).map((variation) => [Number(variation.id), variation])
    );
    const sourceItems = Array.isArray(order?.items) && order.items.length
      ? order.items
      : orderForm.order_items;

    return (Array.isArray(sourceItems) ? sourceItems : [])
      .filter((item) => Number(item?.quantity || 0) > 0)
      .map((item) => {
        const menuItem = menuLookup.get(Number(item.menu_item || item.menu_item_id));
        const variationId = normalizeVariationId(item.variation_id);
        const variation = variationId ? variationLookup.get(Number(variationId)) : null;
        const baseName = item.menu_item_name || item.item_name || menuItem?.name || 'Item';
        const variationName = item.variation_label || item.variation_name || variation?.name || '';
        const quantity = Number(item.quantity || 0);
        const unitPrice = Number(
          item.unit_price ??
          item.price ??
          item.rate ??
          (
            Number(menuItem?.price || 0) +
            (variationId ? Number(variation?.price || item.variation_price || 0) : 0)
          )
        );
        const amount = Number(item.amount ?? item.total ?? (unitPrice * quantity));

        return {
          key: [
            Number(item.menu_item || item.menu_item_id || menuItem?.id || 0),
            variationId || '',
            baseName,
            variationName,
            String(item.special_note || item.note || '').trim(),
            unitPrice.toFixed(2),
          ].join('|'),
          name: variationName ? `${baseName} (${variationName})` : baseName,
          quantity,
          unitPrice,
          amount,
          note: item.special_note || item.note || '',
        };
      });
  };

  const buildRestaurantPrintHtml = (order, printType = 'bill', printOptions = {}) => {
    // Shared thermal HTML (same layout as billing). Do not fork styles here.
    return buildRestaurantReceiptHtml(order, printType, printOptions, {
      hotel: data?.hotel || data?.selected_hotel || {},
      menuItems: data?.menu_items || [],
      menuVariations: data?.menu_variations || [],
      fallbackItems: orderForm.order_items,
      targetLabel: getOrderTargetLabel(selection?.targetType, selection?.target)
        || order?.room_number
        || order?.table_name
        || '',
      orderTypeLabel: getOrderTypeLabel(order?.order_type || getOrderTypeFromServiceMode(orderMeta.service_mode)),
      customerName: order?.customer_name || orderMeta.customer_name || '',
      customerPhone: order?.customer_phone || orderMeta.mobile || '',
      subtotal: order?.subtotal ?? order?.sub_total ?? estimatedSubtotal ?? 0,
      discountAmount: order?.discount_amount ?? discountAmount ?? 0,
      deliveryCharge: order?.delivery_charge ?? deliveryChargeAmount ?? 0,
      containerCharge: order?.container_charge ?? containerChargeAmount ?? 0,
      grandTotal: order?.total_amount ?? estimatedTotal ?? estimatedSubtotal ?? 0,
      taxBreakdown: Array.isArray(order?.tax_breakdown) ? order.tax_breakdown : configuredTaxBreakdown,
      notes: order?.notes || orderForm.notes || '',
      widthMm: getThermalPaperWidthMm(),
    });
  };

  const getNextKotPrintNumber = (order, { commit = true } = {}) => {
    const orderKey = formatOrderNumber(order?.order_number || order?.id || 'draft');
    const storageKey = `roomnexa.restaurant.kotPrintCount.${orderKey}`;
    const timerCount = normalizeQueueTimers(order?.queue_timers).length || 1;
    try {
      const currentCount = Number(window.localStorage.getItem(storageKey) || 0);
      const nextCount = Math.max(currentCount + 1, timerCount);
      if (commit) {
        window.localStorage.setItem(storageKey, String(nextCount));
      }
      return nextCount;
    } catch (_err) {
      return timerCount;
    }
  };

  const getKotPrintedItemsStorageKey = (order) =>
    `roomnexa.restaurant.kotPrintedItems.${formatOrderNumber(order?.order_number || order?.id || 'draft')}`;

  const aggregatePrintableItemsForKot = (items) => {
    const lookup = {};
    (Array.isArray(items) ? items : []).forEach((item) => {
      const key = item.key || `${item.name}|${item.note || ''}|${item.unitPrice}`;
      const quantity = Number(item.quantity || 0);
      if (!lookup[key]) {
        lookup[key] = { ...item, key, quantity: 0, amount: 0 };
      }
      lookup[key].quantity += quantity;
      lookup[key].amount += Number(item.amount || (Number(item.unitPrice || 0) * quantity));
    });
    return lookup;
  };

  const getKotDeltaPrintPayload = (order) => {
    const currentItems = getPrintableOrderItems(order);
    const currentSnapshot = aggregatePrintableItemsForKot(currentItems);
    let previousSnapshot = {};

    try {
      previousSnapshot = JSON.parse(window.localStorage.getItem(getKotPrintedItemsStorageKey(order)) || '{}') || {};
    } catch (_err) {
      previousSnapshot = {};
    }

    const changedItems = Object.values(currentSnapshot)
      .map((item) => {
        const previousQuantity = Number(previousSnapshot?.[item.key]?.quantity || 0);
        const deltaQuantity = Number(item.quantity || 0) - previousQuantity;
        if (deltaQuantity <= 0) return null;
        return {
          ...item,
          quantity: deltaQuantity,
          amount: Number(item.unitPrice || 0) * deltaQuantity,
        };
      })
      .filter(Boolean);

    return { changedItems, currentSnapshot };
  };

  const printHtmlDirectly = (html, title = 'Restaurant Print') => {
    if (!html) return false;
    const printFrame = document.createElement('iframe');
    printFrame.title = title;
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    printFrame.style.opacity = '0';
    printFrame.style.pointerEvents = 'none';
    document.body.appendChild(printFrame);

    const cleanup = () => {
      setTimeout(() => {
        if (printFrame.parentNode) {
          printFrame.parentNode.removeChild(printFrame);
        }
      }, 500);
    };

    try {
      const frameDocument = printFrame.contentDocument || printFrame.contentWindow?.document;
      if (!frameDocument || !printFrame.contentWindow) {
        cleanup();
        return false;
      }
      frameDocument.open();
      frameDocument.write(html);
      frameDocument.close();
      setTimeout(() => {
        try {
          printFrame.contentWindow.focus();
          printFrame.contentWindow.print();
        } finally {
          cleanup();
        }
      }, 250);
      return true;
    } catch (_err) {
      cleanup();
      return false;
    }
  };

  const getDesktopPrintHtmlOptions = (printOptions = {}) => ({
    ...printOptions,
    // Electron silent print uses webContents.print — disable embedded window.print().
    autoPrintOnLoad: hasDesktopPrintApi() ? false : printOptions.autoPrintOnLoad,
    showPreviewToolbar: hasDesktopPrintApi() ? false : printOptions.showPreviewToolbar,
  });

  const ensureDesktopPrinterReady = () => {
    if (!hasDesktopPrintApi()) return true;
    if (isPrinterValidated()) return true;
    setToastMessage('warning', 'Please validate the printer from app before printing.');
    openPrinterValidationDialog();
    return false;
  };

  const getRestaurantPrintWarningSuffix = (printResult, messages) => {
    if (!printResult || printResult.opened) return '';
    const { reason } = printResult;
    if (reason === 'no_changes') return messages.noChanges || '';
    if (reason === 'printer_not_validated') return messages.printerNotValidated || '';
    if (reason === 'desktop_api_missing') return messages.desktopApiMissing || '';
    if (reason === 'print_failed') {
      return (messages.printFailed || '').replace('{error}', printResult.error || 'check printer');
    }
    return messages.blocked || '';
  };

  const printRestaurantHtml = async (html, title = 'Restaurant Print') => {
    if (isDesktopShellMissingApis()) {
      return { opened: false, reason: 'desktop_api_missing' };
    }
    if (hasDesktopPrintApi()) {
      if (!isPrinterValidated()) {
        openPrinterValidationDialog();
        return { opened: false, reason: 'printer_not_validated' };
      }
      const printKind = title.startsWith('KOT') ? 'kot' : 'bill';
      const result = await silentPrintRestaurantHtml(html, { printKind });
      if (!result.success) {
        return { opened: false, reason: 'print_failed', error: result.error };
      }
      return { opened: true, silent: true };
    }

    const didPrint = printHtmlDirectly(html, title);
    return didPrint ? { opened: true, silent: false } : { opened: false, reason: 'blocked' };
  };

  const triggerRestaurantThermalPrint = async (order, printType = 'bill') => {
    let printOptions = {};
    let nextKotSnapshot = null;
    if (printType === 'kot') {
      const { changedItems, currentSnapshot } = getKotDeltaPrintPayload(order);
      if (!changedItems.length) {
        return { opened: false, reason: 'no_changes' };
      }
      printOptions = getDesktopPrintHtmlOptions({
        kotNumber: getNextKotPrintNumber(order),
        orderItems: changedItems,
        showPreviewToolbar: false,
        autoPrintOnLoad: false,
      });
      nextKotSnapshot = currentSnapshot;
      const html = buildRestaurantPrintHtml(order, printType, printOptions);
      const printResult = await printRestaurantHtml(html, `KOT - ${printOptions.kotNumber}`);
      if (!printResult.opened) return printResult;
      if (nextKotSnapshot) {
        try {
          window.localStorage.setItem(getKotPrintedItemsStorageKey(order), JSON.stringify(nextKotSnapshot));
        } catch (_err) {}
      }
      return printResult;
    }

    const html = buildRestaurantPrintHtml(order, printType, getDesktopPrintHtmlOptions(printOptions));
    const invoiceTitle = `Invoice - ${formatOrderNumber(order?.order_number || order?.id || 'draft')}`;
    return printRestaurantHtml(html, invoiceTitle);
  };

  const handlePrintBeforePayment = async () => {
    if (!ensureDesktopPrinterReady()) {
      return;
    }
    if (!selection) {
      setToastMessage('error', 'Choose room or table to print invoice.');
      return;
    }
    const validItems = orderForm.order_items.filter((row) => row.menu_item && Number(row.quantity) > 0);
    if (validItems.length === 0) {
      setToastMessage('error', 'Add at least one menu item before printing.');
      return;
    }

    const draftInvoiceOrder = {
      ...(editingOrder || {}),
      id: editingOrder?.id || 'draft',
      order_number: editingOrder?.order_number || 'Draft',
      order_type: getOrderTypeFromServiceMode(orderMeta.service_mode),
      customer_name: String(orderMeta.customer_name || '').trim(),
      customer_phone: String(orderMeta.mobile || '').trim(),
      room_number: selection?.targetType === 'room' ? selection?.target?.room_number : '',
      table_name: selection?.targetType === 'table' ? selection?.target?.name : '',
      items: validItems,
      subtotal: Number(estimatedSubtotal || 0),
      discount_amount: Number(discountAmount || 0),
      delivery_charge: Number(deliveryChargeAmount || 0),
      container_charge: Number(containerChargeAmount || 0),
      tax_breakdown: configuredTaxBreakdown,
      total_amount: Number(estimatedTotal || 0),
      notes: String(orderForm.notes || '').trim(),
    };

    const printResult = await triggerRestaurantThermalPrint(draftInvoiceOrder, 'bill');
    if (!printResult.opened) {
      if (printResult.reason === 'printer_not_validated') {
        setToastMessage('warning', 'Please validate the printer from app before printing.');
        return;
      }
      if (printResult.reason === 'desktop_api_missing') {
        setToastMessage('error', 'Desktop print API unavailable. Reinstall the latest RoomNexa desktop app.');
        return;
      }
      setToastMessage('warning', printResult.error || 'Print failed. Check the validated printer.');
      return;
    }
    setToastMessage('success', printResult.silent ? 'Invoice sent to printer.' : 'Invoice print preview opened.');
  };

  const resetCurrentOrderDraft = () => {
    const guestPhoneMeta = parseGuestPhoneForForm(
      selection?.target?.guest_phone,
      selection?.target?.guest_country_code
    );
    setOrderForm({ notes: '', order_items: [] });
    setItemEntry({ menu_item_id: '', quantity: 1 });
    setVariationSelectionDialog({
      open: false,
      menu_item_id: '',
      quantity: 1,
      options: [],
      selected_variation_id: '',
    });
    setItemEntryErrors({ menu_item_id: '', quantity: '' });
    setBillingAdjustments({
      discount_type: 'percentage',
      discount_value: '',
      applied_taxes: buildAppliedTaxesFromRules(foodBeverageTaxes),
      delivery_charge: '',
      container_charge: '',
    });
    setOrderMeta({
      service_mode: selection?.targetType === 'room' ? 'room_service' : 'dine_in',
      phone_country_code: guestPhoneMeta.countryCode || '+91',
      mobile: selection?.targetType === 'room' ? (guestPhoneMeta.mobile || '') : '',
      customer_name:
        selection?.targetType === 'room'
          ? (selection?.target?.guest_name || '')
          : '',
      payment_collection_mode: 'collect_now',
      payment_method: '',
      partial_paid_amount: '',
      preparation_time_minutes: 30,
    });
    setEditingOrder(null);
    setPendingMergeSource(null);
    setCancelDialog({ open: false, reason: '', error: '' });
    setPaymentValidationError('');
  };

  const updateOrderItem = (index, patch) => {
    setOrderForm((prev) => {
      const nextItems = [...prev.order_items];
      nextItems[index] = { ...nextItems[index], ...patch };
      return { ...prev, order_items: nextItems };
    });
  };

  const removeOrderItem = (index) => {
    setOrderForm((prev) => {
      return {
        ...prev,
        order_items: prev.order_items.filter((_, idx) => idx !== index),
      };
    });
  };

  const toggleAppliedTax = (taxKey, enabled) => {
    setBillingAdjustments((prev) => ({
      ...prev,
      applied_taxes: (prev.applied_taxes || []).map((row) => (row.tax_key === taxKey
        ? { ...row, enabled }
        : row)),
    }));
  };

  const addOrderItemToDraft = useCallback((itemPayload) => {
    const menuItemId = Number(itemPayload?.menu_item || 0);
    const quantity = Math.max(1, Number(itemPayload?.quantity || 1));
    const variationId = normalizeVariationId(itemPayload?.variation_id);
    const variationLabel = String(itemPayload?.variation_label || '').trim();
    const variationPrice = Math.max(0, Number(itemPayload?.variation_price || 0));
    if (!menuItemId) return;
    setOrderForm((prev) => {
      const nextItems = Array.isArray(prev?.order_items) ? [...prev.order_items] : [];
      const itemIdentityKey = getOrderItemIdentityKey({
        menu_item: menuItemId,
        variation_id: variationId,
      });
      const existingIndex = nextItems.findIndex((row) => getOrderItemIdentityKey(row) === itemIdentityKey);
      if (existingIndex >= 0) {
        const oldQty = Number(nextItems[existingIndex].quantity || 0);
        nextItems[existingIndex] = {
          ...nextItems[existingIndex],
          quantity: oldQty + quantity,
          variation_id: variationId,
          variation_label: variationLabel,
          variation_price: variationPrice,
        };
        return { ...prev, order_items: nextItems };
      }
      return {
        ...prev,
        order_items: [
          ...nextItems,
          {
            ...defaultOrderItem,
            menu_item: menuItemId,
            quantity,
            variation_id: variationId,
            variation_label: variationLabel,
            variation_price: variationPrice,
          },
        ],
      };
    });
  }, []);

  const closeVariationSelectionDialog = useCallback(() => {
    setVariationSelectionDialog({
      open: false,
      menu_item_id: '',
      quantity: 1,
      options: [],
      selected_variation_id: '',
    });
  }, []);

  const handleConfirmVariationSelection = useCallback(() => {
    if (!variationSelectionDialog.open) return;
    const menuItemId = Number(variationSelectionDialog.menu_item_id || 0);
    const quantity = Math.max(1, Number(variationSelectionDialog.quantity || 1));
    const selectedVariationId = normalizeVariationId(variationSelectionDialog.selected_variation_id);
    const selectedVariation = (variationSelectionDialog.options || []).find(
      (option) => Number(option.variation_id) === Number(selectedVariationId)
    );
    if (!menuItemId || !selectedVariation) {
      setToastMessage('warning', 'Select a variation to add this item.');
      return;
    }
    addOrderItemToDraft({
      menu_item: menuItemId,
      quantity,
      variation_id: selectedVariation.variation_id,
      variation_label: selectedVariation.label,
      variation_price: selectedVariation.price,
    });
    setItemEntry({ menu_item_id: '', quantity: 1 });
    setItemEntryErrors({ menu_item_id: '', quantity: '' });
    closeVariationSelectionDialog();
  }, [addOrderItemToDraft, closeVariationSelectionDialog, setToastMessage, variationSelectionDialog]);

  const handleAddSearchedItem = () => {
    const menuItemId = Number(itemEntry.menu_item_id);
    const quantity = Number(itemEntry.quantity || 0);
    const nextErrors = { menu_item_id: '', quantity: '' };
    if (!menuItemId) {
      nextErrors.menu_item_id = 'Please select a menu item.';
    }
    if (quantity <= 0) {
      nextErrors.quantity = 'Quantity should be greater than 0.';
    }
    if (nextErrors.menu_item_id || nextErrors.quantity) {
      setItemEntryErrors(nextErrors);
      return;
    }
    setItemEntryErrors({ menu_item_id: '', quantity: '' });
    const variationOptions = menuItemVariationOptionsByItemId.get(menuItemId) || [];
    if (variationOptions.length > 0) {
      setVariationSelectionDialog({
        open: true,
        menu_item_id: String(menuItemId),
        quantity: Math.max(1, quantity),
        options: variationOptions,
        selected_variation_id: String(variationOptions[0]?.variation_id || ''),
      });
      return;
    }
    addOrderItemToDraft({
      menu_item: menuItemId,
      quantity: Math.max(1, quantity),
    });
    setItemEntry({ menu_item_id: '', quantity: 1 });
    setItemEntryErrors({ menu_item_id: '', quantity: '' });
  };

  const normalizeQty = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 1) return 1;
    return Math.floor(parsed);
  };

  const changeEntryQty = (delta) => {
    setItemEntry((prev) => ({
      ...prev,
      quantity: Math.max(1, normalizeQty(prev.quantity) + delta),
    }));
  };

  const changeRowQty = (index, delta) => {
    setOrderForm((prev) => {
      const nextItems = [...prev.order_items];
      const currentQty = normalizeQty(nextItems[index]?.quantity);
      nextItems[index] = {
        ...nextItems[index],
        quantity: Math.max(1, currentQty + delta),
      };
      return { ...prev, order_items: nextItems };
    });
  };

  const changeChargeValue = (field, delta) => {
    setBillingAdjustments((prev) => {
      const currentValue = Math.max(0, Number(prev[field] || 0));
      const nextValue = Math.max(0, currentValue + delta);
      return {
        ...prev,
        [field]: String(Number(nextValue.toFixed(2))),
      };
    });
  };

  const menuItemMap = useMemo(() => {
    const lookup = new Map();
    data.menu_items.forEach((item) => lookup.set(Number(item.id), item));
    return lookup;
  }, [data.menu_items]);
  const variationLabelMap = useMemo(
    () =>
      new Map(
        (data.menu_variations || []).map((variation) => [
          Number(variation.id),
          variation.online_display_name || variation.name || `Variation #${variation.id}`,
        ])
      ),
    [data.menu_variations]
  );
  const menuItemVariationOptionsByItemId = useMemo(() => {
    const lookup = new Map();
    (data.menu_items || []).forEach((menuItem) => {
      lookup.set(Number(menuItem.id), getMenuItemVariationOptions(menuItem, variationLabelMap));
    });
    return lookup;
  }, [data.menu_items, variationLabelMap]);
  const countryCodes = useMemo(() => countryCodesData, []);
  const selectedPhoneCountryCode = useMemo(
    () =>
      countryCodes.find((cc) => cc.dialCode === orderMeta.phone_country_code)
      || countryCodes.find((cc) => cc.code === 'IN'),
    [countryCodes, orderMeta.phone_country_code]
  );

  useEffect(() => {
    if (!drawerOpen) return;
    const phoneDigits = String(orderMeta.mobile || '').replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      setSearchingGuest(false);
      return;
    }
    let isCancelled = false;
    const timer = setTimeout(async () => {
      setSearchingGuest(true);
      try {
        const response = await searchGuestByPhone(phoneDigits);
        if (isCancelled) return;
        if (response?.guest) {
          const guest = response.guest;
          const guestName = `${guest.first_name || ''} ${guest.last_name || ''}`.trim() || guest.name || 'Guest';
          const parsedGuestPhone = parseGuestPhoneForForm(guest.phone, guest.country_code || orderMeta.phone_country_code);
          setOrderMeta((prev) => ({
            ...prev,
            phone_country_code: parsedGuestPhone.countryCode || prev.phone_country_code,
            mobile: parsedGuestPhone.mobile || prev.mobile,
            customer_name: guestName || prev.customer_name,
          }));
        }
      } catch (_error) {
        // Keep manual entry if lookup fails.
      } finally {
        if (!isCancelled) setSearchingGuest(false);
      }
    }, 320);

    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [drawerOpen, orderMeta.mobile, orderMeta.phone_country_code]);

  const estimatedSubtotal = useMemo(() => {
    const priceByItem = new Map(data.menu_items.map((item) => [item.id, Number(item.price)]));
    return orderForm.order_items.reduce((sum, row) => {
      const basePrice = priceByItem.get(Number(row.menu_item)) || 0;
      const price = resolveOrderItemUnitPrice(row, basePrice);
      const qty = Number(row.quantity || 0);
      return sum + price * qty;
    }, 0);
  }, [data.menu_items, orderForm.order_items]);

  const discountAmount = useMemo(() => {
    const discountValue = Number(billingAdjustments.discount_value || 0);
    if (discountValue <= 0) return 0;
    if (billingAdjustments.discount_type === 'percentage') {
      return (estimatedSubtotal * Math.min(discountValue, MAX_DISCOUNT_PERCENT)) / 100;
    }
    return Math.min(discountValue, getDiscountAmountCap(estimatedSubtotal));
  }, [billingAdjustments.discount_type, billingAdjustments.discount_value, estimatedSubtotal]);
  const discountProgressPercent = useMemo(() => {
    if (estimatedSubtotal <= 0) return 0;
    return Math.max(0, Math.min((discountAmount / estimatedSubtotal) * 100, 100));
  }, [discountAmount, estimatedSubtotal]);

  const taxableSubtotal = useMemo(
    () => Math.max(estimatedSubtotal - discountAmount, 0),
    [estimatedSubtotal, discountAmount]
  );

  const configuredTaxBreakdown = useMemo(
    () => (billingAdjustments.applied_taxes || [])
      .filter((taxRow) => taxRow?.enabled)
      .map((taxRow) => {
      const numericValue = Math.max(Number(taxRow?.value || 0), 0);
      const amount = taxRow?.mode === 'percentage'
        ? (taxableSubtotal * Math.min(numericValue, 100)) / 100
        : numericValue;
      return {
        ...taxRow,
        numericValue,
        amount: Number.isFinite(amount) ? amount : 0,
      };
      }),
    [billingAdjustments.applied_taxes, taxableSubtotal]
  );
  const configuredTaxAmountByKey = useMemo(() => {
    const lookup = new Map();
    configuredTaxBreakdown.forEach((row) => {
      lookup.set(row.tax_key, row.amount);
    });
    return lookup;
  }, [configuredTaxBreakdown]);
  const estimatedTax = useMemo(
    () => configuredTaxBreakdown.reduce((sum, row) => sum + Number(row.amount || 0), 0),
    [configuredTaxBreakdown]
  );
  const deliveryChargeAmount = useMemo(() => {
    const value = Number(billingAdjustments.delivery_charge || 0);
    return value > 0 ? value : 0;
  }, [billingAdjustments.delivery_charge]);
  const containerChargeAmount = useMemo(() => {
    const value = Number(billingAdjustments.container_charge || 0);
    return value > 0 ? value : 0;
  }, [billingAdjustments.container_charge]);
  const estimatedTotal = useMemo(
    () => taxableSubtotal + estimatedTax + deliveryChargeAmount + containerChargeAmount,
    [taxableSubtotal, estimatedTax, deliveryChargeAmount, containerChargeAmount]
  );
  const zeroAmountFullDiscountSettlement = useMemo(
    () => isZeroAmountFullDiscountSettlement(
      billingAdjustments.discount_type,
      billingAdjustments.discount_value,
      estimatedSubtotal,
      estimatedTotal,
    ),
    [
      billingAdjustments.discount_type,
      billingAdjustments.discount_value,
      estimatedSubtotal,
      estimatedTotal,
    ]
  );
  const resolvedPaymentMethod = useMemo(() => {
    if (zeroAmountFullDiscountSettlement) return COMPANY_SETTLEMENT_PAYMENT_METHOD;
    return String(orderMeta.payment_method || '').trim();
  }, [zeroAmountFullDiscountSettlement, orderMeta.payment_method]);
  const editOutstandingDueAmount = useMemo(() => {
    const isEmbeddedEdit = Boolean(embedded && editingOrder?.id);
    if (!isEmbeddedEdit) return 0;
    const sourceOrder = getOrderById(editingOrder?.id);
    const originalDueAmount = Number(sourceOrder?.due_amount || 0);
    const originalTotalAmount = Number(sourceOrder?.total_amount || 0);
    const settlementDelta = Number((estimatedTotal - originalTotalAmount).toFixed(2));
    const projected = originalDueAmount + settlementDelta;
    return Number(Math.min(Math.max(0, projected), estimatedTotal).toFixed(2));
  }, [embedded, editingOrder?.id, getOrderById, estimatedTotal]);
  const partialPaymentBaseAmount = useMemo(() => {
    if (embedded && editingOrder?.id) return Number(editOutstandingDueAmount.toFixed(2));
    return Number(estimatedTotal.toFixed(2));
  }, [embedded, editingOrder?.id, editOutstandingDueAmount, estimatedTotal]);
  const partialPaidNowAmount = useMemo(() => {
    const parsedValue = Number(orderMeta.partial_paid_amount || 0);
    if (!Number.isFinite(parsedValue) || parsedValue <= 0) return 0;
    return Number(Math.min(parsedValue, partialPaymentBaseAmount).toFixed(2));
  }, [orderMeta.partial_paid_amount, partialPaymentBaseAmount]);
  const partialDueNowAmount = useMemo(
    () => Number(Math.max(0, partialPaymentBaseAmount - partialPaidNowAmount).toFixed(2)),
    [partialPaymentBaseAmount, partialPaidNowAmount]
  );
  const isPartialPaymentMode = orderMeta.payment_collection_mode === 'partial';
  const partialPaidNowInlineError = useMemo(() => {
    if (!isPartialPaymentMode) return '';
    const rawValue = String(orderMeta.partial_paid_amount ?? '').trim();
    const enteredPartialAmount = Number(orderMeta.partial_paid_amount || 0);
    if (!rawValue || !Number.isFinite(enteredPartialAmount) || enteredPartialAmount <= 0) {
      return 'Enter partial paid amount.';
    }
    if (enteredPartialAmount >= (partialPaymentBaseAmount - 0.005)) {
      return 'For full payment choose Collect Now.';
    }
    if (partialDueNowAmount <= 0) {
      return 'Remaining due amount should be greater than zero.';
    }
    return '';
  }, [
    isPartialPaymentMode,
    orderMeta.partial_paid_amount,
    partialPaymentBaseAmount,
    partialDueNowAmount,
  ]);
  const partialPaymentMethodInlineError = useMemo(() => {
    if (!isPartialPaymentMode || zeroAmountFullDiscountSettlement) return '';
    return resolvedPaymentMethod ? '' : 'Select payment method.';
  }, [isPartialPaymentMode, zeroAmountFullDiscountSettlement, resolvedPaymentMethod]);
  const suppressPartialGlobalValidationError = useMemo(() => {
    if (!isPartialPaymentMode) return false;
    return [
      'Enter partial paid amount.',
      'For full payment choose Collect Now.',
      'Remaining due amount should be greater than zero.',
      'Select payment method.',
    ].includes(String(paymentValidationError || '').trim());
  }, [isPartialPaymentMode, paymentValidationError]);
  const currentEditingOrderSnapshot = useMemo(() => {
    if (!editingOrder?.id) return null;
    return getOrderById(editingOrder.id);
  }, [getOrderById, editingOrder?.id]);
  const originalOrderTotalAmount = useMemo(
    () => Number(currentEditingOrderSnapshot?.total_amount || 0),
    [currentEditingOrderSnapshot]
  );
  const originalOrderDueAmount = useMemo(
    () => Number(currentEditingOrderSnapshot?.due_amount || 0),
    [currentEditingOrderSnapshot]
  );
  const editSettlementDelta = useMemo(
    () => Number((estimatedTotal - originalOrderTotalAmount).toFixed(2)),
    [estimatedTotal, originalOrderTotalAmount]
  );
  const editSettlementAmount = useMemo(
    () => Math.abs(editSettlementDelta),
    [editSettlementDelta]
  );
  const editSettlementDirection = useMemo(() => {
    if (!editingOrder?.id) return 'no_change';
    if (editSettlementAmount < 0.005) return 'no_change';
    return editSettlementDelta > 0 ? 'collect' : 'return';
  }, [editingOrder?.id, editSettlementAmount, editSettlementDelta]);
  const discountAmountCap = useMemo(
    () => getDiscountAmountCap(estimatedSubtotal),
    [estimatedSubtotal]
  );
  const discountSliderMax = billingAdjustments.discount_type === 'percentage'
    ? MAX_DISCOUNT_PERCENT
    : discountAmountCap;
  const discountSliderStep = 1;
  const discountSliderValue = Math.max(
    0,
    Math.min(Number(billingAdjustments.discount_value || 0), discountSliderMax)
  );
  const handleDiscountSliderChange = (_, value) => {
    const nextValue = Array.isArray(value) ? value[0] : value;
    const boundedValue = clampDiscountValue(billingAdjustments.discount_type, nextValue, estimatedSubtotal);
    setBillingAdjustments((prev) => ({ ...prev, discount_value: String(boundedValue) }));
  };
  const handleDiscountStep = (direction) => {
    const delta = direction === 'inc' ? discountSliderStep : -discountSliderStep;
    const nextValue = Math.max(0, Math.min(discountSliderValue + delta, discountSliderMax));
    setBillingAdjustments((prev) => ({ ...prev, discount_value: String(nextValue) }));
  };
  useEffect(() => {
    setBillingAdjustments((prev) => {
      const currentValue = Number(prev.discount_value || 0);
      const boundedValue = clampDiscountValue(prev.discount_type, currentValue, estimatedSubtotal);
      if (Math.abs(currentValue - boundedValue) < 0.0001) {
        return prev;
      }
      return {
        ...prev,
        discount_value: String(boundedValue),
      };
    });
  }, [billingAdjustments.discount_type, estimatedSubtotal]);
  useEffect(() => {
    if (!zeroAmountFullDiscountSettlement) return;
    setOrderMeta((prev) => {
      const next = { ...prev };
      let changed = false;
      if (prev.payment_method !== COMPANY_SETTLEMENT_PAYMENT_METHOD) {
        next.payment_method = COMPANY_SETTLEMENT_PAYMENT_METHOD;
        changed = true;
      }
      if (prev.payment_collection_mode !== 'collect_now') {
        next.payment_collection_mode = 'collect_now';
        next.partial_paid_amount = '';
        changed = true;
      }
      return changed ? next : prev;
    });
    setPaymentValidationError('');
  }, [zeroAmountFullDiscountSettlement]);
  const activeOrderQueue = useMemo(
    () =>
      orders
        .filter((order) => QUEUE_VISIBLE_STATUSES.has(order.status))
        .sort((first, second) => new Date(first.created_at).getTime() - new Date(second.created_at).getTime()),
    [orders]
  );
  const queueRunningOrderTotal = useMemo(
    () => activeOrderQueue.reduce((sum, order) => sum + Number(order?.total_amount || 0), 0),
    [activeOrderQueue]
  );
  const allOrdersByRecent = useMemo(
    () => [...orders].sort((first, second) => new Date(second?.created_at || 0).getTime() - new Date(first?.created_at || 0).getTime()),
    [orders]
  );
  const pendingOrdersByRecent = useMemo(
    () => allOrdersByRecent.filter(
      (order) => isPendingOrderStatus(order?.status) && isQrBasedIncomingOrder(order)
    ),
    [allOrdersByRecent, isQrBasedIncomingOrder]
  );
  const activeRoomOrderCounts = useMemo(() => {
    const counts = new Map();
    activeOrderQueue.forEach((order) => {
      const roomId = Number(order?.room);
      if (roomId) {
        counts.set(roomId, (counts.get(roomId) || 0) + 1);
      }
    });
    return counts;
  }, [activeOrderQueue]);
  const activeTableOrderCounts = useMemo(() => {
    const counts = new Map();
    activeOrderQueue.forEach((order) => {
      const tableId = Number(order?.table);
      if (tableId) {
        counts.set(tableId, (counts.get(tableId) || 0) + 1);
      }
    });
    return counts;
  }, [activeOrderQueue]);
  const isEditingMode = Boolean(editingOrder?.id);
  const isEmbeddedEditMode = embedded && isEditingMode;
  const isEditRemainingDueMode = isEmbeddedEditMode
    && orderMeta.payment_collection_mode === 'due'
    && editSettlementDirection === 'collect'
    && editSettlementAmount >= 0.005;
  const isEditCollectNowOutstandingMode = isEmbeddedEditMode
    && orderMeta.payment_collection_mode === 'collect_now'
    && editOutstandingDueAmount >= 0.005;
  const editDueAmount = useMemo(() => {
    if (!isEmbeddedEditMode || orderMeta.payment_collection_mode !== 'due') return 0;
    return Number(editOutstandingDueAmount.toFixed(2));
  }, [
    isEmbeddedEditMode,
    orderMeta.payment_collection_mode,
    editOutstandingDueAmount,
  ]);
  const editDueSummaryAmount = useMemo(() => {
    if (!isEmbeddedEditMode) return 0;
    if (orderMeta.payment_collection_mode === 'partial') return Number(partialDueNowAmount.toFixed(2));
    if (orderMeta.payment_collection_mode === 'due') return Number(editDueAmount.toFixed(2));
    if (orderMeta.payment_collection_mode === 'collect_now') return Number(editOutstandingDueAmount.toFixed(2));
    return 0;
  }, [
    isEmbeddedEditMode,
    orderMeta.payment_collection_mode,
    partialDueNowAmount,
    editDueAmount,
    editOutstandingDueAmount,
  ]);
  const editPaymentStatusSummaryLabel = useMemo(() => {
    if (!isEmbeddedEditMode) return 'Paid';
    if (editDueSummaryAmount <= 0.005) return 'Paid';
    if (editDueSummaryAmount >= (estimatedTotal - 0.005)) return 'Due';
    return 'Partial';
  }, [isEmbeddedEditMode, editDueSummaryAmount, estimatedTotal]);
  const editDueSummaryLabel = useMemo(() => {
    if (orderMeta.payment_collection_mode === 'collect_now' && editDueSummaryAmount >= 0.005) {
      return 'Due to Collect';
    }
    return 'Due Amount';
  }, [orderMeta.payment_collection_mode, editDueSummaryAmount]);

  const openKotConfirmDialog = (actionType) => {
    const isKotAction = actionType === 'kot' || actionType === 'kot_print';
    const existingTimers = normalizeQueueTimers(editingOrder?.queue_timers);
    const activeTimer = existingTimers.find((timer) => !timer.ended_at);
    const defaultMinutes = (isKotAction && editingOrder)
      ? (activeTimer ? 10 : Number(orderMeta.preparation_time_minutes || 30))
      : Number(orderMeta.preparation_time_minutes || 30);
    setKotConfirmDialog({
      open: true,
      actionType,
      preparationTimeMinutes: defaultMinutes,
    });
  };

  const closeKotConfirmDialog = () => {
    setKotConfirmDialog((prev) => ({ ...prev, open: false }));
  };

  const confirmKotAndSubmit = async () => {
    const selectedMinutes = Number(kotConfirmDialog.preparationTimeMinutes || 30);
    setOrderMeta((prev) => ({ ...prev, preparation_time_minutes: selectedMinutes }));
    const actionType = kotConfirmDialog.actionType;
    closeKotConfirmDialog();
    await handleSubmitOrder(actionType, { preparationTimeMinutes: selectedMinutes });
  };

  const openCancelDialog = () => {
    if (!editingOrder?.id) {
      setToastMessage('error', 'Load an active order to cancel.');
      return;
    }
    setCancelDialog({
      open: true,
      reason: '',
      error: '',
    });
  };

  const closeCancelDialog = () => {
    setCancelDialog({
      open: false,
      reason: '',
      error: '',
    });
  };

  const confirmCancelOrder = async () => {
    if (!editingOrder?.id) {
      setToastMessage('error', 'No active order selected for cancellation.');
      return;
    }
    const reason = String(cancelDialog.reason || '').trim();
    if (!reason) {
      setCancelDialog((prev) => ({ ...prev, error: 'Please provide cancellation reason.' }));
      return;
    }
    const didCancel = await handleSubmitOrder('cancel', { cancelReason: reason });
    if (didCancel) {
      closeCancelDialog();
    }
  };

  const openUpdateReasonDialog = () => {
    if (!editingOrder?.id) {
      setToastMessage('error', 'No active order selected for update.');
      return;
    }
    const isDuePayment = orderMeta.payment_collection_mode === 'due';
    const selectedPaymentMethod = resolvedPaymentMethod;
    if (!isDuePayment && !selectedPaymentMethod) {
      setPaymentValidationError('Select payment method.');
      return;
    }
    setPaymentValidationError('');
    setUpdateReasonDialog({
      open: true,
      reason: '',
      error: '',
    });
  };

  const closeUpdateReasonDialog = () => {
    setUpdateReasonDialog({
      open: false,
      reason: '',
      error: '',
    });
  };

  const confirmUpdateOrder = async () => {
    if (!editingOrder?.id) {
      setToastMessage('error', 'No active order selected for update.');
      return;
    }
    const reason = String(updateReasonDialog.reason || '').trim();
    if (!reason) {
      setUpdateReasonDialog((prev) => ({ ...prev, error: 'Please provide update reason.' }));
      return;
    }
    const didUpdate = await handleSubmitOrder('update', { updateReason: reason });
    if (didUpdate) {
      closeUpdateReasonDialog();
    }
  };

  const handleSubmitOrder = async (actionType = 'payment', options = {}) => {
    const validItems = orderForm.order_items
      .filter((row) => row.menu_item && Number(row.quantity) > 0)
      .map((row) => {
        const variationId = normalizeVariationId(row?.variation_id);
        return {
          menu_item: Number(row.menu_item),
          quantity: Number(row.quantity),
          variation_id: variationId,
          special_note: String(row.note || '').trim(),
        };
      });

    if (!selection) {
      setToastMessage('error', 'Choose room or table to place order.');
      return false;
    }
    const requiresSilentPrint = actionType === 'payment_print'
      || actionType === 'save_print'
      || actionType === 'kot_print';
    if (requiresSilentPrint && !ensureDesktopPrinterReady()) {
      return false;
    }
    if (validItems.length === 0 && actionType !== 'cancel') {
      setToastMessage('error', 'Add at least one menu item.');
      return false;
    }

    const selectedPreparationTime = Math.max(
      10,
      Number(options.preparationTimeMinutes || orderMeta.preparation_time_minutes || 30)
    );
    const isKotAction = actionType === 'kot' || actionType === 'kot_print';
    const existingQueueTimers = normalizeQueueTimers(editingOrder?.queue_timers);
    const nextQueueTimers = existingQueueTimers.map((timer) => ({ ...timer }));
    const activeQueueTimerIndex = nextQueueTimers.findIndex((timer) => !timer.ended_at);
    const nowIso = new Date().toISOString();
    let finalPreparationTime = Math.max(10, Number(orderMeta.preparation_time_minutes || selectedPreparationTime || 30));
    const orderType = getOrderTypeFromServiceMode(orderMeta.service_mode);
    const isDuePayment = orderMeta.payment_collection_mode === 'due';
    const isPartialPayment = orderMeta.payment_collection_mode === 'partial';
    const paymentActionTypes = new Set(['payment', 'payment_print', 'payment_ebill', 'save', 'save_print', 'save_ebill']);
    const isCancelAction = actionType === 'cancel';
    const shouldCloseOrder = paymentActionTypes.has(actionType) || isCancelAction;
    const hasMergeSource = Boolean(pendingMergeSource?.id);
    const isKotCreateOperation = isKotAction && activeQueueTimerIndex === -1;
    const isKotUpdateOperation = isKotAction && activeQueueTimerIndex > -1;
    if (isKotAction) {
      if (activeQueueTimerIndex > -1) {
        const incrementedTarget = Math.min(
          240,
          Number(nextQueueTimers[activeQueueTimerIndex].target_minutes || 30) + selectedPreparationTime
        );
        nextQueueTimers[activeQueueTimerIndex].target_minutes = incrementedTarget;
        finalPreparationTime = incrementedTarget;
      } else {
        nextQueueTimers.push({
          timer_id: `timer-${nextQueueTimers.length + 1}`,
          started_at: nowIso,
          ended_at: null,
          target_minutes: selectedPreparationTime,
        });
        finalPreparationTime = selectedPreparationTime;
      }
    } else if (shouldCloseOrder && activeQueueTimerIndex > -1) {
      nextQueueTimers[activeQueueTimerIndex].ended_at = nowIso;
    }
    const selectedPaymentMethod = resolvedPaymentMethod;
    const normalizedPartialPaidAmount = Number(partialPaidNowAmount || 0);
    const normalizedPartialDueAmount = Number(partialDueNowAmount || 0);
    const hasPartialPaymentInput = isPartialPayment
      && normalizedPartialPaidAmount > 0
      && normalizedPartialDueAmount > 0;
    const shouldKeepMergeOrderUnpaid = hasMergeSource && !shouldCloseOrder;
    const effectivePaymentCollectionMode = shouldKeepMergeOrderUnpaid
      ? 'collect_now'
      : ((isDuePayment || isPartialPayment) ? 'due' : 'collect_now');
    const effectivePaymentMethod = shouldKeepMergeOrderUnpaid
      ? ''
      : (isDuePayment ? '' : selectedPaymentMethod);
    const isEbillAction = actionType === 'payment_ebill' || actionType === 'save_ebill';
    const recipientEmail = String(options.recipientEmail || '').trim().toLowerCase();
    if (shouldCloseOrder && !isCancelAction && (effectivePaymentCollectionMode !== 'due' || isPartialPayment) && !effectivePaymentMethod) {
      setPaymentValidationError('Select payment method.');
      return false;
    }
    if (!isCancelAction && isPartialPayment) {
      if (!Number.isFinite(Number(orderMeta.partial_paid_amount || 0)) || Number(orderMeta.partial_paid_amount || 0) <= 0) {
        setPaymentValidationError('Enter partial paid amount.');
        return false;
      }
      if (Number(orderMeta.partial_paid_amount || 0) >= (partialPaymentBaseAmount - 0.005)) {
        setPaymentValidationError('For full payment choose Collect Now.');
        return false;
      }
      if (normalizedPartialDueAmount <= 0) {
        setPaymentValidationError('Remaining due amount should be greater than zero.');
        return false;
      }
    }
    if (isEbillAction && !EMAIL_REGEX.test(recipientEmail)) {
      setToastMessage('error', 'Enter a valid email address to send the e-bill.');
      return false;
    }
    setPaymentValidationError('');
    const cancelReason = String(options.cancelReason || '').trim();
    const updateReason = String(options.updateReason || '').trim();
    if (actionType === 'update' && !updateReason) {
      setToastMessage('error', 'Please provide update reason.');
      return false;
    }
    const finalNote = isCancelAction
      ? [String(orderForm.notes || '').trim(), cancelReason ? `Cancellation Reason: ${cancelReason}` : '']
        .filter(Boolean)
        .join('\n')
      : String(orderForm.notes || '').trim();
    const isEditingOrder = Boolean(editingOrder?.id);
    const normalizedDiscountValue = Number(
      clampDiscountValue(
        billingAdjustments.discount_type,
        billingAdjustments.discount_value,
        estimatedSubtotal
      )
    );
    const sourceOrderForUpdate = isEditingOrder
      ? getOrderById(editingOrder?.id)
      : null;
    const previousTotalForSettlement = Number(sourceOrderForUpdate?.total_amount || 0);
    const previousDueForSettlement = Number(sourceOrderForUpdate?.due_amount || 0);
    const revisedTotalForSettlement = Number(estimatedTotal || 0);
    const settlementDeltaForAudit = Number((revisedTotalForSettlement - previousTotalForSettlement).toFixed(2));
    const settlementAmountForAudit = Math.abs(settlementDeltaForAudit);
    const settlementDirectionForAudit = settlementAmountForAudit < 0.005
      ? 'no_change'
      : (settlementDeltaForAudit > 0 ? 'collect' : 'return');
    const isRemainingDueUpdate = actionType === 'update'
      && isEditingOrder
      && effectivePaymentCollectionMode === 'due'
      && !isPartialPayment
      && settlementDirectionForAudit === 'collect'
      && settlementAmountForAudit >= 0.005;
    const isEditDueModeUpdate = actionType === 'update'
      && isEmbeddedEditMode
      && !isPartialPayment
      && effectivePaymentCollectionMode === 'due';
    const projectedDueForUpdate = isEditDueModeUpdate
      ? Number(Math.max(0, previousDueForSettlement + settlementDeltaForAudit).toFixed(2))
      : 0;
    const shouldApplyPaymentTracking = shouldCloseOrder || actionType === 'update';
    const existingDueAmountForTracking = Number(sourceOrderForUpdate?.due_amount || 0);
    const existingPaymentStatusForTracking = String(sourceOrderForUpdate?.payment_status || '').toLowerCase();
    const existingPartialPaidForTracking = Number(sourceOrderForUpdate?.partial_paid_amount || 0);
    const dueAmountForAudit = (() => {
      if (!shouldApplyPaymentTracking && isEditingOrder) {
        return Number(existingDueAmountForTracking.toFixed(2));
      }
      if (hasPartialPaymentInput) return Number(normalizedPartialDueAmount.toFixed(2));
      if (isEditDueModeUpdate) {
        return Number(Math.min(projectedDueForUpdate, revisedTotalForSettlement).toFixed(2));
      }
      if (actionType === 'update' && effectivePaymentCollectionMode === 'due') {
        return Number((isRemainingDueUpdate ? settlementAmountForAudit : revisedTotalForSettlement).toFixed(2));
      }
      if (effectivePaymentCollectionMode === 'due' && shouldCloseOrder) {
        return Number(revisedTotalForSettlement.toFixed(2));
      }
      return 0;
    })();
    const paidAmountForAudit = (() => {
      if (!shouldApplyPaymentTracking && isEditingOrder) {
        if (existingPaymentStatusForTracking === 'partial') {
          return Number(existingPartialPaidForTracking.toFixed(2));
        }
        if (existingPaymentStatusForTracking === 'paid') {
          return Number(revisedTotalForSettlement.toFixed(2));
        }
        return Number(Math.max(0, revisedTotalForSettlement - existingDueAmountForTracking).toFixed(2));
      }
      if (hasPartialPaymentInput) return Number(normalizedPartialPaidAmount.toFixed(2));
      if (effectivePaymentCollectionMode === 'collect_now' && shouldCloseOrder) {
        return Number(revisedTotalForSettlement.toFixed(2));
      }
      if (actionType === 'update') {
        return Number((revisedTotalForSettlement - dueAmountForAudit).toFixed(2));
      }
      return 0;
    })();
    const paymentStatusForAudit = hasPartialPaymentInput
      ? 'partial'
      : (!shouldApplyPaymentTracking && isEditingOrder
        ? (existingPaymentStatusForTracking || (existingDueAmountForTracking > 0 ? 'due' : 'paid'))
        : (effectivePaymentCollectionMode === 'due'
          ? (dueAmountForAudit <= 0.005
            ? 'paid'
            : (dueAmountForAudit >= (revisedTotalForSettlement - 0.005) ? 'due' : 'partial'))
          : 'paid'));
    const shouldRecordCollectNowOnUpdate = actionType === 'update'
      && isEmbeddedEditMode
      && effectivePaymentCollectionMode === 'collect_now'
      && Boolean(effectivePaymentMethod);
    const paymentEntryAmountForPayload = (() => {
      if (isCancelAction) return 0;
      if (hasPartialPaymentInput) return Number(normalizedPartialPaidAmount.toFixed(2));
      if ((shouldCloseOrder || shouldRecordCollectNowOnUpdate) && effectivePaymentCollectionMode === 'collect_now' && effectivePaymentMethod) {
        if (isEditingOrder) {
          return Number(Math.max(0, previousDueForSettlement + settlementDeltaForAudit).toFixed(2));
        }
        return Number(revisedTotalForSettlement.toFixed(2));
      }
      return 0;
    })();
    const partialPaidAmountForPayload = paymentStatusForAudit === 'partial'
      ? Number(paidAmountForAudit.toFixed(2))
      : 0;
    const normalizedCurrentItemsForAudit = validItems
      .map((row) => ({
        menu_item: Number(row.menu_item || 0),
        variation_id: normalizeVariationId(row.variation_id),
        quantity: Number(row.quantity || 0),
        special_note: String(row.special_note || '').trim(),
      }))
      .sort((first, second) => (
        Number(first.menu_item || 0) - Number(second.menu_item || 0)
      ) || (
        Number(first.variation_id || 0) - Number(second.variation_id || 0)
      ) || String(first.special_note || '').localeCompare(String(second.special_note || '')));
    const normalizedSourceItemsForAudit = normalizeDraftOrderItems(sourceOrderForUpdate?.items || [])
      .map((row) => ({
        menu_item: Number(row.menu_item || 0),
        variation_id: normalizeVariationId(row.variation_id),
        quantity: Number(row.quantity || 0),
        special_note: String(row.note || row.special_note || '').trim(),
      }))
      .sort((first, second) => (
        Number(first.menu_item || 0) - Number(second.menu_item || 0)
      ) || (
        Number(first.variation_id || 0) - Number(second.variation_id || 0)
      ) || String(first.special_note || '').localeCompare(String(second.special_note || '')));
    const sourceTaxRowsRaw = (() => {
      if (Array.isArray(sourceOrderForUpdate?.tax_breakdown)) {
        return sourceOrderForUpdate.tax_breakdown;
      }
      if (typeof sourceOrderForUpdate?.tax_breakdown === 'string') {
        try {
          const parsedRows = JSON.parse(sourceOrderForUpdate.tax_breakdown);
          return Array.isArray(parsedRows) ? parsedRows : [];
        } catch (_error) {
          return [];
        }
      }
      return [];
    })();
    const normalizeTaxRowsForAudit = (rows = []) => (
      (Array.isArray(rows) ? rows : [])
        .filter((row) => row && typeof row === 'object' && row.enabled !== false)
        .map((row, index) => ({
          tax_key: row?.tax_key || `tax-${index}`,
          tax_rule_id: Number(row?.tax_rule_id || 0) || null,
          mode: row?.mode === 'amount' ? 'amount' : 'percentage',
          value: Number(row?.numericValue ?? row?.value ?? 0),
          amount: Number(row?.amount || 0),
        }))
        .sort((first, second) => String(first.tax_key).localeCompare(String(second.tax_key)))
    );
    const normalizedSourceTaxesForAudit = normalizeTaxRowsForAudit(sourceTaxRowsRaw);
    const normalizedCurrentTaxesForAudit = normalizeTaxRowsForAudit(configuredTaxBreakdown);
    const updatedFields = [];
    if (actionType === 'update' && isEditingOrder) {
      if (String(sourceOrderForUpdate?.order_type || '') !== String(orderType || '')) updatedFields.push('order_type');
      if (String(sourceOrderForUpdate?.customer_name || '') !== String(orderMeta.customer_name || '').trim()) updatedFields.push('customer_name');
      if (String(sourceOrderForUpdate?.customer_phone_country_code || '') !== String(orderMeta.phone_country_code || '').trim()) {
        updatedFields.push('customer_phone_country_code');
      }
      if (String(sourceOrderForUpdate?.customer_phone || '') !== String(orderMeta.mobile || '').trim()) updatedFields.push('customer_phone');
      if (String(sourceOrderForUpdate?.payment_collection_mode || 'collect_now') !== String(effectivePaymentCollectionMode || 'collect_now')) {
        updatedFields.push('payment_collection_mode');
      }
      if (String(sourceOrderForUpdate?.payment_method || '') !== String(effectivePaymentMethod || '')) updatedFields.push('payment_method');
      if (Number(sourceOrderForUpdate?.preparation_time_minutes || 0) !== Number(finalPreparationTime || 0)) {
        updatedFields.push('preparation_time_minutes');
      }
      if (String(sourceOrderForUpdate?.discount_type || 'percentage') !== String(billingAdjustments.discount_type || 'percentage')) {
        updatedFields.push('discount_type');
      }
      if (Number(sourceOrderForUpdate?.discount_value || 0) !== Number(normalizedDiscountValue || 0)) {
        updatedFields.push('discount_value');
      }
      if (Number(sourceOrderForUpdate?.delivery_charge || 0) !== Number(deliveryChargeAmount || 0)) {
        updatedFields.push('delivery_charge');
      }
      if (Number(sourceOrderForUpdate?.container_charge || 0) !== Number(containerChargeAmount || 0)) {
        updatedFields.push('container_charge');
      }
      if (JSON.stringify(normalizedSourceTaxesForAudit) !== JSON.stringify(normalizedCurrentTaxesForAudit)) {
        updatedFields.push('tax_breakdown');
      }
      if (JSON.stringify(normalizedSourceItemsForAudit) !== JSON.stringify(normalizedCurrentItemsForAudit)) {
        updatedFields.push('order_items');
      }
      if (updatedFields.length === 0) {
        updatedFields.push('no_field_change');
      }
    }
    const currentTargetLabel = getOrderTargetLabel(selection?.targetType, selection?.target);
    const auditAction = (() => {
      if (isCancelAction) return 'order_cancelled';
      if (isKotAction) return isKotCreateOperation ? 'kot_created' : 'kot_updated';
      if (actionType === 'payment' || actionType === 'payment_print' || actionType === 'payment_ebill') {
        return 'payment_captured';
      }
      if (actionType === 'save' || actionType === 'save_print' || actionType === 'save_ebill') {
        return 'order_completed';
      }
      return isEditingOrder ? 'order_updated' : 'order_created';
    })();
    const auditLabel = (() => {
      if (auditAction === 'order_cancelled') return 'Order Cancelled';
      if (auditAction === 'kot_created') return 'KOT Created';
      if (auditAction === 'kot_updated') return 'KOT Updated';
      if (auditAction === 'payment_captured') return 'Payment Captured';
      if (auditAction === 'order_completed') return 'Order Completed';
      if (auditAction === 'order_created') return 'Order Created';
      return 'Order Updated';
    })();
    const auditMessage = (() => {
      if (auditAction === 'order_cancelled') {
        return cancelReason ? `Order cancelled. Reason: ${cancelReason}` : 'Order cancelled.';
      }
      if (auditAction === 'kot_created') {
        return `KOT created with SLA ${finalPreparationTime} minutes for ${currentTargetLabel}.`;
      }
      if (auditAction === 'kot_updated') {
        return `KOT updated with SLA ${finalPreparationTime} minutes for ${currentTargetLabel}.`;
      }
      if (auditAction === 'payment_captured') {
        if (hasPartialPaymentInput) {
          return `Partial payment captured for ${currentTargetLabel} via ${selectedPaymentMethod}. Paid ${CURRENCY} ${normalizedPartialPaidAmount.toFixed(2)}, due ${CURRENCY} ${normalizedPartialDueAmount.toFixed(2)}.`;
        }
        const methodLabel = selectedPaymentMethod || 'Due';
        return `Payment captured for ${currentTargetLabel} via ${methodLabel}.`;
      }
      if (auditAction === 'order_completed') {
        return `Order completed for ${currentTargetLabel}.`;
      }
      if (auditAction === 'order_created') {
        return `Order created for ${currentTargetLabel}.`;
      }
      if (actionType === 'update') {
        const settlementMessage = hasPartialPaymentInput
          ? ` Partial payment recorded. Paid ${CURRENCY} ${normalizedPartialPaidAmount.toFixed(2)}, due ${CURRENCY} ${normalizedPartialDueAmount.toFixed(2)}.`
          : (isRemainingDueUpdate
            ? ` Remaining due ${CURRENCY} ${settlementAmountForAudit.toFixed(2)}. Payment status set to Partial.`
            : (settlementDirectionForAudit === 'collect'
              ? ` Collect remaining ${CURRENCY} ${settlementAmountForAudit.toFixed(2)}.`
              : (settlementDirectionForAudit === 'return'
                ? ` Return ${CURRENCY} ${settlementAmountForAudit.toFixed(2)}.`
                : ' No payment adjustment required.')));
        return `Order updated for ${currentTargetLabel}. Reason: ${updateReason}.${settlementMessage}`;
      }
      return `Order updated for ${currentTargetLabel}.`;
    })();
    const formData = new FormData();
    const shouldKeepCurrentStatus = actionType === 'update' && isEditingOrder;
    const existingOrderStatus = String(
      sourceOrderForUpdate?.status
      || editingOrder?.status
      || 'placed'
    );
    const resolvedStatus = isCancelAction
      ? 'cancelled'
      : (isKotAction ? 'preparing' : (shouldCloseOrder ? 'completed' : (shouldKeepCurrentStatus ? existingOrderStatus : 'placed')));
    formData.append('order_type', orderType);
    formData.append('status', resolvedStatus);
    formData.append('customer_name', String(orderMeta.customer_name || '').trim());
    formData.append('customer_phone_country_code', String(orderMeta.phone_country_code || '').trim());
    formData.append('customer_phone', String(orderMeta.mobile || '').trim());
    formData.append('payment_collection_mode', effectivePaymentCollectionMode);
    formData.append('payment_method', effectivePaymentMethod);
    formData.append('payment_status', paymentStatusForAudit);
    formData.append('due_amount', String(Number(dueAmountForAudit || 0).toFixed(2)));
    formData.append('partial_paid_amount', String(Number(partialPaidAmountForPayload || 0).toFixed(2)));
    formData.append('payment_entry_amount', String(Number(paymentEntryAmountForPayload || 0).toFixed(2)));
    formData.append('payment_entry_method', effectivePaymentMethod);
    formData.append('preparation_time_minutes', String(finalPreparationTime));
    formData.append('queue_timers_json', JSON.stringify(nextQueueTimers));
    formData.append('discount_type', billingAdjustments.discount_type === 'amount' ? 'amount' : 'percentage');
    formData.append('discount_value', String(normalizedDiscountValue));
    formData.append('delivery_charge', String(deliveryChargeAmount));
    formData.append('container_charge', String(containerChargeAmount));
    formData.append(
      'tax_breakdown_json',
      JSON.stringify(configuredTaxBreakdown.map((row, index) => ({
        tax_key: row?.tax_key || `tax-${index}`,
        tax_rule_id: Number(row?.tax_rule_id || 0) || null,
        name: row?.name || 'Tax',
        tax_type_label: row?.tax_type_label || row?.name || 'Tax',
        mode: row?.mode === 'amount' ? 'amount' : 'percentage',
        value: Number(row?.numericValue ?? row?.value ?? 0),
        numericValue: Number(row?.numericValue ?? row?.value ?? 0),
        amount: Number(row?.amount || 0),
        enabled: row?.enabled !== false,
      })))
    );
    formData.append('notes', finalNote);
    formData.append('order_items_json', JSON.stringify(validItems));
    formData.append('audit_context_json', JSON.stringify({
      action: auditAction,
      label: auditLabel,
      message: auditMessage,
      details: {
        action_type: actionType,
        target_type: selection?.targetType || '',
        target_label: currentTargetLabel,
        preparation_time_minutes: finalPreparationTime,
        kot_operation: isKotAction ? (isKotCreateOperation ? 'created' : 'updated') : '',
        payment_collection_mode: effectivePaymentCollectionMode,
        payment_method: effectivePaymentMethod,
        is_editing_order: isEditingOrder,
        update_reason: actionType === 'update' ? updateReason : '',
        updated_fields: actionType === 'update' ? updatedFields : [],
        settlement_direction: actionType === 'update' ? settlementDirectionForAudit : '',
        settlement_amount: actionType === 'update' ? settlementAmountForAudit : 0,
        due_amount: dueAmountForAudit,
        paid_amount: paidAmountForAudit,
        payment_status: paymentStatusForAudit,
        partial_paid_amount: hasPartialPaymentInput ? normalizedPartialPaidAmount : 0,
        partial_due_amount: hasPartialPaymentInput ? normalizedPartialDueAmount : 0,
        payment_entry_amount: paymentEntryAmountForPayload,
        previous_total_amount: actionType === 'update' ? previousTotalForSettlement : 0,
        previous_due_amount: actionType === 'update' ? previousDueForSettlement : 0,
        revised_total_amount: actionType === 'update' ? revisedTotalForSettlement : 0,
      },
    }));
    if (selection.targetType === 'room') {
      formData.append('room', String(selection.target.id));
      formData.append('table', '');
    } else {
      formData.append('table', String(selection.target.id));
      formData.append('room', '');
    }

    setSubmitting(true);
    const mergeSourceSnapshot = pendingMergeSource;
    try {
      let savedOrder = null;
      if (isEditingOrder) {
        savedOrder = await updatePosOrder(editingOrder.id, formData);
      } else {
        savedOrder = await createPosOrder(formData);
      }
      const resolvedOrderId = Number(savedOrder?.id || editingOrder?.id || 0);
      if (isKotAction && resolvedOrderId) {
        setIncomingOrderDialog((prev) => {
          const remainingQueue = (Array.isArray(prev.queue) ? prev.queue : [])
            .filter((row) => Number(row?.id || 0) !== resolvedOrderId);
          const nextActive = prev?.activeOrder && Number(prev.activeOrder.id || 0) === resolvedOrderId
            ? (remainingQueue[0] || null)
            : (prev?.activeOrder || remainingQueue[0] || null);
          return {
            ...prev,
            open: Boolean(nextActive) && prev.open,
            queue: remainingQueue,
            activeOrder: nextActive,
            processing: false,
          };
        });
      }
      const currentEditingOrderId = Number(editingOrder?.id || 0);
      let mergeInfoSuffix = '';
      let mergeWarningSuffix = '';
      let mergeSourceRemoved = false;
      let mergeSourceCompleted = false;
      if (mergeSourceSnapshot?.id && Number(mergeSourceSnapshot.id) !== currentEditingOrderId) {
        const nowIsoTimestamp = new Date().toISOString();
        const completedSourceTimers = normalizeQueueTimers(mergeSourceSnapshot.queue_timers)
          .map((timer) => (timer.ended_at ? timer : { ...timer, ended_at: nowIsoTimestamp }));
        const sourceOrderRef = formatOrderNumber(mergeSourceSnapshot.order_number);
        try {
          const snapshotPaymentMethod = String(mergeSourceSnapshot?.payment_method || '').trim();
          const snapshotPaymentCollectionMode = snapshotPaymentMethod
            ? (mergeSourceSnapshot?.payment_collection_mode === 'due' ? 'due' : 'collect_now')
            : 'due';
          await updatePosOrder(mergeSourceSnapshot.id, {
            status: 'completed',
            queue_timers: completedSourceTimers,
            payment_collection_mode: snapshotPaymentCollectionMode,
            payment_method: snapshotPaymentCollectionMode === 'due' ? '' : snapshotPaymentMethod,
            audit_context: {
              action: 'merge_source_closed',
              label: 'Merged Source Closed',
              message: `Source order ${sourceOrderRef} auto-closed after merge.`,
              details: {
                merged_into_order_id: Number(savedOrder?.id || editingOrder?.id || 0) || null,
                merged_into_order_number: formatOrderNumber(savedOrder?.order_number || editingOrder?.order_number),
                source_order_id: Number(mergeSourceSnapshot?.id || 0) || null,
                source_order_number: sourceOrderRef,
              },
            },
          });
          mergeSourceCompleted = true;
          try {
            await deletePosOrder(mergeSourceSnapshot.id);
            mergeSourceRemoved = true;
            mergeInfoSuffix = ` Merged from ${sourceOrderRef}.`;
          } catch (_deleteMergeSourceError) {
            mergeInfoSuffix = ` Merged from ${sourceOrderRef}.`;
            mergeWarningSuffix = ` Could not remove merged source ${sourceOrderRef}; it has been kept closed.`;
          }
        } catch (_mergeError) {
          mergeWarningSuffix = ` Could not safely close merged source ${sourceOrderRef}; it was not removed.`;
        }
      }

      let ebillWarningSuffix = '';
      if (isEbillAction) {
        if (!resolvedOrderId) {
          throw new Error('Unable to send e-bill: order could not be resolved.');
        }
        try {
          await sendPosOrderEbill(resolvedOrderId, { email: recipientEmail });
        } catch (ebillError) {
          ebillWarningSuffix = ` ${ebillError?.userMessage || ebillError?.message || 'Payment captured but e-bill could not be sent.'}`;
        }
      }
      const isPrintAction = actionType === 'payment_print' || actionType === 'save_print';
      let printWarningSuffix = '';
      if (isPrintAction) {
        if (!resolvedOrderId) {
          throw new Error('Unable to print invoice: order could not be resolved.');
        }
        try {
          const printableOrder = savedOrder?.id ? savedOrder : await getPosOrderDetail(resolvedOrderId);
          const printResult = await triggerRestaurantThermalPrint(printableOrder, 'bill');
          printWarningSuffix = getRestaurantPrintWarningSuffix(printResult, {
            printerNotValidated: ' Payment saved. Validate the restaurant printer in the desktop app to print silently.',
            desktopApiMissing: ' Payment saved. Desktop print failed to load — reinstall the latest RoomNexa desktop app.',
            printFailed: ' Payment saved but print failed: {error}.',
            blocked: ' Payment saved but print was blocked. Please allow popups and try again.',
          });
        } catch (printError) {
          printWarningSuffix = ` ${printError?.userMessage || printError?.message || 'Payment saved but invoice print failed.'}`;
        }
      }

      if (actionType === 'kot_print') {
        const printableOrder = savedOrder?.id ? savedOrder : (resolvedOrderId ? await getPosOrderDetail(resolvedOrderId) : null);
        const printResult = printableOrder ? await triggerRestaurantThermalPrint(printableOrder, 'kot') : { opened: false, reason: 'blocked' };
        printWarningSuffix = getRestaurantPrintWarningSuffix(printResult, {
          noChanges: ' KOT saved but no new item quantity was found to print.',
          printerNotValidated: ' KOT saved. Validate the restaurant printer in the desktop app to print silently.',
          desktopApiMissing: ' KOT saved. Desktop print failed to load — reinstall the latest RoomNexa desktop app.',
          printFailed: ' KOT saved but print failed: {error}.',
          blocked: ' KOT saved but print was blocked. Please allow popups and try again.',
        });
      }

      let successMessage = '';
      if (actionType === 'kot_print') {
        successMessage = printWarningSuffix
          ? (isKotUpdateOperation ? `KOT updated. SLA ${finalPreparationTime} min.` : `KOT created. SLA ${finalPreparationTime} min.`)
          : (isKotUpdateOperation
            ? `KOT updated and sent to printer. SLA ${finalPreparationTime} min.`
            : `KOT created and sent to printer. SLA ${finalPreparationTime} min.`);
      } else if (actionType === 'kot') {
        successMessage = isKotUpdateOperation
          ? `KOT updated successfully. SLA ${finalPreparationTime} min.`
          : `KOT created successfully. SLA ${finalPreparationTime} min.`;
      } else if (isCancelAction) {
        successMessage = isEditingOrder
          ? 'Order cancelled successfully.'
          : 'Order cancelled successfully.';
      } else if (actionType === 'payment_print' || actionType === 'save_print') {
        successMessage = printWarningSuffix
          ? 'Payment captured.'
          : 'Payment captured and invoice sent to printer.';
      } else if (actionType === 'payment_ebill' || actionType === 'save_ebill') {
        successMessage = ebillWarningSuffix
          ? 'Payment captured.'
          : 'Payment captured and restaurant e-bill sent successfully.';
      } else if (actionType === 'payment' || actionType === 'save') {
        successMessage = 'Payment captured and order closed successfully.';
      } else {
        successMessage = actionType === 'update'
          ? 'Order updated successfully.'
          : (isEditingOrder ? 'Order updated and closed successfully.' : 'Order saved and closed successfully.');
      }
      const hasWarning = Boolean(mergeWarningSuffix || ebillWarningSuffix || printWarningSuffix);
      setToastMessage(
        hasWarning ? 'warning' : 'success',
        `${successMessage}${mergeInfoSuffix}${mergeWarningSuffix}${ebillWarningSuffix}${printWarningSuffix}`
      );
      if (isKotAction) {
        let orderForSync = savedOrder;
        if (resolvedOrderId && (!Array.isArray(savedOrder?.items) || savedOrder.items.length === 0)) {
          try {
            orderForSync = await getPosOrderDetail(resolvedOrderId);
          } catch (_detailError) {
            orderForSync = savedOrder;
          }
        }
        const preparationMinutes = Number(orderForSync?.preparation_time_minutes || finalPreparationTime || 30);
        const normalizedTimers = normalizeQueueTimers(orderForSync?.queue_timers);
        const mergeSourceId = Number(mergeSourceSnapshot?.id || 0);
        const enrichedOrder = {
          ...orderForSync,
          id: resolvedOrderId,
          status: orderForSync?.status || 'preparing',
          preparation_time_minutes: preparationMinutes,
          queue_timers: normalizedTimers,
          updated_at: orderForSync?.updated_at || new Date().toISOString(),
          created_at: orderForSync?.created_at || new Date().toISOString(),
          room: Number(orderForSync?.room || 0)
            || (selection?.targetType === 'room' ? Number(selection.target?.id || 0) : 0)
            || null,
          table: Number(orderForSync?.table || 0)
            || (selection?.targetType === 'table' ? Number(selection.target?.id || 0) : 0)
            || null,
        };
        setOrders((prev) => {
          let rows = Array.isArray(prev) ? [...prev] : [];
          if (mergeSourceId && mergeSourceId !== resolvedOrderId) {
            if (mergeSourceRemoved) {
              rows = rows.filter((row) => Number(row?.id || 0) !== mergeSourceId);
            } else if (mergeSourceCompleted) {
              const completedAt = new Date().toISOString();
              rows = rows.map((row) => (
                Number(row?.id || 0) === mergeSourceId
                  ? {
                    ...row,
                    status: 'completed',
                    updated_at: completedAt,
                    queue_timers: normalizeQueueTimers(mergeSourceSnapshot?.queue_timers)
                      .map((timer) => (timer.ended_at ? timer : { ...timer, ended_at: completedAt })),
                  }
                  : row
              ));
            }
          }
          const existingIndex = rows.findIndex((row) => Number(row?.id || 0) === resolvedOrderId);
          if (existingIndex > -1) {
            const nextRows = [...rows];
            nextRows[existingIndex] = { ...nextRows[existingIndex], ...enrichedOrder };
            return nextRows;
          }
          return [...rows, enrichedOrder];
        });
        setEditingOrder({
          id: resolvedOrderId,
          order_number: enrichedOrder?.order_number || editingOrder?.order_number || '',
          status: enrichedOrder?.status || 'preparing',
          queue_timers: normalizedTimers,
          preparation_time_minutes: preparationMinutes,
        });
        if (Array.isArray(enrichedOrder?.items) && enrichedOrder.items.length > 0) {
          setOrderForm((prev) => ({
            notes: getEditableOrderNote(enrichedOrder?.notes || prev.notes),
            order_items: normalizeDraftOrderItems(enrichedOrder.items),
          }));
        }
        setOrderMeta((prev) => ({
          ...prev,
          preparation_time_minutes: preparationMinutes,
        }));
        setPendingMergeSource(null);
        setKotConfirmDialog((prev) => ({ ...prev, open: false }));
      } else {
        closeDrawer({ notifyParent: false });
        await loadData();
        if (embedded && typeof onEmbeddedSaved === 'function') {
          onEmbeddedSaved();
        }
        if (embedded && typeof onEmbeddedClose === 'function') {
          onEmbeddedClose();
        }
      }
      return true;
    } catch (err) {
      setToastMessage('error', err?.userMessage || err?.message || 'Failed to place order.');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleServeQueueOrder = async (order) => {
    const orderId = Number(order?.id || 0);
    if (!orderId || order?.status === 'served') return;
    setServingOrderIds((prev) => ({ ...prev, [orderId]: true }));
    const existingTimers = normalizeQueueTimers(order?.queue_timers);
    const nextTimers = existingTimers.map((timer) => ({ ...timer }));
    const activeQueueTimerIndex = nextTimers.findIndex((timer) => !timer.ended_at);
    if (activeQueueTimerIndex > -1) {
      nextTimers[activeQueueTimerIndex].ended_at = new Date().toISOString();
    }
    try {
      await updatePosOrder(orderId, {
        status: 'served',
        queue_timers: nextTimers,
        audit_context: {
          action: 'order_served',
          label: 'Order Served',
          message: `Order ${formatOrderNumber(order?.order_number)} marked as served.`,
          details: {
            order_id: orderId,
            order_number: formatOrderNumber(order?.order_number),
          },
        },
      });
      setOrders((prev) => prev.map((row) => (
        Number(row.id) === orderId
          ? {
            ...row,
            status: 'served',
            updated_at: new Date().toISOString(),
            queue_timers: nextTimers,
          }
          : row
      )));
      setToastMessage('success', 'Order marked as served. Timer stopped.');
    } catch (err) {
      setToastMessage('error', err?.userMessage || 'Failed to mark order as served.');
    } finally {
      setServingOrderIds((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const closeIncomingOrderDialog = useCallback(() => {
    setIncomingOrderDialog((prev) => ({ ...prev, open: false }));
  }, []);

  const buildIncomingOrderDecisionPayload = useCallback((order, nextStatus, nextPreparationMinutes) => {
    const normalizedPreparationMinutes = snapToIncomingPrepOption(nextPreparationMinutes || order?.preparation_time_minutes || 30);
    const sourceItems = Array.isArray(order?.items) ? order.items : [];
    const orderItems = sourceItems
      .filter((item) => Number(item?.menu_item || 0) > 0 && Number(item?.quantity || 0) > 0)
      .map((item) => ({
        menu_item: Number(item.menu_item),
        variation_id: normalizeVariationId(item?.variation_id),
        quantity: Math.max(1, Number(item.quantity || 1)),
        special_note: String(item?.special_note || '').trim(),
      }));
    const existingQueueTimers = normalizeQueueTimers(order?.queue_timers);
    const nowIso = new Date().toISOString();
    const nextQueueTimers = nextStatus === 'preparing'
      ? [
        ...existingQueueTimers.map((timer) => (timer?.ended_at ? timer : { ...timer, ended_at: nowIso })),
        {
          timer_id: `timer-${existingQueueTimers.length + 1}`,
          started_at: nowIso,
          ended_at: null,
          target_minutes: normalizedPreparationMinutes,
        },
      ]
      : existingQueueTimers.map((timer) => (timer?.ended_at ? timer : { ...timer, ended_at: nowIso }));

    return {
      status: nextStatus,
      preparation_time_minutes: normalizedPreparationMinutes,
      queue_timers: nextQueueTimers,
      order_items: orderItems,
      audit_context: {
        action: nextStatus === 'preparing' ? 'kot_created' : 'order_cancelled',
        label: nextStatus === 'preparing' ? 'Order Accepted' : 'Order Declined',
        message: nextStatus === 'preparing'
          ? `Order ${formatOrderNumber(order?.order_number)} accepted in restaurant queue.`
          : `Order ${formatOrderNumber(order?.order_number)} declined in restaurant queue.`,
        details: {
          source: 'restaurant_live_popup',
          order_id: Number(order?.id || 0) || null,
          order_number: formatOrderNumber(order?.order_number),
          preparation_time_minutes: normalizedPreparationMinutes,
        },
      },
    };
  }, []);

  const handleAcceptPendingOrder = useCallback(async (
    order,
    {
      preparationMinutes = null,
      fromPopup = false,
    } = {}
  ) => {
    const orderId = Number(order?.id || 0);
    if (!orderId) return false;
    const resolvedPreparationMinutes = snapToIncomingPrepOption(
      preparationMinutes || order?.preparation_time_minutes || incomingOrderPrepMinutes || 30
    );
    const payload = buildIncomingOrderDecisionPayload(order, 'preparing', resolvedPreparationMinutes);
    if (!fromPopup) {
      setPendingAcceptingOrderId(orderId);
    }
    try {
      const runningSessionOrder = findRunningMergeTargetOrder(order);
      if (runningSessionOrder) {
        const isRoomServiceMerge = String(order?.order_type || '').toLowerCase() === 'room_service' && Number(order?.room || 0);
        const mergeLabel = isRoomServiceMerge ? 'Room Service Order Merged' : 'QR Table Order Merged';
        const mergeSource = isRoomServiceMerge ? 'room_service_accept' : 'qr_table_session_accept';
        const targetLabel = isRoomServiceMerge
          ? `room queue #${formatOrderNumber(runningSessionOrder?.order_number)}`
          : `active table queue #${formatOrderNumber(runningSessionOrder?.order_number)}`;
        const mergedItems = mergeDraftOrderItems(
          normalizeDraftOrderItems(runningSessionOrder?.items || []),
          normalizeDraftOrderItems(order?.items || [])
        );
        const mergedQueueTimers = mergeQueueTimersForMergedOrder(
          runningSessionOrder?.queue_timers || [],
          payload.queue_timers || []
        );
        const destinationNote = getEditableOrderNote(runningSessionOrder?.notes);
        const incomingNote = getEditableOrderNote(order?.notes);
        const mergedNote = [destinationNote, incomingNote].filter(Boolean).join(destinationNote && incomingNote ? '\n' : '');

        const mergedPayload = {
          order_type: runningSessionOrder.order_type || order.order_type || 'dine_in',
          status: 'preparing',
          room: runningSessionOrder.room || null,
          table: runningSessionOrder.table || order.table || null,
          customer_name: runningSessionOrder.customer_name || order.customer_name || '',
          customer_phone_country_code: runningSessionOrder.customer_phone_country_code || order.customer_phone_country_code || '+91',
          customer_phone: runningSessionOrder.customer_phone || order.customer_phone || '',
          payment_collection_mode: runningSessionOrder.payment_collection_mode || 'due',
          payment_method: runningSessionOrder.payment_method || '',
          preparation_time_minutes: Math.max(
            resolvedPreparationMinutes,
            Number(runningSessionOrder?.preparation_time_minutes || 30)
          ),
          queue_timers: mergedQueueTimers,
          discount_type: runningSessionOrder.discount_type || 'percentage',
          discount_value: runningSessionOrder.discount_value || '0.00',
          delivery_charge: runningSessionOrder.delivery_charge || '0.00',
          container_charge: runningSessionOrder.container_charge || '0.00',
          tax_breakdown: [],
          notes: mergedNote,
          order_items: mergedItems.map((item) => ({
            menu_item: Number(item.menu_item),
            variation_id: normalizeVariationId(item.variation_id),
            quantity: Math.max(1, Number(item.quantity || 1)),
            special_note: String(item.note || item.special_note || '').trim(),
          })),
          audit_context: {
            action: 'order_merged',
            label: mergeLabel,
            message: `Order ${formatOrderNumber(order?.order_number)} merged into active order ${formatOrderNumber(runningSessionOrder?.order_number)}.`,
            details: {
              source: mergeSource,
              merge_source_order_id: orderId,
              merge_source_order_number: formatOrderNumber(order?.order_number),
              merged_into_order_id: Number(runningSessionOrder?.id || 0),
              merged_into_order_number: formatOrderNumber(runningSessionOrder?.order_number),
              qr_table_session_id: Number(order?.qr_table_session || 0) || null,
              room_id: Number(order?.room || 0) || null,
              preparation_time_minutes: resolvedPreparationMinutes,
            },
          },
        };

        const updatedOrder = await updatePosOrder(runningSessionOrder.id, mergedPayload);
        try {
          await deletePosOrder(orderId);
        } catch (_deleteMergedQrOrderError) {
          await updatePosOrder(orderId, {
            status: 'cancelled',
            queue_timers: normalizeQueueTimers(order?.queue_timers || [])
              .map((timer) => (timer.ended_at ? timer : { ...timer, ended_at: new Date().toISOString() })),
            tax_breakdown: [],
            order_items: [],
            audit_context: {
              action: 'merge_source_closed',
              label: isRoomServiceMerge ? 'Merged Room Service Source Closed' : 'Merged QR Source Closed',
              message: `Order ${formatOrderNumber(order?.order_number)} was merged into ${formatOrderNumber(runningSessionOrder?.order_number)}.`,
              details: {
                source: mergeSource,
                merged_into_order_id: Number(runningSessionOrder?.id || 0),
              },
            },
          });
        }

        setOrders((prev) => prev
          .filter((row) => Number(row?.id || 0) !== orderId)
          .map((row) => (
            Number(row?.id || 0) === Number(runningSessionOrder?.id || 0)
              ? { ...row, ...(updatedOrder || {}), status: 'preparing' }
              : row
          )));
        setToastMessage(
          'success',
          `Order #${formatOrderNumber(order?.order_number)} merged into ${targetLabel}.`
        );
        if (fromPopup) {
          setIncomingOrderDialog((prev) => {
            const remainingQueue = (Array.isArray(prev.queue) ? prev.queue : [])
              .filter((row) => Number(row?.id || 0) !== orderId);
            const nextActive = remainingQueue[0] || null;
            return {
              open: Boolean(nextActive),
              queue: remainingQueue,
              activeOrder: nextActive,
              processing: false,
            };
          });
        }
        return true;
      }

      await updatePosOrder(orderId, payload);
      setOrders((prev) => prev.map((row) => (
        Number(row?.id || 0) === orderId
          ? {
            ...row,
            status: 'preparing',
            preparation_time_minutes: resolvedPreparationMinutes,
            queue_timers: payload.queue_timers,
            updated_at: new Date().toISOString(),
          }
          : row
      )));
      setToastMessage('success', `Order #${formatOrderNumber(order?.order_number)} accepted from restaurant queue.`);
      if (fromPopup) {
        setIncomingOrderDialog((prev) => {
          const remainingQueue = (Array.isArray(prev.queue) ? prev.queue : [])
            .filter((row) => Number(row?.id || 0) !== orderId);
          const nextActive = remainingQueue[0] || null;
          return {
            open: Boolean(nextActive),
            queue: remainingQueue,
            activeOrder: nextActive,
            processing: false,
          };
        });
      }
      return true;
    } catch (err) {
      if (fromPopup) {
        setIncomingOrderDialog((prev) => ({ ...prev, processing: false }));
      }
      setToastMessage('error', err?.userMessage || 'Unable to update incoming order status.');
      return false;
    } finally {
      if (!fromPopup) {
        setPendingAcceptingOrderId(null);
      }
    }
  }, [buildIncomingOrderDecisionPayload, findRunningMergeTargetOrder, incomingOrderPrepMinutes]);

  const handleDeclinePendingOrder = useCallback(async (order) => {
    const orderId = Number(order?.id || 0);
    if (!orderId) return false;
    const resolvedPreparationMinutes = snapToIncomingPrepOption(
      order?.preparation_time_minutes || incomingOrderPrepMinutes || 30
    );
    const payload = buildIncomingOrderDecisionPayload(order, 'cancelled', resolvedPreparationMinutes);
    setPendingDecliningOrderId(orderId);
    try {
      await updatePosOrder(orderId, payload);
      setOrders((prev) => prev.map((row) => (
        Number(row?.id || 0) === orderId
          ? {
            ...row,
            status: 'cancelled',
            preparation_time_minutes: resolvedPreparationMinutes,
            queue_timers: payload.queue_timers,
            updated_at: new Date().toISOString(),
          }
          : row
      )));
      setToastMessage('success', `Order #${formatOrderNumber(order?.order_number)} declined.`);
      return true;
    } catch (err) {
      setToastMessage('error', err?.userMessage || 'Unable to decline pending order.');
      return false;
    } finally {
      setPendingDecliningOrderId(null);
    }
  }, [buildIncomingOrderDecisionPayload, incomingOrderPrepMinutes]);

  const handleIncomingOrderDecision = useCallback(async (decision) => {
    const activeOrder = incomingOrderDialog.activeOrder;
    const orderId = Number(activeOrder?.id || 0);
    if (!orderId) return;
    setIncomingOrderDialog((prev) => ({ ...prev, processing: true }));
    if (decision === 'accept') {
      await handleAcceptPendingOrder(activeOrder, {
        preparationMinutes: incomingOrderPrepMinutes,
        fromPopup: true,
      });
      return;
    }

    const nextPreparationMinutes = snapToIncomingPrepOption(
      incomingOrderPrepMinutes || activeOrder?.preparation_time_minutes || 30
    );
    const payload = buildIncomingOrderDecisionPayload(activeOrder, 'cancelled', nextPreparationMinutes);
    try {
      await updatePosOrder(orderId, payload);
      setOrders((prev) => prev.map((row) => (
        Number(row?.id || 0) === orderId
          ? {
            ...row,
            status: 'cancelled',
            preparation_time_minutes: nextPreparationMinutes,
            queue_timers: payload.queue_timers,
            updated_at: new Date().toISOString(),
          }
          : row
      )));
      setToastMessage(
        'success',
        `Order #${formatOrderNumber(activeOrder?.order_number)} declined.`
      );
      setIncomingOrderDialog((prev) => {
        const remainingQueue = (Array.isArray(prev.queue) ? prev.queue : [])
          .filter((order) => Number(order?.id || 0) !== orderId);
        const nextActive = remainingQueue[0] || null;
        return {
          open: Boolean(nextActive),
          queue: remainingQueue,
          activeOrder: nextActive,
          processing: false,
        };
      });
    } catch (err) {
      setIncomingOrderDialog((prev) => ({ ...prev, processing: false }));
      setToastMessage('error', err?.userMessage || 'Unable to update incoming order status.');
    }
  }, [buildIncomingOrderDecisionPayload, handleAcceptPendingOrder, incomingOrderDialog.activeOrder, incomingOrderDialog.queue, incomingOrderPrepMinutes]);

  if (loading && !error) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <ErrorState
        error={error}
        statusCode={error?.statusCode}
        onRetry={loadData}
        showHomeButton={true}
      />
    );
  }

  return (
    <Box sx={{ p: embedded ? 0 : { xs: 2, sm: 3 } }}>
      <PrinterValidationBanner />

      {!embedded ? (
        <>
      <Box sx={{ mb: 0.5 }}>
        <DashboardHeader
          title="Restaurant"
          breadcrumbs={[
            { label: 'Home', path: '/dashboard/' },
            { label: 'Restaurant' },
          ]}
          navLinks={[
            { url: '/pos-restaurant/?tab=dashboard', label: 'Dashboard' },
            { url: '/pos-restaurant/?tab=orders', label: 'Orders' },
          ]}
        />
      </Box>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.6 },
          mb: 2.5,
          borderRadius: 3,
          border: `1px solid ${isDark ? 'rgba(76,225,182,0.34)' : 'rgba(76,225,182,0.3)'}`,
          background: isDark
            ? 'linear-gradient(135deg, rgba(23,26,34,0.98) 0%, rgba(34,22,27,0.96) 52%, rgba(24,30,39,0.98) 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #fff7ed 45%, #ecfeff 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            right: -44,
            top: -52,
            width: 185,
            height: 185,
            borderRadius: '50%',
            background: isDark ? 'rgba(76,225,182,0.12)' : 'rgba(56,189,248,0.12)',
            filter: 'blur(2px)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            left: -34,
            bottom: -66,
            width: 165,
            height: 165,
            borderRadius: '50%',
            background: isDark ? 'rgba(251,146,60,0.12)' : 'rgba(251,146,60,0.16)',
          }}
        />
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.6}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 1.6,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: isDark ? 'rgba(76,225,182,0.2)' : 'rgba(20,184,166,0.14)',
                  border: `1px solid ${isDark ? 'rgba(76,225,182,0.45)' : 'rgba(20,184,166,0.25)'}`,
                }}
              >
                <RestaurantIcon sx={{ color: BOOKING_DRAWER_ACCENT, fontSize: 20 }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: -0.25 }}>
                Restaurant Command Center
              </Typography>
            </Stack>
            <Typography sx={{ mt: 0.6, color: isDark ? '#b6c1d0' : '#546176', maxWidth: 740 }}>
              Track rooms, tables, queue criticality and live KOT service windows from one dashboard.
            </Typography>
          </Box>
          <Box sx={{ width: { xs: '100%', md: 320 }, ml: { md: 'auto' } }}>
            <Box
              sx={{
                p: { xs: 1.25, sm: 1.4 },
                borderRadius: 2.2,
                border: `1px solid ${isDark ? 'rgba(76,225,182,0.38)' : 'rgba(76,225,182,0.32)'}`,
                background: isDark
                  ? 'linear-gradient(150deg, rgba(76,225,182,0.18) 0%, rgba(59,130,246,0.12) 58%, rgba(15,23,42,0.52) 100%)'
                  : 'linear-gradient(150deg, rgba(76,225,182,0.18) 0%, rgba(59,130,246,0.08) 58%, rgba(255,255,255,0.92) 100%)',
                boxShadow: isDark
                  ? '0 14px 28px rgba(10,18,30,0.32)'
                  : '0 12px 24px rgba(15,23,42,0.12)',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={0.85} alignItems="center" sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: 1.5,
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: isDark ? 'rgba(76,225,182,0.2)' : 'rgba(20,184,166,0.16)',
                      border: `1px solid ${isDark ? 'rgba(76,225,182,0.45)' : 'rgba(20,184,166,0.28)'}`,
                    }}
                  >
                    <ReceiptLongIcon sx={{ fontSize: 18, color: BOOKING_DRAWER_ACCENT }} />
                  </Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, opacity: 0.9 }}>
                    RUNNING ORDER TOTAL
                  </Typography>
                </Stack>
                <Chip
                  size="small"
                  label={`${activeOrderQueue.length} Active`}
                  sx={{
                    height: 22,
                    bgcolor: isDark ? 'rgba(15,23,42,0.32)' : 'rgba(255,255,255,0.72)',
                    color: isDark ? '#dbeafe' : '#1e3a8a',
                    fontWeight: 700,
                  }}
                />
              </Stack>
              <Typography
                variant="h4"
                sx={{
                  mt: 0.55,
                  fontWeight: 900,
                  letterSpacing: -0.4,
                  color: isDark ? '#f0fdfa' : '#0f172a',
                  lineHeight: 1.15,
                }}
              >
                {formatCurrencyAmount(queueRunningOrderTotal)}
              </Typography>
              <Typography variant="caption" sx={{ mt: 0.25, display: 'block', opacity: 0.8 }}>
                Live sum of all queue order values.
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Paper>
      {pendingOrdersByRecent.length > 0 ? (
        <Paper
          ref={ordersSectionRef}
          sx={{
            mt: 1.4,
            mb: 2.2,
            p: { xs: 1.25, sm: 1.6 },
            borderRadius: 2.6,
            border: `1px solid ${isDark ? 'rgba(76,225,182,0.22)' : 'rgba(76,225,182,0.28)'}`,
            background: isDark
              ? 'linear-gradient(165deg, rgba(37,37,44,0.98) 0%, rgba(22,22,28,0.98) 100%)'
              : 'linear-gradient(165deg, #ffffff 0%, #f6fffb 100%)',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ReceiptLongIcon sx={{ color: BOOKING_DRAWER_ACCENT }} />
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Orders
              </Typography>
            </Stack>
            <Chip
              size="small"
              label={`${pendingOrdersByRecent.length} Pending`}
              sx={{
                bgcolor: isDark ? alpha('#f59e0b', 0.26) : alpha('#f59e0b', 0.22),
                color: isDark ? '#fde68a' : '#92400e',
                fontWeight: 700,
              }}
            />
          </Stack>
          <Paper
            variant="outlined"
            sx={{
              p: 1,
              borderRadius: 1.8,
              borderColor: alpha('#f59e0b', 0.5),
              bgcolor: isDark ? alpha('#f59e0b', 0.15) : alpha('#f59e0b', 0.12),
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: isDark ? '#fde68a' : '#92400e', mb: 0.7 }}>
              Pending Queue
            </Typography>
            <Stack spacing={0.6}>
              {pendingOrdersByRecent.slice(0, 8).map((order) => (
                <Paper
                  key={`restaurant-pending-order-${order.id}`}
                  variant="outlined"
                  onClick={() => handleQueueOrderClick(order)}
                  sx={{
                    p: 0.85,
                    borderRadius: 1.4,
                    cursor: 'pointer',
                    borderColor: alpha('#f59e0b', 0.42),
                    bgcolor: isDark ? alpha('#f59e0b', 0.08) : alpha('#fff7ed', 0.9),
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={0.8}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        #{formatOrderNumber(order?.order_number)}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.82 }}>
                        {order?.room_number ? `Room ${order.room_number}` : (order?.table_name || 'Table')} | {formatOrderDateTime(order?.created_at)}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.45} alignItems="center">
                      <Button
                        size="small"
                        variant="contained"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleAcceptPendingOrder(order, {
                            preparationMinutes: snapToIncomingPrepOption(
                              order?.preparation_time_minutes || incomingOrderPrepMinutes || 30
                            ),
                          });
                        }}
                        disabled={
                          pendingAcceptingOrderId === Number(order?.id || 0)
                          || pendingDecliningOrderId === Number(order?.id || 0)
                        }
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          minWidth: 70,
                          bgcolor: '#16a34a',
                          '&:hover': { bgcolor: '#15803d' },
                        }}
                      >
                        {pendingAcceptingOrderId === Number(order?.id || 0) ? '...' : 'Accept'}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDeclinePendingOrder(order);
                        }}
                        disabled={
                          pendingDecliningOrderId === Number(order?.id || 0)
                          || pendingAcceptingOrderId === Number(order?.id || 0)
                        }
                        sx={{ textTransform: 'none', fontWeight: 700, minWidth: 74 }}
                      >
                        {pendingDecliningOrderId === Number(order?.id || 0) ? '...' : 'Decline'}
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Paper>
        </Paper>
      ) : null}
        </>
      ) : null}

      <Snackbar
        open={toast.open}
        autoHideDuration={3400}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{
          vertical: isCompact ? 'bottom' : 'top',
          horizontal: 'center',
        }}
        TransitionComponent={ToastSlideTransition}
        sx={{
          zIndex: (theme) => theme.zIndex.snackbar + 2,
          ...(isCompact
            ? {
                bottom: `calc(${MOBILE_BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px) + 16px) !important`,
                left: 16,
                right: 16,
              }
            : {
                top: `calc(${MOBILE_APP_HEADER_HEIGHT}px + env(safe-area-inset-top, 0px) + 12px) !important`,
              }),
        }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          onClick={() => {
            if (String(toast.message || '').toLowerCase().includes('validate the printer')) {
              openPrinterValidationDialog();
            }
          }}
          sx={{
            width: '100%',
            minWidth: { xs: '100%', sm: 360 },
            maxWidth: { xs: '100%', sm: 520 },
            borderRadius: 2,
            cursor: String(toast.message || '').toLowerCase().includes('validate the printer')
              ? 'pointer'
              : 'default',
            boxShadow: isDark ? '0 10px 24px rgba(0,0,0,0.35)' : '0 10px 24px rgba(15,23,42,0.18)',
          }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {!embedded ? (
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Stack spacing={2}>
            <Paper sx={getServiceSectionPanelSx(isDark, '#4ce1b6')}>
              <Box
                sx={{
                  position: 'absolute',
                  right: -46,
                  top: -58,
                  width: 142,
                  height: 142,
                  borderRadius: '50%',
                  bgcolor: isDark ? alpha('#4ce1b6', 0.2) : alpha('#4ce1b6', 0.14),
                  pointerEvents: 'none',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: -42,
                  bottom: -66,
                  width: 128,
                  height: 128,
                  borderRadius: '50%',
                  bgcolor: isDark ? alpha('#38bdf8', 0.14) : alpha('#38bdf8', 0.1),
                  pointerEvents: 'none',
                }}
              />
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5, position: 'relative', zIndex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Rooms
                </Typography>
                <Chip
                  size="small"
                  label={data.rooms.length}
                  sx={{
                    fontWeight: 700,
                    bgcolor: isDark ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.14)',
                    color: isDark ? '#c7d2fe' : '#3730a3',
                  }}
                />
              </Stack>
              {data.rooms.length === 0 ? (
                <Alert severity="info">
                  No rooms enabled for restaurant billing. Enable rooms from POS configuration.
                </Alert>
              ) : (
                <Grid container spacing={1.4} sx={{ position: 'relative', zIndex: 1 }}>
                  {data.rooms.map((room) => {
                    const roomImageSrc = getRoomImageSrc(room);
                    const activeOrderCount = activeRoomOrderCounts.get(Number(room.id)) || 0;
                    const hasActiveOrder = activeOrderCount > 0;
                    return (
                      <Grid item xs={12} sm={6} lg={3} key={room.id}>
                        <Paper
                          variant="outlined"
                          onClick={() => openOrderDrawer('room', room)}
                          sx={getServiceCardSx(isDark, hasActiveOrder)}
                        >
                          <Box sx={getServiceCardMediaSx(isDark, hasActiveOrder)}>
                            {roomImageSrc ? (
                              <Box
                                component="img"
                                src={roomImageSrc}
                                alt={`Room ${room.room_number}`}
                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                                <HotelIcon sx={{ fontSize: 34, color: isDark ? '#8ddbc7' : '#1f8f70' }} />
                              </Stack>
                            )}
                          </Box>
                          <Box sx={{ p: 1.1 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                              <Typography sx={{ fontWeight: 800, fontSize: 14 }}>Room {room.room_number}</Typography>
                              {hasActiveOrder ? (
                                <Chip
                                  size="small"
                                  label={`Active x${activeOrderCount}`}
                                  sx={{
                                    bgcolor: isDark ? 'rgba(255,107,53,0.3)' : 'rgba(255,107,53,0.22)',
                                    color: isDark ? '#ffd4c6' : '#9a3412',
                                    fontWeight: 700,
                                    '& .MuiChip-label': { px: 0.9 },
                                  }}
                                />
                              ) : null}
                            </Stack>
                            <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mt: 0.7 }}>
                              <HotelIcon sx={{ fontSize: 15, color: BOOKING_DRAWER_ACCENT }} />
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12.8 }}>
                                {room.room_type_name || 'Room Type'}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.45 }}>
                              <ApartmentIcon sx={{ fontSize: 14, color: isDark ? '#9aa5b1' : '#64748b' }} />
                              <Typography variant="caption" sx={{ opacity: 0.8, fontSize: 11.6 }}>
                                {room.floor_label || 'No floor assigned'}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mt: 0.55 }}>
                              <PersonIcon sx={{ fontSize: 15, color: '#f59e0b' }} />
                              <Typography variant="body2" sx={{ fontSize: 12 }}>
                                Guest: {room.guest_name || 'No active guest'}
                              </Typography>
                            </Stack>
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Paper>

            <Paper sx={getServiceSectionPanelSx(isDark, '#f59e0b')}>
              <Box
                sx={{
                  position: 'absolute',
                  right: -46,
                  top: -58,
                  width: 142,
                  height: 142,
                  borderRadius: '50%',
                  bgcolor: isDark ? alpha('#f59e0b', 0.2) : alpha('#f59e0b', 0.14),
                  pointerEvents: 'none',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: -42,
                  bottom: -66,
                  width: 128,
                  height: 128,
                  borderRadius: '50%',
                  bgcolor: isDark ? alpha('#fb923c', 0.14) : alpha('#fb923c', 0.1),
                  pointerEvents: 'none',
                }}
              />
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5, position: 'relative', zIndex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Tables
                </Typography>
                <Chip
                  size="small"
                  label={data.tables.length}
                  sx={{
                    fontWeight: 700,
                    bgcolor: isDark ? 'rgba(245,158,11,0.22)' : 'rgba(245,158,11,0.16)',
                    color: isDark ? '#fde68a' : '#92400e',
                  }}
                />
              </Stack>
              {data.tables.length === 0 ? (
                <Alert severity="info">No POS tables configured.</Alert>
              ) : (
                <Grid container spacing={1.4} sx={{ position: 'relative', zIndex: 1 }}>
                  {data.tables.map((table) => {
                    const activeOrderCount = activeTableOrderCounts.get(Number(table.id)) || 0;
                    const hasActiveOrder = activeOrderCount > 0;
                    return (
                      <Grid item xs={12} sm={6} lg={3} key={table.id}>
                        <Paper
                          variant="outlined"
                          onClick={() => openOrderDrawer('table', table)}
                          sx={getServiceCardSx(isDark, hasActiveOrder)}
                        >
                          <Box sx={getServiceCardMediaSx(isDark, hasActiveOrder)}>
                            <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                              <TableRestaurantIcon sx={{ fontSize: 34, color: isDark ? '#8ddbc7' : '#1f8f70' }} />
                            </Stack>
                          </Box>
                          <Box sx={{ p: 1.1 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                              <Typography sx={{ fontWeight: 800, fontSize: 14 }}>{table.name}</Typography>
                              {hasActiveOrder ? (
                                <Chip
                                  size="small"
                                  label={`Active x${activeOrderCount}`}
                                  sx={{
                                    bgcolor: isDark ? 'rgba(255,107,53,0.3)' : 'rgba(255,107,53,0.22)',
                                    color: isDark ? '#ffd4c6' : '#9a3412',
                                    fontWeight: 700,
                                    '& .MuiChip-label': { px: 0.9 },
                                  }}
                                />
                              ) : null}
                            </Stack>
                            <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mt: 0.7 }}>
                              <PersonIcon sx={{ fontSize: 15, color: BOOKING_DRAWER_ACCENT }} />
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12.8 }}>
                                Seats: {table.capacity || 0}
                              </Typography>
                            </Stack>
                            <Typography
                              variant="caption"
                              sx={{
                                display: '-webkit-box',
                                mt: 0.45,
                                overflow: 'hidden',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                opacity: 0.8,
                                fontSize: 11.6,
                              }}
                            >
                              {table.extra_information || 'No extra information'}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Paper>
          </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 2.1,
              borderRadius: 2.5,
              border: `1px solid ${isDark ? 'rgba(76,225,182,0.24)' : 'rgba(76,225,182,0.3)'}`,
              background: isDark
                ? 'linear-gradient(165deg, rgba(37,37,44,0.98) 0%, rgba(22,22,28,0.98) 100%)'
                : 'linear-gradient(165deg, #ffffff 0%, #f6fffb 100%)',
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <ReceiptLongIcon sx={{ color: BOOKING_DRAWER_ACCENT }} />
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Order Queue
                </Typography>
              </Stack>
              <Stack spacing={0.5} alignItems="flex-end">
                <Chip
                  size="small"
                  label={`${activeOrderQueue.length} Active`}
                  sx={{
                    bgcolor: isDark ? 'rgba(76,225,182,0.2)' : 'rgba(76,225,182,0.15)',
                    color: isDark ? '#8de5cd' : '#1f8f70',
                  }}
                />
              </Stack>
            </Stack>

            <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap" sx={{ mb: 1.2 }}>
              {[
                { label: 'On Track', color: '#16a34a' },
                { label: 'Attention', color: '#ca8a04' },
                { label: 'High', color: '#d97706' },
                { label: 'Urgent', color: '#ea580c' },
                { label: 'Critical+', color: '#b91c1c' },
              ].map((level) => (
                <Chip
                  key={`queue-level-${level.label}`}
                  size="small"
                  label={level.label}
                  sx={{
                    height: 21,
                    fontSize: 11,
                    bgcolor: alpha(level.color, isDark ? 0.28 : 0.16),
                    color: level.color,
                    fontWeight: 700,
                  }}
                />
              ))}
            </Stack>

            {activeOrderQueue.length === 0 ? (
              <Alert severity="info">No active order in queue.</Alert>
            ) : (
              <Stack spacing={1.1}>
                {activeOrderQueue.map((order, index) => {
                  const statusMeta = getOrderStatusMeta(order.status);
                  const statusKey = String(order?.status || '').toLowerCase();
                  const isPendingAcceptance =
                    (statusKey === 'draft' || statusKey === 'placed') && isQrBasedIncomingOrder(order);
                  const queueTimers = normalizeQueueTimers(order?.queue_timers);
                  const activeTimer = queueTimers.find((timer) => !timer.ended_at) || null;
                  const latestTimer = queueTimers.length ? queueTimers[queueTimers.length - 1] : null;
                  const timerStartedAt = activeTimer?.started_at || latestTimer?.started_at || order?.created_at || null;
                  const elapsedReferenceTimestamp = activeTimer
                    ? queueNowTimestamp
                    : (
                      latestTimer?.ended_at
                        ? new Date(latestTimer.ended_at).getTime()
                        : (order?.status === 'served'
                          ? new Date(order?.updated_at || order?.created_at || 0).getTime()
                          : queueNowTimestamp)
                    );
                  const elapsedMs = (isPendingAcceptance || !timerStartedAt)
                    ? 0
                    : getOrderElapsedMs(
                      { created_at: timerStartedAt },
                      elapsedReferenceTimestamp || queueNowTimestamp
                    );
                  const elapsedMinutes = elapsedMs / 60000;
                  const targetMinutes = Math.max(
                    10,
                    Number(activeTimer?.target_minutes || latestTimer?.target_minutes || order?.preparation_time_minutes || 30)
                  );
                  const isTimerStopped = !activeTimer && (Boolean(latestTimer?.ended_at) || order?.status === 'served');
                  const criticalityMeta = isPendingAcceptance
                    ? { label: 'Pending Acceptance', color: isDark ? '#67e8f9' : '#0891b2' }
                    : isTimerStopped
                    ? { label: 'Timer Stopped', color: isDark ? '#94a3b8' : '#64748b' }
                    : getQueueCriticalityMeta(elapsedMinutes, targetMinutes);
                  const elapsedLabel = formatOrderElapsedTime(elapsedMs);
                  const criticalityProgress = Math.min(100, Math.max(0, (elapsedMinutes / (targetMinutes * 2)) * 100));
                  const isServing = Boolean(servingOrderIds[order.id]);
                  const isRoomServiceQueueOrder = String(order?.order_type || '').toLowerCase() === 'room_service'
                    || Number(order?.room || 0) > 0
                    || Boolean(order?.room_number);
                  const rawTableLabel = String(order?.table_name || order?.table || '').trim();
                  const tableLabelNumber = rawTableLabel.match(/\d+/)?.[0] || rawTableLabel;
                  const queueTableChipLabel = tableLabelNumber
                    ? `T${Number(tableLabelNumber) || tableLabelNumber}`
                    : 'T--';
                  const itemSummary = (order.items || [])
                    .map((item) => `${item.menu_item_name || item.item_name} x${Number(item.quantity || 0)}`)
                    .join(', ');
                  return (
                    <Paper
                      key={order.id}
                      variant="outlined"
                      onClick={() => handleQueueOrderClick(order)}
                      sx={{
                        p: 1.2,
                        borderRadius: 2,
                        cursor: 'pointer',
                        borderColor: alpha(criticalityMeta.color, isDark ? 0.6 : 0.35),
                        boxShadow: isDark
                          ? `0 6px 16px ${alpha(criticalityMeta.color, 0.2)}`
                          : `0 6px 16px ${alpha(criticalityMeta.color, 0.15)}`,
                        background: isDark
                          ? `linear-gradient(150deg, ${alpha(criticalityMeta.color, 0.18)} 0%, rgba(20,20,26,0.96) 72%)`
                          : `linear-gradient(150deg, ${alpha(criticalityMeta.color, 0.12)} 0%, #ffffff 72%)`,
                        transition: 'transform .2s ease, box-shadow .2s ease, border-color .2s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: isDark
                            ? `0 10px 22px ${alpha(criticalityMeta.color, 0.3)}`
                            : `0 10px 22px ${alpha(criticalityMeta.color, 0.22)}`,
                        },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.6 }}>
                        <Chip
                          size="small"
                          label={`Queue #${String(index + 1).padStart(2, '0')}`}
                          sx={{
                            bgcolor: isDark ? 'rgba(76,225,182,0.18)' : 'rgba(76,225,182,0.14)',
                            color: isDark ? '#8de5cd' : '#1f8f70',
                            fontWeight: 700,
                          }}
                        />
                        <Stack direction="row" spacing={0.55}>
                          <Chip
                            size="small"
                            label={statusMeta.label}
                            sx={{
                              bgcolor: `${statusMeta.color}22`,
                              color: statusMeta.color,
                              fontWeight: 700,
                            }}
                          />
                          <Chip
                            size="small"
                            label={criticalityMeta.label}
                            sx={{
                              bgcolor: alpha(criticalityMeta.color, isDark ? 0.38 : 0.2),
                              color: criticalityMeta.color,
                              fontWeight: 800,
                            }}
                          />
                        </Stack>
                      </Stack>

                      <Stack direction="row" spacing={0.9} alignItems="center" sx={{ mb: 0.65 }}>
                        {getOrderTypeIcon(order.order_type)}
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {getOrderTypeLabel(order.order_type)}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.74 }}>
                          {order.room_number ? `Room ${order.room_number}` : order.table_name || 'Table'}
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 0.55 }}>
                        <AccessTimeIcon sx={{ fontSize: 14, opacity: 0.72 }} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 800,
                            letterSpacing: 0.2,
                            color: criticalityMeta.color,
                          }}
                        >
                          {elapsedLabel}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                          Started {formatOrderTime(timerStartedAt)}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.65 }}>
                          #{formatOrderNumber(order.order_number)}
                        </Typography>
                        <Stack direction="row" spacing={0.6} alignItems="center" sx={{ ml: 'auto' }}>
                          {!isRoomServiceQueueOrder ? (
                            <Chip
                              size="small"
                              label={queueTableChipLabel}
                              sx={{
                                height: 20,
                                bgcolor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.08)',
                                color: isDark ? '#dce4ee' : '#475569',
                                fontWeight: 700,
                              }}
                            />
                          ) : null}
                          <Chip
                            size="small"
                            label={`SLA ${targetMinutes}m`}
                            sx={{
                              height: 20,
                              bgcolor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.08)',
                              color: isDark ? '#dce4ee' : '#475569',
                              fontWeight: 700,
                            }}
                          />
                          <Button
                            size="small"
                            variant={isTimerStopped ? 'outlined' : 'contained'}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleServeQueueOrder(order);
                            }}
                            disabled={isTimerStopped || isServing}
                            sx={{
                              minWidth: 72,
                              height: 26,
                              px: 1,
                              textTransform: 'none',
                              fontSize: 11.5,
                              fontWeight: 800,
                              borderRadius: 1.5,
                              ...(isTimerStopped
                                ? {
                                  borderColor: isDark ? '#5b6676' : '#a7b3c3',
                                  color: isDark ? '#b9c5d6' : '#556579',
                                }
                                : {
                                  backgroundColor: '#16a34a',
                                  color: '#ffffff',
                                  '&:hover': { backgroundColor: '#15803d' },
                                }),
                            }}
                          >
                            {isTimerStopped ? 'Served' : isServing ? 'Serving...' : 'Serve'}
                          </Button>
                        </Stack>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={criticalityProgress}
                        sx={{
                          height: 6,
                          borderRadius: 999,
                          bgcolor: isDark ? 'rgba(255,255,255,0.14)' : 'rgba(15,23,42,0.08)',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 999,
                            backgroundColor: criticalityMeta.color,
                          },
                        }}
                      />

                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.85,
                          fontSize: 12.6,
                          opacity: 0.9,
                          display: '-webkit-box',
                          overflow: 'hidden',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {itemSummary || 'No item details'}
                      </Typography>

                      <Typography variant="caption" sx={{ display: 'block', mt: 0.55, fontWeight: 700 }}>
                        Total: {CURRENCY} {Number(order.total_amount || 0).toFixed(2)}
                      </Typography>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>
      ) : null}

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        sx={getDrawerRootSx({
          '& .MuiDrawer-paper': posOrderDrawerPaperSx,
        })}
        PaperProps={{
          sx: posOrderDrawerPaperSx,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              ...getBookingDrawerHeaderSx(isDark),
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <RestaurantIcon sx={{ color: BOOKING_DRAWER_ACCENT, fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: drawerTheme.text }}>
                {isEmbeddedEditMode ? 'Edit Order' : 'Place Order'}
              </Typography>
              {selection ? (
                <Chip
                  size="small"
                  label={isEmbeddedEditMode
                    ? `#${formatOrderNumber(editingOrder?.order_number)}`
                    : (selection.targetType === 'room' ? `Room ${selection.target.room_number}` : selection.target.name)}
                  sx={getBookingDrawerMetaChipSx(isDark)}
                />
              ) : null}
            </Stack>
            <Stack direction="row" spacing={0.8} alignItems="center">
              {selection && !isEmbeddedEditMode ? (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(event) => setOrderTargetMenuAnchorEl(event.currentTarget)}
                  disabled={isTargetChangeLoading || submitting}
                  startIcon={<AutorenewIcon sx={{ fontSize: 16 }} />}
                  endIcon={<KeyboardArrowDownRoundedIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    ...getBookingDrawerAuditButtonSx(isDark),
                    borderRadius: 999,
                    px: 1.2,
                  }}
                >
                  Change Room/Table
                </Button>
              ) : null}
              <IconButton onClick={closeDrawer}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>
          <Menu
            anchorEl={orderTargetMenuAnchorEl}
            open={Boolean(orderTargetMenuAnchorEl)}
            onClose={() => setOrderTargetMenuAnchorEl(null)}
            {...withDrawerMenuPaperSx(drawerOverlays.menuProps, {
              width: 320,
              maxHeight: 420,
              borderRadius: 2,
              border: `1px solid ${isDark ? '#364152' : '#d7dee8'}`,
              background: isDark
                ? 'linear-gradient(165deg, rgba(29,31,40,0.98) 0%, rgba(21,23,32,0.98) 100%)'
                : 'linear-gradient(165deg, #ffffff 0%, #f8fbff 100%)',
            })}
          >
            <Box sx={{ px: 1.2, py: 0.9, borderBottom: `1px solid ${isDark ? '#333b4a' : '#e2e8f0'}` }}>
              <Typography sx={{ fontWeight: 800, fontSize: 13.2 }}>Move Current Order</Typography>
              <Typography variant="caption" sx={{ opacity: 0.74 }}>
                Choose destination room/table. Existing active order will be merged.
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ px: 1.2, py: 0.55, display: 'block', opacity: 0.7 }}>
              Rooms
            </Typography>
            {data.rooms.map((room) => {
              const roomId = Number(room.id);
              const isCurrentTarget = selection?.targetType === 'room' && Number(selection?.target?.id) === roomId;
              const roomDestinationOrder = findLatestEditableOrderByTarget('room', roomId, editingOrder?.id);
              return (
                <MenuItem
                  key={`move-room-${room.id}`}
                  disabled={isCurrentTarget || isTargetChangeLoading}
                  onClick={() => handleChangeOrderTarget('room', room)}
                  sx={{ py: 0.8 }}
                >
                  <ListItemIcon sx={{ minWidth: 34 }}>
                    <HotelIcon sx={{ fontSize: 18, color: isDark ? '#8bcad6' : '#0e7490' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={`Room ${room.room_number}`}
                    secondary={roomDestinationOrder
                      ? `Merge with ${formatOrderNumber(roomDestinationOrder.order_number)}`
                      : 'Move order here'}
                    primaryTypographyProps={{ fontWeight: 700, fontSize: 13.2 }}
                    secondaryTypographyProps={{ fontSize: 11.3 }}
                  />
                  {isCurrentTarget ? (
                    <Chip size="small" label="Current" sx={{ height: 20 }} />
                  ) : roomDestinationOrder ? (
                    <Chip
                      size="small"
                      label="Merge"
                      sx={{
                        height: 20,
                        bgcolor: isDark ? 'rgba(249,115,22,0.22)' : 'rgba(249,115,22,0.16)',
                        color: isDark ? '#fdba74' : '#c2410c',
                        fontWeight: 700,
                      }}
                    />
                  ) : null}
                </MenuItem>
              );
            })}
            <Typography variant="caption" sx={{ px: 1.2, py: 0.55, display: 'block', opacity: 0.7 }}>
              Tables
            </Typography>
            {data.tables.map((table) => {
              const tableId = Number(table.id);
              const isCurrentTarget = selection?.targetType === 'table' && Number(selection?.target?.id) === tableId;
              const tableDestinationOrder = findLatestEditableOrderByTarget('table', tableId, editingOrder?.id);
              return (
                <MenuItem
                  key={`move-table-${table.id}`}
                  disabled={isCurrentTarget || isTargetChangeLoading}
                  onClick={() => handleChangeOrderTarget('table', table)}
                  sx={{ py: 0.8 }}
                >
                  <ListItemIcon sx={{ minWidth: 34 }}>
                    <TableRestaurantIcon sx={{ fontSize: 18, color: isDark ? '#93c5fd' : '#1d4ed8' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={table.name || `Table ${table.id}`}
                    secondary={tableDestinationOrder
                      ? `Merge with ${formatOrderNumber(tableDestinationOrder.order_number)}`
                      : 'Move order here'}
                    primaryTypographyProps={{ fontWeight: 700, fontSize: 13.2 }}
                    secondaryTypographyProps={{ fontSize: 11.3 }}
                  />
                  {isCurrentTarget ? (
                    <Chip size="small" label="Current" sx={{ height: 20 }} />
                  ) : tableDestinationOrder ? (
                    <Chip
                      size="small"
                      label="Merge"
                      sx={{
                        height: 20,
                        bgcolor: isDark ? 'rgba(249,115,22,0.22)' : 'rgba(249,115,22,0.16)',
                        color: isDark ? '#fdba74' : '#c2410c',
                        fontWeight: 700,
                      }}
                    />
                  ) : null}
                </MenuItem>
              );
            })}
          </Menu>

          {isTargetChangeLoading ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 5,
                bgcolor: isDark ? 'rgba(7,9,13,0.62)' : 'rgba(15,23,42,0.2)',
                backdropFilter: 'blur(2px)',
                p: 2,
                pt: 11,
                pointerEvents: 'all',
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 1.4,
                  borderRadius: 2,
                  border: `1px solid ${isDark ? 'rgba(76,225,182,0.28)' : 'rgba(15,118,110,0.18)'}`,
                  background: isDark
                    ? 'linear-gradient(165deg, rgba(24,27,36,0.94) 0%, rgba(20,22,30,0.96) 100%)'
                    : 'linear-gradient(165deg, rgba(255,255,255,0.96) 0%, rgba(248,251,255,0.98) 100%)',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <CircularProgress size={18} />
                  <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>
                    Updating order destination...
                  </Typography>
                </Stack>
                <Skeleton variant="rounded" height={38} sx={{ mb: 1 }} />
                <Skeleton variant="rounded" height={38} sx={{ mb: 1 }} />
                <Skeleton variant="rounded" height={88} />
              </Paper>
            </Box>
          ) : null}

          <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', p: 2 }}>
            <Paper variant="outlined" sx={getPosOrderDrawerSectionPaperSx(isDark)}>
              <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.8, opacity: 0.82 }}>
                Service Options
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={orderMeta.service_mode}
                onChange={(_, value) => {
                  if (!value) return;
                  setOrderMeta((prev) => ({ ...prev, service_mode: value }));
                }}
                sx={getPosOrderDrawerToggleGroupSx(isDark)}
              >
                {[
                  { value: 'delivery', label: 'Delivery' },
                  { value: 'pick_up', label: 'Pick Up' },
                  { value: 'dine_in', label: 'Dine In' },
                  { value: 'room_service', label: 'Room Service' },
                ].map((option) => (
                  <ToggleButton
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Paper>

            <Grid container spacing={1.1} sx={{ mb: 1.5 }}>
              <Grid item xs={12} sm={6}>
                <Box ref={phoneInputAnchorRef}>
                  <TextField
                    fullWidth
                    size="small"
                    label={selection?.targetType === 'room' ? 'Mobile Number' : 'Mobile Number (Optional)'}
                    value={orderMeta.mobile}
                    onChange={(e) => {
                      const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setOrderMeta((prev) => ({ ...prev, mobile: digitsOnly }));
                    }}
                    inputProps={{ maxLength: 10, inputMode: 'numeric', pattern: '[0-9]*' }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            ref={phoneCountryCodeAnchorRef}
                            onClick={() => setPhoneCountryCodeMenuOpen(true)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              gap: 0.5,
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              '&:hover': {
                                backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                              },
                            }}
                          >
                            <Typography component="span" sx={{ fontSize: '1.1rem' }}>
                              {selectedPhoneCountryCode?.flag || '🇮🇳'}
                            </Typography>
                            <Typography component="span" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                              {selectedPhoneCountryCode?.dialCode || '+91'}
                            </Typography>
                            <Typography component="span" sx={{ fontSize: '0.72rem', opacity: 0.72 }}>▼</Typography>
                          </Box>
                        </InputAdornment>
                      ),
                      endAdornment: searchingGuest ? (
                        <InputAdornment position="end">
                          <CircularProgress size={16} />
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={selection?.targetType === 'room' ? 'Room Guest' : 'Name (Optional)'}
                  value={
                    selection?.targetType === 'room'
                      ? (orderMeta.customer_name || selection?.target?.guest_name || '')
                      : orderMeta.customer_name
                  }
                  onChange={(e) => setOrderMeta((prev) => ({ ...prev, customer_name: e.target.value }))}
                />
              </Grid>
            </Grid>
            <Menu
              anchorEl={phoneCountryCodeAnchorRef.current}
              open={phoneCountryCodeMenuOpen}
              onClose={() => {
                setPhoneCountryCodeMenuOpen(false);
                setPhoneCountryCodeSearch('');
              }}
              {...withDrawerMenuPaperSx(drawerOverlays.menuProps, {
                maxHeight: 400,
                width: 300,
              })}
            >
              <Box sx={{ p: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search country..."
                  value={phoneCountryCodeSearch}
                  onChange={(e) => setPhoneCountryCodeSearch(e.target.value)}
                  sx={{ mb: 1 }}
                />
              </Box>
              {countryCodes
                .filter((country) => {
                  if (!phoneCountryCodeSearch) return true;
                  const search = phoneCountryCodeSearch.toLowerCase();
                  return (
                    country.name.toLowerCase().includes(search)
                    || country.dialCode.includes(search)
                    || country.code.toLowerCase().includes(search)
                  );
                })
                .map((country) => (
                  <MenuItem
                    key={country.code}
                    onClick={() => {
                      setOrderMeta((prev) => ({ ...prev, phone_country_code: country.dialCode }));
                      setPhoneCountryCodeMenuOpen(false);
                      setPhoneCountryCodeSearch('');
                    }}
                    selected={selectedPhoneCountryCode?.dialCode === country.dialCode}
                  >
                    <ListItemIcon>
                      <Typography sx={{ fontSize: '1.1rem' }}>{country.flag}</Typography>
                    </ListItemIcon>
                    <ListItemText
                      primary={country.dialCode}
                      secondary={country.name}
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                  </MenuItem>
                ))}
            </Menu>

            <Paper
              variant="outlined"
              sx={getPosOrderDrawerSectionPaperSx(isDark)}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14.5 }}>Order Items</Typography>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>
                  Search item, enter qty, press Enter/Add
                </Typography>
              </Stack>
              <Grid container spacing={1} sx={{ mb: 1.2 }}>
                <Grid item xs={12} sm={8}>
                  <Autocomplete
                    options={data.menu_items}
                    slotProps={drawerOverlays.autocompleteSlotProps}
                    getOptionLabel={(option) => {
                      const hasVariation = Array.isArray(option?.variation_config) && option.variation_config.length > 0;
                      if (hasVariation) {
                        return `${option?.name || 'Menu Item'} (Variation)`;
                      }
                      return `${option?.name || 'Menu Item'} - ${CURRENCY} ${Number(option?.price || 0).toFixed(2)}`;
                    }}
                    value={data.menu_items.find((item) => Number(item.id) === Number(itemEntry.menu_item_id)) || null}
                    onChange={(_, value) => {
                      if (itemEntryErrors.menu_item_id) {
                        setItemEntryErrors((prev) => ({ ...prev, menu_item_id: '' }));
                      }
                      setItemEntry((prev) => ({ ...prev, menu_item_id: value ? String(value.id) : '' }));
                    }}
                    onInputChange={() => {
                      if (itemEntryErrors.menu_item_id) {
                        setItemEntryErrors((prev) => ({ ...prev, menu_item_id: '' }));
                      }
                    }}
                    renderOption={(props, option) => {
                      const imageUrl = String(option?.menu_image_url || option?.menu_image || '').trim();
                      const hasVariation = Array.isArray(option?.variation_config) && option.variation_config.length > 0;
                      const label = hasVariation
                        ? `${option?.name || 'Menu Item'} (Variation)`
                        : `${option?.name || 'Menu Item'} - ${CURRENCY} ${Number(option?.price || 0).toFixed(2)}`;
                      return (
                        <Box
                          component="li"
                          {...props}
                          key={option?.id ?? props.key}
                          sx={{
                            display: 'flex !important',
                            alignItems: 'center',
                            gap: 1.25,
                            py: 0.85,
                          }}
                        >
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1,
                              flexShrink: 0,
                              overflow: 'hidden',
                              bgcolor: isDark ? 'rgba(148,163,184,0.14)' : 'rgba(15,23,42,0.06)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {imageUrl ? (
                              <Box
                                component="img"
                                src={imageUrl}
                                alt=""
                                loading="lazy"
                                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                              />
                            ) : (
                              <RestaurantIcon sx={{ fontSize: 20, color: isDark ? '#94a3b8' : '#64748b' }} />
                            )}
                          </Box>
                          <Typography
                            component="span"
                            sx={{
                              fontSize: 13.5,
                              fontWeight: 650,
                              color: isDark ? '#e2e8f0' : '#0f172a',
                              lineHeight: 1.3,
                            }}
                          >
                            {label}
                          </Typography>
                        </Box>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Search Menu Item"
                        error={!!itemEntryErrors.menu_item_id}
                        helperText={itemEntryErrors.menu_item_id || ''}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddSearchedItem();
                          }
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={8} sm={2}>
                  <Box>
                    <Box
                      sx={{
                        height: 40,
                        width: 'fit-content',
                        maxWidth: '100%',
                        ml: 'auto',
                        border: `1px solid ${isDark ? '#4b5563' : '#cbd5e1'}`,
                        borderRadius: 1.2,
                        backgroundColor: isDark ? 'rgba(15,23,42,0.45)' : '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        overflow: 'hidden',
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => changeEntryQty(-1)}
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 0,
                          color: isDark ? '#e2e8f0' : '#334155',
                          '&:hover': {
                            backgroundColor: isDark ? 'rgba(148,163,184,0.16)' : 'rgba(148,163,184,0.2)',
                          },
                        }}
                      >
                        <RemoveRoundedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <Box
                        sx={{
                          minWidth: 36,
                          px: 1.1,
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 800,
                          borderLeft: `1px solid ${isDark ? '#4b5563' : '#cbd5e1'}`,
                          borderRight: `1px solid ${isDark ? '#4b5563' : '#cbd5e1'}`,
                          color: isDark ? '#e2e8f0' : '#111827',
                        }}
                      >
                        {normalizeQty(itemEntry.quantity)}
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => changeEntryQty(1)}
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 0,
                          color: isDark ? '#e2e8f0' : '#334155',
                          '&:hover': {
                            backgroundColor: isDark ? 'rgba(148,163,184,0.16)' : 'rgba(148,163,184,0.2)',
                          },
                        }}
                      >
                        <AddRoundedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                    {itemEntryErrors.quantity ? (
                      <Typography variant="caption" sx={{ color: '#ef4444', display: 'block', mt: 0.35 }}>
                        {itemEntryErrors.quantity}
                      </Typography>
                    ) : null}
                  </Box>
                </Grid>
                <Grid item xs={4} sm={2}>
                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddSearchedItem}
                    sx={{ textTransform: 'none', height: '40px', backgroundColor: '#4ce1b6', color: '#111', fontWeight: 700 }}
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>
              <Box
                sx={{
                  borderTop: `1px solid ${isDark ? 'rgba(148,163,184,0.28)' : 'rgba(100,116,139,0.22)'}`,
                  mb: 1.05,
                }}
              />
              <Stack spacing={1}>
                {orderForm.order_items.length === 0 ? (
                  <Alert severity="info">No item added yet.</Alert>
                ) : null}
                {orderForm.order_items.map((row, index) => {
                  const selectedMenuItem = menuItemMap.get(Number(row.menu_item));
                  const menuImageUrl = String(
                    selectedMenuItem?.menu_image_url || selectedMenuItem?.menu_image || ''
                  ).trim();
                  const basePrice = Number(selectedMenuItem?.price || 0);
                  const unitPrice = resolveOrderItemUnitPrice(row, basePrice);
                  const quantity = Number(row.quantity || 0);
                  const lineAmount = unitPrice * quantity;
                  const rowVariationId = normalizeVariationId(row?.variation_id);
                  const variationLabel = String(
                    row?.variation_label
                    || (rowVariationId ? variationLabelMap.get(Number(rowVariationId)) : '')
                    || ''
                  ).trim();
                  const variationPrice = Math.max(0, Number(row?.variation_price || 0));
                  return (
                    <Paper
                      key={`restaurant-order-item-${index}`}
                      variant="outlined"
                      sx={{
                        p: 0.95,
                        borderRadius: 1.4,
                        borderColor: isDark ? 'rgba(76,225,182,0.2)' : 'rgba(15,23,42,0.12)',
                        backgroundColor: isDark ? 'rgba(14,18,24,0.35)' : '#fbfdff',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'grid',
                          rowGap: 0.8,
                          columnGap: { xs: 0.8, lg: 0.85 },
                          alignItems: 'center',
                          gridTemplateColumns: {
                            xs: '32px 40px minmax(0,1.2fr) minmax(0,1fr)',
                            lg: '32px 40px minmax(0,1.4fr) minmax(190px,0.95fr) minmax(0,1.4fr)',
                          },
                        }}
                      >
                        <IconButton
                          onClick={() => removeOrderItem(index)}
                          size="small"
                          sx={{
                            border: `1px solid ${isDark ? '#45455a' : '#d5d9df'}`,
                            borderRadius: 1,
                            flexShrink: 0,
                            gridRow: { xs: '1 / span 2', lg: 'auto' },
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>

                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1,
                            flexShrink: 0,
                            overflow: 'hidden',
                            bgcolor: isDark ? 'rgba(148,163,184,0.14)' : 'rgba(15,23,42,0.06)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gridRow: { xs: '1 / span 2', lg: 'auto' },
                          }}
                        >
                          {menuImageUrl ? (
                            <Box
                              component="img"
                              src={menuImageUrl}
                              alt=""
                              loading="lazy"
                              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                          ) : (
                            <RestaurantIcon sx={{ fontSize: 18, color: isDark ? '#94a3b8' : '#64748b' }} />
                          )}
                        </Box>

                        <Box sx={{ minWidth: 0, gridColumn: { xs: '3 / -1', lg: 'auto' } }}>
                          <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                            {selectedMenuItem?.name || 'Menu Item'}
                          </Typography>
                          <Stack direction="row" spacing={0.65} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                            {variationLabel ? (
                              <Chip
                                size="small"
                                label={
                                  variationPrice > 0
                                    ? `${variationLabel} (${CURRENCY} ${variationPrice.toFixed(2)})`
                                    : variationLabel
                                }
                                sx={{
                                  height: 22,
                                  fontWeight: 700,
                                  bgcolor: isDark ? 'rgba(76,225,182,0.2)' : 'rgba(76,225,182,0.16)',
                                  color: isDark ? '#86efac' : '#166534',
                                }}
                              />
                            ) : null}
                            <Chip
                              size="small"
                              label={`${quantity} × ${unitPrice.toFixed(2)} = ${lineAmount.toFixed(2)} ${CURRENCY}`}
                              sx={{
                                height: 22,
                                fontWeight: 800,
                                bgcolor: isDark ? 'rgba(251,191,36,0.2)' : 'rgba(251,191,36,0.18)',
                                color: isDark ? '#fde68a' : '#92400e',
                              }}
                            />
                          </Stack>
                        </Box>

                        <Box
                          sx={{
                            gridColumn: { xs: '3 / 4', lg: 'auto' },
                            gridRow: { xs: 2, lg: 'auto' },
                          }}
                        >
                          <Box
                            sx={{
                              height: 40,
                              width: 'fit-content',
                              maxWidth: '100%',
                              ml: 'auto',
                              border: `1px solid ${isDark ? '#4b5563' : '#cbd5e1'}`,
                              borderRadius: 1.2,
                              backgroundColor: isDark ? 'rgba(15,23,42,0.45)' : '#ffffff',
                              display: 'flex',
                              alignItems: 'center',
                              overflow: 'hidden',
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => changeRowQty(index, -1)}
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 0,
                                color: isDark ? '#e2e8f0' : '#334155',
                                '&:hover': {
                                  backgroundColor: isDark ? 'rgba(148,163,184,0.16)' : 'rgba(148,163,184,0.2)',
                                },
                              }}
                            >
                              <RemoveRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                            <Box
                              sx={{
                                minWidth: 36,
                                px: 1.1,
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 800,
                                borderLeft: `1px solid ${isDark ? '#4b5563' : '#cbd5e1'}`,
                                borderRight: `1px solid ${isDark ? '#4b5563' : '#cbd5e1'}`,
                                color: isDark ? '#e2e8f0' : '#111827',
                              }}
                            >
                              {normalizeQty(row.quantity)}
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => changeRowQty(index, 1)}
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 0,
                                color: isDark ? '#e2e8f0' : '#334155',
                                '&:hover': {
                                  backgroundColor: isDark ? 'rgba(148,163,184,0.16)' : 'rgba(148,163,184,0.2)',
                                },
                              }}
                            >
                              <AddRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Box>
                        </Box>

                        <TextField
                          fullWidth
                          size="small"
                          label="Special Note"
                          value={row.note || ''}
                          onChange={(e) => updateOrderItem(index, { note: e.target.value })}
                          sx={{
                            gridColumn: { xs: '4 / 5', lg: 'auto' },
                            gridRow: { xs: 2, lg: 'auto' },
                            ml: { xs: 0, lg: 0.7 },
                            '& .MuiInputBase-root': {
                              minHeight: 44,
                            },
                          }}
                        />
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            </Paper>
            {!isEmbeddedEditMode ? (
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Order Note"
                value={orderForm.notes}
                onChange={(e) => setOrderForm((prev) => ({ ...prev, notes: e.target.value }))}
                sx={{ mb: 1.5 }}
              />
            ) : null}

            {isEmbeddedEditMode ? (
              <>
                <Grid container spacing={1.2} sx={{ mb: 1.5 }}>
                  <Grid item xs={12} md={6}>
                    <Paper
                      variant="outlined"
                      sx={getPosOrderDrawerSectionPaperSx(isDark, { mb: 0, height: '100%' })}
                    >
                      <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>Discount</Typography>
                      <Stack spacing={1}>
                        <ToggleButtonGroup
                          exclusive
                          fullWidth
                          value={billingAdjustments.discount_type}
                          onChange={(_, value) => {
                            if (!value) return;
                            setBillingAdjustments((prev) => ({
                              ...prev,
                              discount_type: value,
                              discount_value: '',
                            }));
                          }}
                          sx={{
                            border: `1px solid ${isDark ? '#4b5563' : '#cbd5e1'}`,
                            borderRadius: 1.1,
                            overflow: 'hidden',
                            '& .MuiToggleButton-root': {
                              flex: 1,
                              textTransform: 'none',
                              fontWeight: 700,
                              borderColor: 'transparent',
                              color: isDark ? '#e5e7eb' : '#334155',
                              backgroundColor: isDark ? 'rgba(31,41,55,0.62)' : '#ffffff',
                              px: 1.45,
                              py: 0.35,
                              minHeight: 34,
                            },
                            '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
                              marginLeft: 0,
                              borderLeft: `1px solid ${isDark ? '#4b5563' : '#cbd5e1'}`,
                            },
                            '& .Mui-selected': {
                              backgroundColor: '#0f766e !important',
                              color: '#ecfeff !important',
                              borderColor: '#2dd4bf !important',
                            },
                          }}
                        >
                          <ToggleButton value="percentage">Percentage (%)</ToggleButton>
                          <ToggleButton value="amount">Amount ({CURRENCY})</ToggleButton>
                        </ToggleButtonGroup>

                        <Box
                          sx={{
                            border: `1px solid ${isDark ? '#3c3f52' : '#d7dee6'}`,
                            borderRadius: 1.5,
                            p: 0.8,
                            background: isDark
                              ? 'linear-gradient(120deg, rgba(76,225,182,0.12) 0%, rgba(76,225,182,0.04) 100%)'
                              : 'linear-gradient(120deg, rgba(76,225,182,0.14) 0%, rgba(76,225,182,0.05) 100%)',
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.6 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleDiscountStep('dec')}
                              disabled={discountSliderValue <= 0}
                              sx={{ border: `1px solid ${isDark ? '#475569' : '#cbd5e1'}`, borderRadius: 1 }}
                            >
                              <RemoveRoundedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                                {billingAdjustments.discount_type === 'percentage' ? 'Discount %' : `Discount ${CURRENCY}`}
                              </Typography>
                              <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                                {billingAdjustments.discount_type === 'percentage'
                                  ? `${discountSliderValue.toFixed(0)}%`
                                  : `${CURRENCY} ${discountSliderValue.toFixed(2)}`}
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => handleDiscountStep('inc')}
                              disabled={discountSliderValue >= discountSliderMax}
                              sx={{ border: `1px solid ${isDark ? '#475569' : '#cbd5e1'}`, borderRadius: 1 }}
                            >
                              <AddRoundedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Stack>
                          <Slider
                            value={discountSliderValue}
                            min={0}
                            max={discountSliderMax}
                            step={discountSliderStep}
                            onChange={handleDiscountSliderChange}
                            disabled={discountSliderMax <= 0}
                            sx={{ color: BOOKING_DRAWER_ACCENT, py: 0.5, width: '100%' }}
                          />
                          <LinearProgress
                            variant="determinate"
                            value={discountProgressPercent}
                            sx={{
                              mt: 0.4,
                              height: 7,
                              width: '100%',
                              borderRadius: 999,
                              backgroundColor: isDark ? 'rgba(100,116,139,0.28)' : 'rgba(148,163,184,0.25)',
                              '& .MuiLinearProgress-bar': {
                                background: 'linear-gradient(90deg, #4ce1b6 0%, #22c55e 100%)',
                              },
                            }}
                          />
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper
                      variant="outlined"
                      sx={getPosOrderDrawerSectionPaperSx(isDark, { mb: 0, height: '100%' })}
                    >
                      <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>Taxes</Typography>
                      <Typography variant="caption" sx={{ display: 'block', mb: 1, opacity: 0.74 }}>
                        Auto-loaded from Finance Setup (Food & Beverage)
                      </Typography>
                      <Stack spacing={0.9}>
                        {billingAdjustments.applied_taxes.length === 0 ? (
                          <Alert severity="info" sx={{ py: 0 }}>
                            No tax selected for this order.
                          </Alert>
                        ) : billingAdjustments.applied_taxes.map((taxRow) => {
                          const configuredValueLabel = taxRow.mode === 'percentage'
                            ? `${Number(taxRow.value || 0).toFixed(2)}%`
                            : `${CURRENCY} ${Number(taxRow.value || 0).toFixed(2)}`;
                          return (
                            <Box
                              key={taxRow.tax_key}
                              sx={{
                                border: `1px solid ${isDark ? '#3a3e52' : '#d6dbe3'}`,
                                borderRadius: 1.3,
                                p: 0.8,
                                backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : '#fafcff',
                                opacity: taxRow.enabled ? 1 : 0.74,
                              }}
                            >
                              <Stack direction="row" spacing={1} alignItems="center">
                                <IconButton
                                  onClick={() => toggleAppliedTax(taxRow.tax_key, !taxRow.enabled)}
                                  size="small"
                                  sx={{
                                    border: `1px solid ${isDark ? '#45455a' : '#d5d9df'}`,
                                    borderRadius: 1,
                                    color: taxRow.enabled ? (isDark ? '#f1f5f9' : '#1f2937') : '#10b981',
                                    backgroundColor: taxRow.enabled
                                      ? 'transparent'
                                      : (isDark ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.12)'),
                                  }}
                                >
                                  {taxRow.enabled ? <CloseIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                                </IconButton>
                                <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 90 }}>
                                  {taxRow.name}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700, ml: 'auto' }}>
                                  {configuredValueLabel}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 800, color: BOOKING_DRAWER_ACCENT, minWidth: 92, textAlign: 'right' }}>
                                  {CURRENCY} {Number(configuredTaxAmountByKey.get(taxRow.tax_key) || 0).toFixed(2)}
                                </Typography>
                              </Stack>
                            </Box>
                          );
                        })}
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    mb: 1.5,
                    display: 'grid',
                    gap: 1.2,
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    alignItems: 'start',
                  }}
                >
                  <Stack spacing={1.2}>
                    <Paper
                      variant="outlined"
                      sx={getPosOrderDrawerSectionPaperSx(isDark, { mb: 0, p: 1.3 })}
                    >
                      <Typography sx={{ fontWeight: 700, mb: 0.7, fontSize: 13.5 }}>
                        Payment Collection
                      </Typography>
                      <ToggleButtonGroup
                        exclusive
                        fullWidth
                        size="small"
                        value={orderMeta.payment_collection_mode}
                        onChange={(_, value) => {
                          if (!value || zeroAmountFullDiscountSettlement) return;
                          setOrderMeta((prev) => ({
                            ...prev,
                            payment_collection_mode: value,
                            partial_paid_amount: '',
                          }));
                          setPaymentValidationError('');
                        }}
                        sx={{
                          mb: 1.05,
                          border: `1px solid ${isDark ? '#4b5563' : '#cbd5e1'}`,
                          borderRadius: 1.1,
                          overflow: 'hidden',
                          '& .MuiToggleButton-root': {
                            textTransform: 'none',
                            fontWeight: 700,
                            borderColor: 'transparent',
                            color: isDark ? '#e5e7eb' : '#334155',
                            backgroundColor: isDark ? 'rgba(31,41,55,0.62)' : '#ffffff',
                          },
                          '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
                            marginLeft: 0,
                            borderLeft: `1px solid ${isDark ? '#4b5563' : '#cbd5e1'}`,
                          },
                          '& .Mui-selected': {
                            backgroundColor: '#0f766e !important',
                            color: '#ecfeff !important',
                            borderColor: '#2dd4bf !important',
                          },
                        }}
                      >
                        <ToggleButton value="collect_now">Collect Now</ToggleButton>
                        <ToggleButton value="partial" disabled={zeroAmountFullDiscountSettlement}>Partial</ToggleButton>
                        <ToggleButton value="due" disabled={zeroAmountFullDiscountSettlement}>Due</ToggleButton>
                      </ToggleButtonGroup>
                      {orderMeta.payment_collection_mode === 'partial' ? (
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label={`Paid Now (${CURRENCY})`}
                          value={orderMeta.partial_paid_amount}
                          error={Boolean(partialPaidNowInlineError)}
                          helperText={partialPaidNowInlineError || ''}
                          onChange={(event) => {
                            setOrderMeta((prev) => ({ ...prev, partial_paid_amount: event.target.value }));
                            setPaymentValidationError('');
                          }}
                          inputProps={{ min: 0, step: '0.01', max: Number(partialPaymentBaseAmount.toFixed(2)) }}
                          sx={{ mb: 1.05 }}
                        />
                      ) : null}
                      <Typography sx={{ fontWeight: 700, mb: 0.9, fontSize: 13.5 }}>
                        Payment Method {orderMeta.payment_collection_mode === 'due' ? '(Disabled for due)' : zeroAmountFullDiscountSettlement ? '(Auto)' : '*'}
                      </Typography>
                      {zeroAmountFullDiscountSettlement ? (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mb: 0.8,
                            fontWeight: 600,
                            color: isDark ? '#9cead6' : '#0f766e',
                          }}
                        >
                          100% discount with zero payable — {COMPANY_SETTLEMENT_PAYMENT_METHOD} applied automatically.
                        </Typography>
                      ) : null}
                      <Stack
                        direction="row"
                        spacing={0.7}
                        useFlexGap
                        flexWrap="wrap"
                        sx={{ opacity: orderMeta.payment_collection_mode === 'due' || zeroAmountFullDiscountSettlement ? 0.72 : 1 }}
                      >
                        {PAYMENT_METHOD_OPTIONS.map((method) => {
                          const selected = resolvedPaymentMethod === method;
                          const disableMethodSelection = orderMeta.payment_collection_mode === 'due'
                            || zeroAmountFullDiscountSettlement;
                          return (
                            <Chip
                              key={method}
                              label={method}
                              clickable={!disableMethodSelection}
                              disabled={disableMethodSelection}
                              onClick={() => {
                                if (disableMethodSelection) return;
                                setOrderMeta((prev) => ({ ...prev, payment_method: method }));
                                setPaymentValidationError('');
                              }}
                              size="medium"
                              sx={{
                                borderRadius: 999,
                                px: 0.55,
                                height: 34,
                                fontWeight: 700,
                                '& .MuiChip-label': { px: 1.15, fontSize: '0.92rem' },
                                border: `1px solid ${selected ? '#4ce1b6' : (isDark ? '#3a4455' : '#c9d2dc')}`,
                                backgroundColor: selected
                                  ? (isDark ? 'rgba(76,225,182,0.24)' : 'rgba(76,225,182,0.16)')
                                  : 'transparent',
                                color: selected
                                  ? (isDark ? '#9cead6' : '#0f766e')
                                  : (isDark ? '#d0d7df' : '#334155'),
                              }}
                            />
                          );
                        })}
                      </Stack>
                      {orderMeta.payment_collection_mode === 'partial' && partialPaymentMethodInlineError ? (
                        <FormHelperText error sx={{ mt: 0.7, ml: 0.1, fontWeight: 600 }}>
                          {partialPaymentMethodInlineError}
                        </FormHelperText>
                      ) : null}
                      {orderMeta.payment_collection_mode === 'partial' ? (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 0.8,
                            fontWeight: 700,
                            color: partialDueNowAmount > 0 ? (isDark ? '#facc15' : '#a16207') : (isDark ? '#9ca3af' : '#64748b'),
                          }}
                        >
                          Remaining Due: {CURRENCY} {partialDueNowAmount.toFixed(2)}
                        </Typography>
                      ) : null}
                      {paymentValidationError && !suppressPartialGlobalValidationError ? (
                        <Alert
                          severity="error"
                          sx={{
                            mt: 1,
                            py: 0,
                            '& .MuiAlert-message': {
                              fontWeight: 600,
                            },
                          }}
                        >
                          {paymentValidationError}
                        </Alert>
                      ) : null}
                    </Paper>
                  </Stack>
                  <Stack spacing={1.2}>
                    <Paper
                      variant="outlined"
                      sx={getPosOrderDrawerSectionPaperSx(isDark, { mb: 0, p: 1.4 })}
                    >
                      <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1.7 }}>Additional Charges</Typography>
                      <Grid container columnSpacing={1.6} rowSpacing={1.4} sx={{ mt: 0.35 }}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label={`Delivery Charge (${CURRENCY})`}
                            InputLabelProps={{ shrink: true }}
                            value={billingAdjustments.delivery_charge}
                            onChange={(e) => setBillingAdjustments((prev) => ({ ...prev, delivery_charge: e.target.value }))}
                            inputProps={{ min: 0, step: '0.01' }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end" sx={{ mr: -0.7 }}>
                                  <Stack spacing={0.2}>
                                    <IconButton
                                      size="small"
                                      onClick={() => changeChargeValue('delivery_charge', 1)}
                                      sx={{
                                        width: 18,
                                        height: 14,
                                        border: `1px solid ${isDark ? '#495162' : '#d1d5db'}`,
                                        borderRadius: 0.7,
                                      }}
                                    >
                                      <KeyboardArrowUpRoundedIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => changeChargeValue('delivery_charge', -1)}
                                      sx={{
                                        width: 18,
                                        height: 14,
                                        border: `1px solid ${isDark ? '#495162' : '#d1d5db'}`,
                                        borderRadius: 0.7,
                                      }}
                                    >
                                      <KeyboardArrowDownRoundedIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Stack>
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiInputLabel-root': {
                                backgroundColor: isDark ? drawerTheme.surface : '#fff',
                                px: 0.35,
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label={`Container Charge (${CURRENCY})`}
                            InputLabelProps={{ shrink: true }}
                            value={billingAdjustments.container_charge}
                            onChange={(e) => setBillingAdjustments((prev) => ({ ...prev, container_charge: e.target.value }))}
                            inputProps={{ min: 0, step: '0.01' }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end" sx={{ mr: -0.7 }}>
                                  <Stack spacing={0.2}>
                                    <IconButton
                                      size="small"
                                      onClick={() => changeChargeValue('container_charge', 1)}
                                      sx={{
                                        width: 18,
                                        height: 14,
                                        border: `1px solid ${isDark ? '#495162' : '#d1d5db'}`,
                                        borderRadius: 0.7,
                                      }}
                                    >
                                      <KeyboardArrowUpRoundedIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => changeChargeValue('container_charge', -1)}
                                      sx={{
                                        width: 18,
                                        height: 14,
                                        border: `1px solid ${isDark ? '#495162' : '#d1d5db'}`,
                                        borderRadius: 0.7,
                                      }}
                                    >
                                      <KeyboardArrowDownRoundedIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Stack>
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiInputLabel-root': {
                                backgroundColor: isDark ? drawerTheme.surface : '#fff',
                                px: 0.35,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                    <Paper
                      variant="outlined"
                      sx={getPosOrderDrawerSummaryPaperSx(isDark)}
                    >
                      <Typography sx={{ fontWeight: 800, mb: 1, color: BOOKING_DRAWER_ACCENT }}>Billing Summary</Typography>
                      <Stack spacing={0.7}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" sx={{ opacity: 0.82 }}>Subtotal</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{CURRENCY} {estimatedSubtotal.toFixed(2)}</Typography>
                        </Stack>
                        {discountAmount > 0 ? (
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" sx={{ opacity: 0.82 }}>Discount</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>-{CURRENCY} {discountAmount.toFixed(2)}</Typography>
                          </Stack>
                        ) : null}
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" sx={{ opacity: 0.82 }}>Taxable Subtotal</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{CURRENCY} {taxableSubtotal.toFixed(2)}</Typography>
                        </Stack>
                        {configuredTaxBreakdown.map((taxRow) => (
                          <Stack
                            key={`edit-billing-tax-${taxRow.tax_key}`}
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography variant="body2" sx={{ opacity: 0.82 }}>
                              {taxRow.name}
                              {taxRow.mode === 'percentage' ? ` (${Number(taxRow.numericValue || 0).toFixed(2)}%)` : ''}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {CURRENCY} {Number(taxRow.amount || 0).toFixed(2)}
                            </Typography>
                          </Stack>
                        ))}
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" sx={{ opacity: 0.82 }}>Total Tax</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{CURRENCY} {estimatedTax.toFixed(2)}</Typography>
                        </Stack>
                        {deliveryChargeAmount > 0 ? (
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" sx={{ opacity: 0.82 }}>Delivery Charge</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{CURRENCY} {deliveryChargeAmount.toFixed(2)}</Typography>
                          </Stack>
                        ) : null}
                        {containerChargeAmount > 0 ? (
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" sx={{ opacity: 0.82 }}>Container Charge</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{CURRENCY} {containerChargeAmount.toFixed(2)}</Typography>
                          </Stack>
                        ) : null}
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" sx={{ opacity: 0.82 }}>Previous Billed Total</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{CURRENCY} {originalOrderTotalAmount.toFixed(2)}</Typography>
                        </Stack>
                        <Box sx={{ mt: 0.4, pt: 0.8, borderTop: `1px dashed ${isDark ? 'rgba(148,163,184,0.35)' : 'rgba(100,116,139,0.35)'}` }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontWeight: 800 }}>Total</Typography>
                            <Typography sx={{ fontWeight: 900, color: BOOKING_DRAWER_ACCENT }}>
                              {CURRENCY} {estimatedTotal.toFixed(2)}
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.45 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: editSettlementDirection === 'collect'
                                  ? (isDark ? '#facc15' : '#a16207')
                                  : (editSettlementDirection === 'return'
                                    ? (isDark ? '#c4b5fd' : '#6d28d9')
                                    : (isDark ? '#9ca3af' : '#475569')),
                              }}
                            >
                              {editSettlementDirection === 'collect'
                                ? (isEditRemainingDueMode ? 'Due Remaining' : 'Collect Remaining')
                                : (editSettlementDirection === 'return' ? 'Return Amount' : 'Payment Adjustment')}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 900,
                                color: editSettlementDirection === 'collect'
                                  ? (isDark ? '#facc15' : '#a16207')
                                  : (editSettlementDirection === 'return'
                                    ? (isDark ? '#c4b5fd' : '#6d28d9')
                                    : (isDark ? '#9ca3af' : '#475569')),
                              }}
                            >
                              {editSettlementDirection === 'no_change'
                                ? `${CURRENCY} 0.00`
                                : `${CURRENCY} ${editSettlementAmount.toFixed(2)}`}
                            </Typography>
                          </Stack>
                          {orderMeta.payment_collection_mode === 'partial' ? (
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.45 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? '#cbd5e1' : '#475569' }}>
                                Paid Now
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 900, color: isDark ? '#cbd5e1' : '#475569' }}>
                                {CURRENCY} {partialPaidNowAmount.toFixed(2)}
                              </Typography>
                            </Stack>
                          ) : null}
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.45 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color:
                                  editPaymentStatusSummaryLabel === 'Partial'
                                    ? (isDark ? '#facc15' : '#a16207')
                                    : (isDark ? '#cbd5e1' : '#475569'),
                              }}
                            >
                              {editDueSummaryLabel}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 900,
                                color:
                                  editPaymentStatusSummaryLabel === 'Partial'
                                    ? (isDark ? '#facc15' : '#a16207')
                                    : (isDark ? '#cbd5e1' : '#475569'),
                              }}
                            >
                              {CURRENCY} {editDueSummaryAmount.toFixed(2)}
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.45 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color:
                                  editPaymentStatusSummaryLabel === 'Partial'
                                    ? (isDark ? '#facc15' : '#a16207')
                                    : (isDark ? '#cbd5e1' : '#475569'),
                              }}
                            >
                              Payment Status
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 900,
                                color:
                                  editPaymentStatusSummaryLabel === 'Partial'
                                    ? (isDark ? '#facc15' : '#a16207')
                                    : (isDark ? '#cbd5e1' : '#475569'),
                              }}
                            >
                              {editPaymentStatusSummaryLabel}
                            </Typography>
                          </Stack>
                        </Box>
                      </Stack>
                    </Paper>
                  </Stack>
                </Box>
              </>
            ) : null}
          </Box>
          {variationSelectionDialog.open ? (() => {
            const menuItemId = Number(variationSelectionDialog.menu_item_id || 0);
            const selectedMenuItem = menuItemMap.get(menuItemId);
            const basePrice = Number(selectedMenuItem?.price || 0);
            const selectedVariation = (variationSelectionDialog.options || []).find(
              (option) => Number(option.variation_id) === Number(variationSelectionDialog.selected_variation_id)
            );
            const selectedVariationPrice = Number(selectedVariation?.price || 0);
            const effectiveUnitPrice = Number.isFinite(selectedVariationPrice) ? selectedVariationPrice : basePrice;
            return (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 9,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 2,
                  bgcolor: isDark ? 'rgba(7,9,13,0.62)' : 'rgba(15,23,42,0.28)',
                  backdropFilter: 'blur(2px)',
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    width: '100%',
                    maxWidth: 420,
                    p: 2,
                    borderRadius: 2.2,
                    border: `1px solid ${isDark ? 'rgba(76,225,182,0.34)' : 'rgba(76,225,182,0.28)'}`,
                    background: isDark
                      ? 'linear-gradient(165deg, rgba(34,34,40,0.98) 0%, rgba(24,24,30,0.98) 100%)'
                      : 'linear-gradient(165deg, #ffffff 0%, #f3fffb 100%)',
                  }}
                >
                  <Typography sx={{ fontWeight: 800, mb: 0.6 }}>
                    Select Variation
                  </Typography>
                  <Typography variant="body2" sx={{ color: isDark ? '#b8c3d4' : '#4b5a70', mb: 1.2 }}>
                    {selectedMenuItem?.name || 'Menu Item'} x {Math.max(1, Number(variationSelectionDialog.quantity || 1))}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.65 }}>
                    Choose Variation
                  </Typography>
                  <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                    {(variationSelectionDialog.options || []).map((option) => {
                      const isSelected = String(variationSelectionDialog.selected_variation_id) === String(option.variation_id);
                      return (
                        <Chip
                          key={`row-variation-${option.variation_id}`}
                          clickable
                          label={
                            Number(option.price || 0) > 0
                              ? `${option.label} (${CURRENCY} ${Number(option.price || 0).toFixed(2)})`
                              : option.label
                          }
                          onClick={() =>
                            setVariationSelectionDialog((prev) => ({
                              ...prev,
                              selected_variation_id: String(option.variation_id),
                            }))
                          }
                          variant={isSelected ? 'filled' : 'outlined'}
                          sx={{
                            fontWeight: 700,
                            borderColor: isSelected
                              ? (isDark ? 'rgba(76,225,182,0.82)' : 'rgba(16,185,129,0.82)')
                              : (isDark ? 'rgba(148,163,184,0.42)' : 'rgba(100,116,139,0.38)'),
                            bgcolor: isSelected
                              ? (isDark ? 'rgba(76,225,182,0.24)' : 'rgba(16,185,129,0.14)')
                              : 'transparent',
                            color: isSelected
                              ? (isDark ? '#b8fff0' : '#065f46')
                              : (isDark ? '#e2e8f0' : '#334155'),
                            '& .MuiChip-label': {
                              px: 1.05,
                            },
                          }}
                        />
                      );
                    })}
                  </Stack>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 0.9,
                      color: isDark ? '#9fc2ba' : '#136f5b',
                      fontWeight: 700,
                    }}
                  >
                    Final rate: {CURRENCY} {effectiveUnitPrice.toFixed(2)}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={closeVariationSelectionDialog}
                      disabled={submitting}
                      sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleConfirmVariationSelection}
                      disabled={submitting || !variationSelectionDialog.selected_variation_id}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 800,
                        background: 'linear-gradient(90deg, #4ce1b6 0%, #2dd4a0 100%)',
                        color: '#0b1f18',
                        '&:hover': {
                          background: 'linear-gradient(90deg, #3dd1a8 0%, #22c997 100%)',
                        },
                      }}
                    >
                      Add Item
                    </Button>
                  </Stack>
                </Paper>
              </Box>
            );
          })() : null}
          {kotConfirmDialog.open ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                bgcolor: isDark ? 'rgba(7,9,13,0.62)' : 'rgba(15,23,42,0.28)',
                backdropFilter: 'blur(2px)',
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  maxWidth: 380,
                  p: 2,
                  borderRadius: 2.2,
                  border: `1px solid ${isDark ? 'rgba(76,225,182,0.34)' : 'rgba(76,225,182,0.28)'}`,
                  background: isDark
                    ? 'linear-gradient(165deg, rgba(34,34,40,0.98) 0%, rgba(24,24,30,0.98) 100%)'
                    : 'linear-gradient(165deg, #ffffff 0%, #f3fffb 100%)',
                }}
              >
                <Stack direction="row" spacing={0.9} alignItems="center" sx={{ mb: 1 }}>
                  <AccessTimeIcon sx={{ color: BOOKING_DRAWER_ACCENT }} />
                  <Typography sx={{ fontWeight: 800 }}>
                    {kotConfirmDialog.actionType === 'kot_print' ? 'Confirm KOT & Print' : 'Confirm KOT'}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: isDark ? '#b8c3d4' : '#4b5a70', mb: 1.2 }}>
                  {editingOrder
                    ? 'Select preparation time. Active timer will be extended, otherwise a new timer cycle will start.'
                    : 'Select preparation time. Queue urgency and criticality will be calculated based on this SLA.'}
                </Typography>
                {editingOrder ? (() => {
                  const activeTimer = normalizeQueueTimers(editingOrder?.queue_timers)
                    .find((timer) => !timer.ended_at);
                  if (activeTimer) {
                    return (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mb: 1,
                          color: isDark ? '#9fc2ba' : '#136f5b',
                          fontWeight: 700,
                        }}
                      >
                        Current SLA {Math.max(10, Number(activeTimer.target_minutes || 30))}m
                        {' '}+{' '}
                        {Number(kotConfirmDialog.preparationTimeMinutes || 0)}m
                        {' '}=
                        {' '}
                        {Math.min(240, Math.max(10, Number(activeTimer.target_minutes || 30))
                          + Number(kotConfirmDialog.preparationTimeMinutes || 0))}m
                      </Typography>
                    );
                  }
                  return (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mb: 1,
                        color: isDark ? '#9fc2ba' : '#136f5b',
                        fontWeight: 700,
                      }}
                    >
                      Previous timer served. New timer SLA will start at{' '}
                      {Number(kotConfirmDialog.preparationTimeMinutes || 0)}m.
                    </Typography>
                  );
                })() : null}
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Preparation Time (minutes)"
                  value={kotConfirmDialog.preparationTimeMinutes}
                  SelectProps={{ MenuProps: drawerOverlays.selectMenuProps }}
                  onChange={(event) =>
                    setKotConfirmDialog((prev) => ({
                      ...prev,
                      preparationTimeMinutes: Number(event.target.value || 30),
                    }))
                  }
                >
                  {KOT_PREPARATION_TIME_OPTIONS.map((minutes) => (
                    <MenuItem key={`kot-time-${minutes}`} value={minutes}>
                      {minutes} minutes
                    </MenuItem>
                  ))}
                </TextField>
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={closeKotConfirmDialog}
                    disabled={submitting}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={confirmKotAndSubmit}
                    disabled={submitting}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      background: 'linear-gradient(90deg, #4ce1b6 0%, #2dd4a0 100%)',
                      color: '#0b1f18',
                      '&:hover': {
                        background: 'linear-gradient(90deg, #3dd1a8 0%, #22c997 100%)',
                      },
                    }}
                  >
                    {kotConfirmDialog.actionType === 'kot_print' ? 'Confirm KOT & Print' : 'Confirm KOT'}
                  </Button>
                </Stack>
              </Paper>
            </Box>
          ) : null}
          {cancelDialog.open ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 7,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                bgcolor: isDark ? 'rgba(7,9,13,0.66)' : 'rgba(15,23,42,0.32)',
                backdropFilter: 'blur(2px)',
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  maxWidth: 420,
                  p: 2,
                  borderRadius: 2.2,
                  border: `1px solid ${isDark ? 'rgba(239,68,68,0.35)' : 'rgba(239,68,68,0.26)'}`,
                  background: isDark
                    ? 'linear-gradient(165deg, rgba(34,34,40,0.98) 0%, rgba(24,24,30,0.98) 100%)'
                    : 'linear-gradient(165deg, #ffffff 0%, #fff5f5 100%)',
                }}
              >
                <Typography sx={{ fontWeight: 800, mb: 1.1 }}>
                  Cancel Order
                </Typography>
                <Typography variant="body2" sx={{ color: isDark ? '#b8c3d4' : '#4b5a70', mb: 1.2 }}>
                  Please provide cancellation reason before submitting.
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  label="Reason"
                  value={cancelDialog.reason}
                  error={Boolean(cancelDialog.error)}
                  helperText={cancelDialog.error || ''}
                  onChange={(event) =>
                    setCancelDialog((prev) => ({
                      ...prev,
                      reason: event.target.value,
                      error: '',
                    }))
                  }
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={closeCancelDialog}
                    disabled={submitting}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Close
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={confirmCancelOrder}
                    disabled={submitting}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      backgroundColor: isDark ? '#dc2626' : '#ef4444',
                      color: '#ffffff',
                      '&:hover': {
                        backgroundColor: isDark ? '#dc2626' : '#dc2626',
                      },
                    }}
                  >
                    Submit
                  </Button>
                </Stack>
              </Paper>
            </Box>
          ) : null}
          {isEmbeddedEditMode && updateReasonDialog.open ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                bgcolor: isDark ? 'rgba(7,9,13,0.66)' : 'rgba(15,23,42,0.32)',
                backdropFilter: 'blur(2px)',
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  maxWidth: 460,
                  p: 2,
                  borderRadius: 2.2,
                  border: `1px solid ${isDark ? 'rgba(20,184,166,0.35)' : 'rgba(20,184,166,0.26)'}`,
                  background: isDark
                    ? 'linear-gradient(165deg, rgba(34,34,40,0.98) 0%, rgba(24,24,30,0.98) 100%)'
                    : 'linear-gradient(165deg, #ffffff 0%, #f0fdfa 100%)',
                }}
              >
                <Typography sx={{ fontWeight: 800, mb: 1.1 }}>
                  Confirm Order Update
                </Typography>
                <Typography variant="body2" sx={{ color: isDark ? '#b8c3d4' : '#4b5a70', mb: 1.2 }}>
                  Please provide a reason for this update. It will be captured in the audit log.
                </Typography>
                <Alert
                  severity={editSettlementDirection === 'return' ? 'info' : (editSettlementDirection === 'collect' ? 'warning' : 'success')}
                  sx={{ mb: 1.2 }}
                >
                  {isEditRemainingDueMode
                    ? `Remaining ${CURRENCY} ${editSettlementAmount.toFixed(2)} will be marked as due. Payment status will be Partial.`
                    : (orderMeta.payment_collection_mode === 'due'
                      ? `Due amount will be ${CURRENCY} ${editDueAmount.toFixed(2)}.`
                      : (isEditCollectNowOutstandingMode
                        ? `Collect due amount ${CURRENCY} ${editDueSummaryAmount.toFixed(2)} now. Payment status will be Paid.`
                      : (editSettlementDirection === 'collect'
                        ? `Collect remaining ${CURRENCY} ${editSettlementAmount.toFixed(2)} based on revised total.`
                        : (editSettlementDirection === 'return'
                          ? `Return ${CURRENCY} ${editSettlementAmount.toFixed(2)} based on revised total.`
                          : 'No payment adjustment required.'))))}
                </Alert>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  label="Update Reason"
                  value={updateReasonDialog.reason}
                  error={Boolean(updateReasonDialog.error)}
                  helperText={updateReasonDialog.error || ''}
                  onChange={(event) =>
                    setUpdateReasonDialog((prev) => ({
                      ...prev,
                      reason: event.target.value,
                      error: '',
                    }))
                  }
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={closeUpdateReasonDialog}
                    disabled={submitting}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Close
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={confirmUpdateOrder}
                    disabled={submitting}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      backgroundColor: isDark ? '#0f766e' : '#14b8a6',
                      color: '#ffffff',
                      '&:hover': {
                        backgroundColor: isDark ? '#0f766e' : '#0f766e',
                      },
                    }}
                  >
                    {submitting ? 'Updating...' : 'Confirm Update'}
                  </Button>
                </Stack>
              </Paper>
            </Box>
          ) : null}
          <Box
            sx={{
              p: 1.5,
              ...getBookingDrawerFooterSx(isDark),
            }}
          >
            {isEmbeddedEditMode ? (
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={openUpdateReasonDialog}
                    disabled={submitting}
                    sx={{
                      ...footerActionButtonSx,
                      backgroundColor: isDark ? '#0f766e' : '#14b8a6',
                      borderColor: isDark ? '#14b8a6' : '#0f766e',
                      color: '#ffffff',
                      '&:hover': {
                        backgroundColor: isDark ? '#0f766e' : '#0f766e',
                      },
                    }}
                  >
                    {submitting ? 'Updating...' : 'Update'}
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={openCancelDialog}
                    disabled={submitting || !editingOrder?.id}
                    sx={{
                      ...footerActionButtonSx,
                      backgroundColor: isDark ? '#dc2626' : '#ef4444',
                      borderColor: isDark ? '#b91c1c' : '#dc2626',
                      color: '#ffffff',
                      '&:hover': {
                        backgroundColor: isDark ? '#dc2626' : '#dc2626',
                      },
                    }}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={1}>
                <Grid item xs={2.4} sm={2.4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={resetCurrentOrderDraft}
                    disabled={submitting}
                    sx={{
                      ...footerActionButtonSx,
                      borderColor: isDark ? '#4a5568' : '#c2ccd8',
                      color: isDark ? '#e2e8f0' : '#334155',
                      backgroundColor: isDark ? 'rgba(71,85,105,0.16)' : 'rgba(148,163,184,0.1)',
                      '&:hover': {
                        borderColor: isDark ? '#64748b' : '#94a3b8',
                        backgroundColor: isDark ? 'rgba(100,116,139,0.28)' : 'rgba(100,116,139,0.18)',
                      },
                    }}
                  >
                    Reset
                  </Button>
                </Grid>
                <Grid item xs={2.4} sm={2.4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => openKotConfirmDialog('kot')}
                    disabled={submitting}
                    sx={{
                      ...footerActionButtonSx,
                      backgroundColor: isDark ? '#f59e0b' : '#fbbf24',
                      borderColor: isDark ? '#d97706' : '#f59e0b',
                      color: '#111827',
                      '&:hover': {
                        backgroundColor: isDark ? '#f59e0b' : '#f59e0b',
                      },
                    }}
                  >
                    {submitting ? 'Saving...' : 'KOT'}
                  </Button>
                </Grid>
                <Grid item xs={2.4} sm={2.4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => openKotConfirmDialog('kot_print')}
                    disabled={submitting}
                    sx={{
                      ...footerActionButtonSx,
                      backgroundColor: isDark ? '#ea580c' : '#f97316',
                      borderColor: isDark ? '#c2410c' : '#ea580c',
                      color: '#ffffff',
                      '&:hover': {
                        backgroundColor: isDark ? '#ea580c' : '#ea580c',
                      },
                    }}
                  >
                    KOT & Print
                  </Button>
                </Grid>
                <Grid item xs={2.4} sm={2.4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={openPaymentDrawer}
                    disabled={submitting}
                    sx={{
                      ...footerActionButtonSx,
                      backgroundColor: isDark ? '#16a34a' : '#22c55e',
                      borderColor: isDark ? '#15803d' : '#16a34a',
                      color: '#ffffff',
                      '&:hover': {
                        backgroundColor: isDark ? '#16a34a' : '#16a34a',
                      },
                    }}
                  >
                    Make Payment
                  </Button>
                </Grid>
                <Grid item xs={2.4} sm={2.4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={openCancelDialog}
                    disabled={submitting || !editingOrder?.id}
                    sx={{
                      ...footerActionButtonSx,
                      backgroundColor: isDark ? '#dc2626' : '#ef4444',
                      borderColor: isDark ? '#b91c1c' : '#dc2626',
                      color: '#ffffff',
                      '&:hover': {
                        backgroundColor: isDark ? '#dc2626' : '#dc2626',
                      },
                    }}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            )}
          </Box>
        </Box>
      </Drawer>
      <Drawer
        anchor="right"
        open={paymentDrawerOpen}
        onClose={closePaymentDrawer}
        sx={{
          '& .MuiDrawer-paper': posOrderDrawerPaperSx,
        }}
        PaperProps={{
          sx: posOrderDrawerPaperSx,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              ...getBookingDrawerHeaderSx(isDark),
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <ReceiptLongIcon sx={{ color: BOOKING_DRAWER_ACCENT, fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: drawerTheme.text }}>
                Make Payment
              </Typography>
              {selection ? (
                <Chip
                  size="small"
                  label={selection.targetType === 'room' ? `Room ${selection.target.room_number}` : selection.target.name}
                  sx={getBookingDrawerMetaChipSx(isDark)}
                />
              ) : null}
            </Stack>
            <Stack direction="row" spacing={0.8} alignItems="center">
              {selection && !isEmbeddedEditMode ? (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(event) => setOrderTargetMenuAnchorEl(event.currentTarget)}
                  disabled={isTargetChangeLoading || submitting}
                  startIcon={<AutorenewIcon sx={{ fontSize: 16 }} />}
                  endIcon={<KeyboardArrowDownRoundedIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    ...getBookingDrawerAuditButtonSx(isDark),
                    borderRadius: 999,
                    px: 1.2,
                  }}
                >
                  Change Room/Table
                </Button>
              ) : null}
              <IconButton onClick={closePaymentDrawer}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', p: 2 }}>
            <Paper
              variant="outlined"
              sx={getPosOrderDrawerSectionPaperSx(isDark)}
            >
              <Typography sx={{ fontWeight: 700, fontSize: 14.5, mb: 1 }}>Added Items</Typography>
              <Stack spacing={0.8}>
                {orderForm.order_items.length === 0 ? (
                  <Alert severity="info">No item added yet.</Alert>
                ) : orderForm.order_items.map((row, index) => {
                  const selectedMenuItem = menuItemMap.get(Number(row.menu_item));
                  const basePrice = Number(selectedMenuItem?.price || 0);
                  const unitPrice = resolveOrderItemUnitPrice(row, basePrice);
                  const quantity = Number(row.quantity || 0);
                  const lineAmount = unitPrice * quantity;
                  const rowVariationId = normalizeVariationId(row?.variation_id);
                  const variationLabel = String(
                    row?.variation_label
                    || (rowVariationId ? variationLabelMap.get(Number(rowVariationId)) : '')
                    || ''
                  ).trim();
                  const variationPrice = Math.max(0, Number(row?.variation_price || 0));
                  return (
                    <Paper
                      key={`payment-order-item-${index}`}
                      variant="outlined"
                      sx={{
                        p: 0.9,
                        borderRadius: 1.4,
                        borderColor: isDark ? 'rgba(76,225,182,0.2)' : 'rgba(15,23,42,0.12)',
                        backgroundColor: isDark ? 'rgba(14,18,24,0.35)' : '#fbfdff',
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.2} sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>
                          {selectedMenuItem?.name || row?.item_name || 'Menu Item'}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.86, whiteSpace: 'nowrap' }}>
                          Qty: {quantity}   Rate: {CURRENCY} {unitPrice.toFixed(2)}
                        </Typography>
                        <Typography sx={{ fontWeight: 800, color: BOOKING_DRAWER_ACCENT, whiteSpace: 'nowrap' }}>
                          {CURRENCY} {lineAmount.toFixed(2)}
                        </Typography>
                      </Stack>
                      {variationLabel ? (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.35, opacity: 0.82 }}>
                          Variation: {variationLabel}
                          {variationPrice > 0 ? ` (${CURRENCY} ${variationPrice.toFixed(2)})` : ''}
                        </Typography>
                      ) : null}
                    </Paper>
                  );
                })}
              </Stack>
            </Paper>

            <Grid container spacing={1.2} sx={{ mb: 1.5 }}>
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={getPosOrderDrawerSectionPaperSx(isDark, { mb: 0, height: '100%' })}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>Discount</Typography>
                  <Stack spacing={1}>
                    <ToggleButtonGroup
                      exclusive
                      fullWidth
                      value={billingAdjustments.discount_type}
                      onChange={(_, value) => {
                        if (!value) return;
                        setBillingAdjustments((prev) => ({
                          ...prev,
                          discount_type: value,
                          discount_value: '',
                        }));
                      }}
                      sx={{
                        border: `1px solid ${isDark ? '#4b5563' : '#cbd5e1'}`,
                        borderRadius: 1.1,
                        overflow: 'hidden',
                        '& .MuiToggleButton-root': {
                          flex: 1,
                          textTransform: 'none',
                          fontWeight: 700,
                          borderColor: 'transparent',
                          color: isDark ? '#e5e7eb' : '#334155',
                          backgroundColor: isDark ? 'rgba(31,41,55,0.62)' : '#ffffff',
                          px: 1.45,
                          py: 0.35,
                          minHeight: 34,
                        },
                        '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
                          marginLeft: 0,
                          borderLeft: `1px solid ${isDark ? '#4b5563' : '#cbd5e1'}`,
                        },
                        '& .Mui-selected': {
                          backgroundColor: '#0f766e !important',
                          color: '#ecfeff !important',
                          borderColor: '#2dd4bf !important',
                        },
                      }}
                    >
                      <ToggleButton value="percentage">Percentage (%)</ToggleButton>
                      <ToggleButton value="amount">Amount ({CURRENCY})</ToggleButton>
                    </ToggleButtonGroup>

                    <Box
                      sx={{
                        border: `1px solid ${isDark ? '#3c3f52' : '#d7dee6'}`,
                        borderRadius: 1.5,
                        p: 0.8,
                        background: isDark
                          ? 'linear-gradient(120deg, rgba(76,225,182,0.12) 0%, rgba(76,225,182,0.04) 100%)'
                          : 'linear-gradient(120deg, rgba(76,225,182,0.14) 0%, rgba(76,225,182,0.05) 100%)',
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.6 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleDiscountStep('dec')}
                          disabled={discountSliderValue <= 0}
                          sx={{ border: `1px solid ${isDark ? '#475569' : '#cbd5e1'}`, borderRadius: 1 }}
                        >
                          <RemoveRoundedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" sx={{ opacity: 0.75 }}>
                            {billingAdjustments.discount_type === 'percentage' ? 'Discount %' : `Discount ${CURRENCY}`}
                          </Typography>
                          <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                            {billingAdjustments.discount_type === 'percentage'
                              ? `${discountSliderValue.toFixed(0)}%`
                              : `${CURRENCY} ${discountSliderValue.toFixed(2)}`}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleDiscountStep('inc')}
                          disabled={discountSliderValue >= discountSliderMax}
                          sx={{ border: `1px solid ${isDark ? '#475569' : '#cbd5e1'}`, borderRadius: 1 }}
                        >
                          <AddRoundedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Stack>
                      <Slider
                        value={discountSliderValue}
                        min={0}
                        max={discountSliderMax}
                        step={discountSliderStep}
                        onChange={handleDiscountSliderChange}
                        disabled={discountSliderMax <= 0}
                        sx={{ color: BOOKING_DRAWER_ACCENT, py: 0.5, width: '100%' }}
                      />
                      <LinearProgress
                        variant="determinate"
                        value={discountProgressPercent}
                        sx={{
                          mt: 0.4,
                          height: 7,
                          width: '100%',
                          borderRadius: 999,
                          backgroundColor: isDark ? 'rgba(100,116,139,0.28)' : 'rgba(148,163,184,0.25)',
                          '& .MuiLinearProgress-bar': {
                            background: 'linear-gradient(90deg, #4ce1b6 0%, #22c55e 100%)',
                          },
                        }}
                      />
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={getPosOrderDrawerSectionPaperSx(isDark, { mb: 0, height: '100%' })}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>Taxes</Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 1, opacity: 0.74 }}>
                    Auto-loaded from Finance Setup (Food & Beverage)
                  </Typography>
                  <Stack spacing={0.9}>
                    {billingAdjustments.applied_taxes.length === 0 ? (
                      <Alert severity="info" sx={{ py: 0 }}>
                        No tax selected for this order.
                      </Alert>
                    ) : billingAdjustments.applied_taxes.map((taxRow) => {
                      const configuredValueLabel = taxRow.mode === 'percentage'
                        ? `${Number(taxRow.value || 0).toFixed(2)}%`
                        : `${CURRENCY} ${Number(taxRow.value || 0).toFixed(2)}`;
                      return (
                        <Box
                          key={taxRow.tax_key}
                          sx={{
                            border: `1px solid ${isDark ? '#3a3e52' : '#d6dbe3'}`,
                            borderRadius: 1.3,
                            p: 0.8,
                            backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : '#fafcff',
                            opacity: taxRow.enabled ? 1 : 0.74,
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center">
                            <IconButton
                              onClick={() => toggleAppliedTax(taxRow.tax_key, !taxRow.enabled)}
                              size="small"
                              sx={{
                                border: `1px solid ${isDark ? '#45455a' : '#d5d9df'}`,
                                borderRadius: 1,
                                color: taxRow.enabled ? (isDark ? '#f1f5f9' : '#1f2937') : '#10b981',
                                backgroundColor: taxRow.enabled
                                  ? 'transparent'
                                  : (isDark ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.12)'),
                              }}
                            >
                              {taxRow.enabled ? <CloseIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                            </IconButton>
                            <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 90 }}>
                              {taxRow.name}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, ml: 'auto' }}>
                              {configuredValueLabel}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: BOOKING_DRAWER_ACCENT, minWidth: 92, textAlign: 'right' }}>
                              {CURRENCY} {Number(configuredTaxAmountByKey.get(taxRow.tax_key) || 0).toFixed(2)}
                            </Typography>
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>

            <Box
              sx={{
                mb: 1.5,
                display: 'grid',
                gap: 1.2,
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                alignItems: 'start',
              }}
            >
              <Stack spacing={1.2}>
                <Paper
                  variant="outlined"
                    sx={getPosOrderDrawerSectionPaperSx(isDark, { mb: 0, p: 1.3 })}
                >
                  <Typography sx={{ fontWeight: 700, mb: 0.7, fontSize: 13.5 }}>
                    Payment Collection
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    fullWidth
                    size="small"
                    value={orderMeta.payment_collection_mode}
                    onChange={(_, value) => {
                      if (!value || zeroAmountFullDiscountSettlement) return;
                      setOrderMeta((prev) => ({
                        ...prev,
                        payment_collection_mode: value,
                        partial_paid_amount: '',
                      }));
                      setPaymentValidationError('');
                    }}
                    sx={{
                      mb: 1.05,
                      border: `1px solid ${isDark ? '#4b5563' : '#cbd5e1'}`,
                      borderRadius: 1.1,
                      overflow: 'hidden',
                      '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        fontWeight: 700,
                        borderColor: 'transparent',
                        color: isDark ? '#e5e7eb' : '#334155',
                        backgroundColor: isDark ? 'rgba(31,41,55,0.62)' : '#ffffff',
                      },
                      '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
                        marginLeft: 0,
                        borderLeft: `1px solid ${isDark ? '#4b5563' : '#cbd5e1'}`,
                      },
                      '& .Mui-selected': {
                        backgroundColor: '#0f766e !important',
                        color: '#ecfeff !important',
                        borderColor: '#2dd4bf !important',
                      },
                    }}
                  >
                    <ToggleButton value="collect_now">Collect Now</ToggleButton>
                    <ToggleButton value="partial" disabled={zeroAmountFullDiscountSettlement}>Partial</ToggleButton>
                    <ToggleButton value="due" disabled={zeroAmountFullDiscountSettlement}>Due</ToggleButton>
                  </ToggleButtonGroup>
                  {orderMeta.payment_collection_mode === 'partial' ? (
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label={`Paid Now (${CURRENCY})`}
                      value={orderMeta.partial_paid_amount}
                      error={Boolean(partialPaidNowInlineError)}
                      helperText={partialPaidNowInlineError || ''}
                      onChange={(event) => {
                        setOrderMeta((prev) => ({ ...prev, partial_paid_amount: event.target.value }));
                        setPaymentValidationError('');
                      }}
                      inputProps={{ min: 0, step: '0.01', max: Number(partialPaymentBaseAmount.toFixed(2)) }}
                      sx={{ mb: 1.05 }}
                    />
                  ) : null}
                  <Typography sx={{ fontWeight: 700, mb: 0.9, fontSize: 13.5 }}>
                    Payment Method {orderMeta.payment_collection_mode === 'due' ? '(Disabled for due)' : zeroAmountFullDiscountSettlement ? '(Auto)' : '*'}
                  </Typography>
                  {zeroAmountFullDiscountSettlement ? (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mb: 0.8,
                        fontWeight: 600,
                        color: isDark ? '#9cead6' : '#0f766e',
                      }}
                    >
                      100% discount with zero payable — {COMPANY_SETTLEMENT_PAYMENT_METHOD} applied automatically.
                    </Typography>
                  ) : null}
                  <Stack
                    direction="row"
                    spacing={0.7}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ opacity: orderMeta.payment_collection_mode === 'due' || zeroAmountFullDiscountSettlement ? 0.72 : 1 }}
                  >
                    {PAYMENT_METHOD_OPTIONS.map((method) => {
                      const selected = resolvedPaymentMethod === method;
                      const disableMethodSelection = orderMeta.payment_collection_mode === 'due'
                        || zeroAmountFullDiscountSettlement;
                      return (
                        <Chip
                          key={method}
                          label={method}
                          clickable={!disableMethodSelection}
                          disabled={disableMethodSelection}
                          onClick={() => {
                            if (disableMethodSelection) return;
                            setOrderMeta((prev) => ({ ...prev, payment_method: method }));
                            setPaymentValidationError('');
                          }}
                          size="medium"
                          sx={{
                            borderRadius: 999,
                            px: 0.55,
                            height: 34,
                            fontWeight: 700,
                            '& .MuiChip-label': { px: 1.15, fontSize: '0.92rem' },
                            border: `1px solid ${selected ? '#4ce1b6' : (isDark ? '#3a4455' : '#c9d2dc')}`,
                            backgroundColor: selected
                              ? (isDark ? 'rgba(76,225,182,0.24)' : 'rgba(76,225,182,0.16)')
                              : 'transparent',
                            color: selected
                              ? (isDark ? '#9cead6' : '#0f766e')
                              : (isDark ? '#d0d7df' : '#334155'),
                          }}
                        />
                      );
                    })}
                  </Stack>
                  {orderMeta.payment_collection_mode === 'partial' && partialPaymentMethodInlineError ? (
                    <FormHelperText error sx={{ mt: 0.7, ml: 0.1, fontWeight: 600 }}>
                      {partialPaymentMethodInlineError}
                    </FormHelperText>
                  ) : null}
                  {orderMeta.payment_collection_mode === 'partial' ? (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 0.8,
                        fontWeight: 700,
                        color: partialDueNowAmount > 0 ? (isDark ? '#facc15' : '#a16207') : (isDark ? '#9ca3af' : '#64748b'),
                      }}
                    >
                      Remaining Due: {CURRENCY} {partialDueNowAmount.toFixed(2)}
                    </Typography>
                  ) : null}
                  {paymentValidationError && !suppressPartialGlobalValidationError ? (
                    <Alert
                      severity="error"
                      sx={{
                        mt: 1,
                        py: 0,
                        '& .MuiAlert-message': {
                          fontWeight: 600,
                        },
                      }}
                    >
                      {paymentValidationError}
                    </Alert>
                  ) : null}
                </Paper>
              </Stack>
              <Stack
                spacing={1.2}
              >
                <Paper
                  variant="outlined"
                    sx={getPosOrderDrawerSectionPaperSx(isDark, { mb: 0, p: 1.4 })}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1.7 }}>Additional Charges</Typography>
                  <Grid container columnSpacing={1.6} rowSpacing={1.4} sx={{ mt: 0.35 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={`Delivery Charge (${CURRENCY})`}
                        InputLabelProps={{ shrink: true }}
                        value={billingAdjustments.delivery_charge}
                        onChange={(e) => setBillingAdjustments((prev) => ({ ...prev, delivery_charge: e.target.value }))}
                        inputProps={{ min: 0, step: '0.01' }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end" sx={{ mr: -0.7 }}>
                              <Stack spacing={0.2}>
                                <IconButton
                                  size="small"
                                  onClick={() => changeChargeValue('delivery_charge', 1)}
                                  sx={{
                                    width: 18,
                                    height: 14,
                                    border: `1px solid ${isDark ? '#495162' : '#d1d5db'}`,
                                    borderRadius: 0.7,
                                  }}
                                >
                                  <KeyboardArrowUpRoundedIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => changeChargeValue('delivery_charge', -1)}
                                  sx={{
                                    width: 18,
                                    height: 14,
                                    border: `1px solid ${isDark ? '#495162' : '#d1d5db'}`,
                                    borderRadius: 0.7,
                                  }}
                                >
                                  <KeyboardArrowDownRoundedIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Stack>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiInputLabel-root': {
                            backgroundColor: isDark ? drawerTheme.surface : '#fff',
                            px: 0.35,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={`Container Charge (${CURRENCY})`}
                        InputLabelProps={{ shrink: true }}
                        value={billingAdjustments.container_charge}
                        onChange={(e) => setBillingAdjustments((prev) => ({ ...prev, container_charge: e.target.value }))}
                        inputProps={{ min: 0, step: '0.01' }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end" sx={{ mr: -0.7 }}>
                              <Stack spacing={0.2}>
                                <IconButton
                                  size="small"
                                  onClick={() => changeChargeValue('container_charge', 1)}
                                  sx={{
                                    width: 18,
                                    height: 14,
                                    border: `1px solid ${isDark ? '#495162' : '#d1d5db'}`,
                                    borderRadius: 0.7,
                                  }}
                                >
                                  <KeyboardArrowUpRoundedIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => changeChargeValue('container_charge', -1)}
                                  sx={{
                                    width: 18,
                                    height: 14,
                                    border: `1px solid ${isDark ? '#495162' : '#d1d5db'}`,
                                    borderRadius: 0.7,
                                  }}
                                >
                                  <KeyboardArrowDownRoundedIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Stack>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiInputLabel-root': {
                            backgroundColor: isDark ? drawerTheme.surface : '#fff',
                            px: 0.35,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
                <Paper
                  variant="outlined"
                  sx={getPosOrderDrawerSummaryPaperSx(isDark)}
                >
                  <Typography sx={{ fontWeight: 800, mb: 1, color: BOOKING_DRAWER_ACCENT }}>Billing Summary</Typography>
                  <Stack spacing={0.7}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" sx={{ opacity: 0.82 }}>Subtotal</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{CURRENCY} {estimatedSubtotal.toFixed(2)}</Typography>
                    </Stack>
                    {discountAmount > 0 ? (
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" sx={{ opacity: 0.82 }}>Discount</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>-{CURRENCY} {discountAmount.toFixed(2)}</Typography>
                      </Stack>
                    ) : null}
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" sx={{ opacity: 0.82 }}>Taxable Subtotal</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{CURRENCY} {taxableSubtotal.toFixed(2)}</Typography>
                    </Stack>
                    {configuredTaxBreakdown.map((taxRow) => (
                      <Stack
                        key={`payment-billing-tax-${taxRow.tax_key}`}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="body2" sx={{ opacity: 0.82 }}>
                          {taxRow.name}
                          {taxRow.mode === 'percentage' ? ` (${Number(taxRow.numericValue || 0).toFixed(2)}%)` : ''}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {CURRENCY} {Number(taxRow.amount || 0).toFixed(2)}
                        </Typography>
                      </Stack>
                    ))}
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" sx={{ opacity: 0.82 }}>Total Tax</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{CURRENCY} {estimatedTax.toFixed(2)}</Typography>
                    </Stack>
                    {deliveryChargeAmount > 0 ? (
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" sx={{ opacity: 0.82 }}>Delivery Charge</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{CURRENCY} {deliveryChargeAmount.toFixed(2)}</Typography>
                      </Stack>
                    ) : null}
                    {containerChargeAmount > 0 ? (
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" sx={{ opacity: 0.82 }}>Container Charge</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{CURRENCY} {containerChargeAmount.toFixed(2)}</Typography>
                      </Stack>
                    ) : null}
                    {orderMeta.payment_collection_mode === 'partial' ? (
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" sx={{ opacity: 0.82 }}>Paid Now</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {CURRENCY} {partialPaidNowAmount.toFixed(2)}
                        </Typography>
                      </Stack>
                    ) : null}
                    {orderMeta.payment_collection_mode === 'partial' ? (
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" sx={{ opacity: 0.82 }}>Due Amount</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {CURRENCY} {partialDueNowAmount.toFixed(2)}
                        </Typography>
                      </Stack>
                    ) : null}
                    <Box sx={{ mt: 0.4, pt: 0.8, borderTop: `1px dashed ${isDark ? 'rgba(148,163,184,0.35)' : 'rgba(100,116,139,0.35)'}` }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ fontWeight: 800 }}>Total</Typography>
                        <Typography sx={{ fontWeight: 900, color: BOOKING_DRAWER_ACCENT }}>
                          {CURRENCY} {estimatedTotal.toFixed(2)}
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                </Paper>
              </Stack>
            </Box>
          </Box>
          <Box
            sx={{
              p: 1.5,
              ...getBookingDrawerFooterSx(isDark),
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={2.4} sm={2.4}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={closePaymentDrawer}
                  disabled={submitting}
                  sx={{
                    ...footerActionButtonSx,
                    borderColor: isDark ? '#4f5b72' : '#c6d2e1',
                    color: isDark ? '#d6dfeb' : '#334155',
                    backgroundColor: isDark ? 'rgba(71,85,105,0.14)' : 'rgba(148,163,184,0.12)',
                    '&:hover': {
                      borderColor: isDark ? '#718096' : '#94a3b8',
                      backgroundColor: isDark ? 'rgba(100,116,139,0.28)' : 'rgba(100,116,139,0.2)',
                    },
                  }}
                >
                  Back
                </Button>
              </Grid>
              <Grid item xs={2.4} sm={2.4}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handlePrintBeforePayment}
                  disabled={submitting}
                  sx={{
                    ...footerActionButtonSx,
                    background: isDark
                      ? 'linear-gradient(120deg, #0f766e 0%, #14b8a6 100%)'
                      : 'linear-gradient(120deg, #2dd4bf 0%, #0f766e 100%)',
                    color: '#ffffff',
                    boxShadow: isDark ? '0 10px 22px rgba(20,184,166,0.32)' : '0 10px 22px rgba(20,184,166,0.24)',
                    '&:hover': {
                      background: isDark
                        ? 'linear-gradient(120deg, #14b8a6 0%, #0f766e 100%)'
                        : 'linear-gradient(120deg, #0f766e 0%, #115e59 100%)',
                    },
                  }}
                >
                  Print
                </Button>
              </Grid>
              <Grid item xs={2.4} sm={2.4}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => handleSubmitOrder('payment')}
                  disabled={submitting}
                  sx={{
                    ...footerActionButtonSx,
                    background: isDark
                      ? 'linear-gradient(120deg, #1f9d55 0%, #16a34a 100%)'
                      : 'linear-gradient(120deg, #22c55e 0%, #16a34a 100%)',
                    color: '#ffffff',
                    boxShadow: isDark ? '0 10px 22px rgba(22,163,74,0.35)' : '0 10px 22px rgba(22,163,74,0.28)',
                    '&:hover': {
                      background: isDark
                        ? 'linear-gradient(120deg, #22c55e 0%, #16a34a 100%)'
                        : 'linear-gradient(120deg, #16a34a 0%, #15803d 100%)',
                    },
                  }}
                >
                  Payment
                </Button>
              </Grid>
              <Grid item xs={2.4} sm={2.4}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => handleSubmitOrder('payment_print')}
                  disabled={submitting}
                  sx={{
                    ...footerActionButtonSx,
                    background: isDark
                      ? 'linear-gradient(120deg, #0369a1 0%, #0284c7 100%)'
                      : 'linear-gradient(120deg, #0ea5e9 0%, #0284c7 100%)',
                    color: '#ffffff',
                    boxShadow: isDark ? '0 10px 22px rgba(2,132,199,0.35)' : '0 10px 22px rgba(2,132,199,0.28)',
                    '&:hover': {
                      background: isDark
                        ? 'linear-gradient(120deg, #0284c7 0%, #0ea5e9 100%)'
                        : 'linear-gradient(120deg, #0284c7 0%, #0369a1 100%)',
                    },
                  }}
                >
                  Payment & Print
                </Button>
              </Grid>
              <Grid item xs={2.4} sm={2.4}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={openEbillDialog}
                  disabled={submitting}
                  sx={{
                    ...footerActionButtonSx,
                    background: isDark
                      ? 'linear-gradient(120deg, #6d28d9 0%, #7c3aed 100%)'
                      : 'linear-gradient(120deg, #8b5cf6 0%, #7c3aed 100%)',
                    color: '#ffffff',
                    boxShadow: isDark ? '0 10px 22px rgba(124,58,237,0.35)' : '0 10px 22px rgba(124,58,237,0.28)',
                    '&:hover': {
                      background: isDark
                        ? 'linear-gradient(120deg, #7c3aed 0%, #8b5cf6 100%)'
                        : 'linear-gradient(120deg, #7c3aed 0%, #6d28d9 100%)',
                    },
                  }}
                >
                  Payment & EBill
                </Button>
              </Grid>
            </Grid>
          </Box>
          {ebillDialog.open ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 9,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                bgcolor: isDark ? 'rgba(7,9,13,0.66)' : 'rgba(15,23,42,0.32)',
                backdropFilter: 'blur(2px)',
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  maxWidth: 430,
                  p: 2,
                  borderRadius: 2.2,
                  border: `1px solid ${isDark ? 'rgba(124,58,237,0.34)' : 'rgba(124,58,237,0.25)'}`,
                  background: isDark
                    ? 'linear-gradient(165deg, rgba(34,34,40,0.98) 0%, rgba(24,24,30,0.98) 100%)'
                    : 'linear-gradient(165deg, #ffffff 0%, #faf5ff 100%)',
                }}
              >
                <Typography sx={{ pb: 0.7, fontWeight: 800, fontSize: '1.2rem' }}>
                  Send Restaurant E-Bill
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.2, color: isDark ? '#b8c3d4' : '#4b5a70' }}>
                  We will email the restaurant invoice PDF after payment is captured.
                </Typography>
                <TextField
                  fullWidth
                  autoFocus
                  size="small"
                  type="email"
                  label="Guest Email"
                  value={ebillDialog.email}
                  onChange={(event) =>
                    setEbillDialog((prev) => ({ ...prev, email: event.target.value, error: '' }))
                  }
                  error={Boolean(ebillDialog.error)}
                  helperText={
                    ebillDialog.error
                    || (ebillDialog.loading
                      ? 'Loading guest email...'
                      : (ebillDialog.source === 'booking_guest'
                        ? 'Auto-loaded from room booking guest. You can edit before sending.'
                        : 'Enter recipient email for restaurant invoice.'))
                  }
                  disabled={ebillDialog.loading || submitting}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1.5, justifyContent: 'flex-end' }}>
                  <Button
                    onClick={closeEbillDialog}
                    disabled={submitting}
                    variant="outlined"
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={confirmPaymentAndSendEbill}
                    disabled={submitting || ebillDialog.loading}
                    variant="contained"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      background: isDark
                        ? 'linear-gradient(120deg, #6d28d9 0%, #7c3aed 100%)'
                        : 'linear-gradient(120deg, #8b5cf6 0%, #7c3aed 100%)',
                    }}
                  >
                    Payment & EBill
                  </Button>
                </Stack>
              </Paper>
            </Box>
          ) : null}
        </Box>
      </Drawer>
      <Dialog
        open={incomingOrderDialog.open}
        onClose={() => {
          if (!incomingOrderDialog.processing) closeIncomingOrderDialog();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Incoming Open Orders
        </DialogTitle>
        <DialogContent>
          {incomingOrderDialog.activeOrder ? (() => {
            const order = incomingOrderDialog.activeOrder;
            const statusKey = String(order?.status || '').toLowerCase();
            const isPendingAcceptance =
              (statusKey === 'draft' || statusKey === 'placed') && isQrBasedIncomingOrder(order);
            const queueTimers = normalizeQueueTimers(order?.queue_timers);
            const activeTimer = queueTimers.find((timer) => !timer.ended_at) || null;
            const latestTimer = queueTimers.length ? queueTimers[queueTimers.length - 1] : null;
            const timerStartedAt = activeTimer?.started_at || latestTimer?.started_at || order?.created_at || null;
            const elapsedMs = (isPendingAcceptance || !timerStartedAt)
              ? 0
              : getOrderElapsedMs({ created_at: timerStartedAt }, queueNowTimestamp);
            const targetMinutes = Math.max(
              10,
              Number(activeTimer?.target_minutes || latestTimer?.target_minutes || order?.preparation_time_minutes || incomingOrderPrepMinutes || 30)
            );
            const targetMs = targetMinutes * 60 * 1000;
            const remainingMs = Math.max(0, targetMs - elapsedMs);
            const orderItems = Array.isArray(order?.items) ? order.items : [];
            return (
              <Stack spacing={1.1} sx={{ mt: 0.4 }}>
                <Paper variant="outlined" sx={{ p: 1.15, borderRadius: 1.8 }}>
                  <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mb: 0.75 }}>
                    <Chip size="small" label={`#${formatOrderNumber(order?.order_number)}`} sx={{ fontWeight: 800 }} />
                    <Chip size="small" label={getOrderTypeLabel(order?.order_type)} sx={{ fontWeight: 700 }} />
                    <Chip size="small" label={order?.room_number ? `Room ${order.room_number}` : (order?.table_name || 'Table')} sx={{ fontWeight: 700 }} />
                  </Stack>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Guest: {order?.customer_name || 'Guest'} {order?.customer_phone ? `- ${order.customer_phone}` : ''}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Amount: {formatCurrencyAmount(order?.total_amount)}
                  </Typography>
                  {isPendingAcceptance ? (
                    <Typography variant="caption" sx={{ color: isDark ? '#67e8f9' : '#0891b2', fontWeight: 700 }}>
                      Pending Acceptance - timer starts only after you click Accept Order.
                    </Typography>
                  ) : (
                    <Typography variant="caption" sx={{ color: isDark ? '#b7c7d6' : '#536174' }}>
                      Waiting: {formatClockDuration(elapsedMs)} | SLA: {formatClockDuration(targetMs)} | Left: {formatClockDuration(remainingMs)}
                    </Typography>
                  )}
                  {String(order?.notes || '').trim() ? (
                    <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: isDark ? '#d0e2ef' : '#334155' }}>
                      Note: {String(order.notes || '').trim()}
                    </Typography>
                  ) : null}
                </Paper>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.55 }}>
                    Order Items
                  </Typography>
                  <Stack spacing={0.5}>
                    {orderItems.slice(0, 8).map((item, index) => (
                      <Paper key={`incoming-item-${order.id}-${index}`} variant="outlined" sx={{ p: 0.75, borderRadius: 1.3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {item?.menu_item_name || item?.item_name || `Item #${index + 1}`}
                          </Typography>
                          <Chip size="small" label={`x${Math.max(1, Number(item?.quantity || 1))}`} sx={{ fontWeight: 700 }} />
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
                <FormControl fullWidth size="small">
                  <InputLabel id="incoming-open-prep-time-label">Preparation time</InputLabel>
                  <Select
                    labelId="incoming-open-prep-time-label"
                    label="Preparation time"
                    value={incomingOrderPrepMinutes}
                    onChange={(event) => setIncomingOrderPrepMinutes(Number(event.target.value))}
                  >
                    {INCOMING_ORDER_PREP_OPTIONS.map((m) => (
                      <MenuItem key={m} value={m}>
                        {m} min
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" sx={{ color: isDark ? '#9fbbd2' : '#64748b' }}>
                  Pending incoming queue: {incomingOrderDialog.queue.length}
                </Typography>
              </Stack>
            );
          })() : (
            <Alert severity="info">No pending incoming orders.</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => handleIncomingOrderDecision('decline')}
            disabled={incomingOrderDialog.processing || !incomingOrderDialog.activeOrder}
            variant="outlined"
            color="error"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {incomingOrderDialog.processing ? 'Updating...' : 'Decline'}
          </Button>
          <Button
            onClick={() => handleIncomingOrderDecision('accept')}
            disabled={incomingOrderDialog.processing || !incomingOrderDialog.activeOrder}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              background: 'linear-gradient(90deg, #4ce1b6 0%, #2dd4a0 100%)',
              color: '#1a1a1a',
              '&:hover': {
                background: 'linear-gradient(90deg, #3dd1a8 0%, #22c997 100%)',
              },
            }}
          >
            {incomingOrderDialog.processing ? 'Updating...' : 'Accept Order'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RestaurantLandingPage;
